import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Menu, // Import Menu icon
  X,    // Import X icon for closing
  LayoutDashboard, 
  Database, 
  Users, 
  FileText, 
  CheckCircle, 
  Save, 
  Search, 
  Plus, 
  Wand2, 
  ClipboardPaste, 
  ArrowLeft,
  Upload,
  File as FileIcon,
  Trash2,
  Globe,
  Settings,
  Download,
  Eye,
  Edit3,
  UserPlus,
  HardDrive,
  FileSpreadsheet,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
    BarChart,
    Bar,
    LineChart,
    Line,
  Tooltip, 
  ResponsiveContainer,
  Legend,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid
} from 'recharts';
import * as XLSX from 'xlsx';
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { Tooltip as MapTooltip } from "react-tooltip";
import SpanishCommissionsWorkspace from './SpanishCommissionsWorkspace';
import SpanishParliamentActivityWorkspace from './SpanishParliamentActivityWorkspace';
import SpanishParliamentarySpeechesWorkspace from './SpanishParliamentarySpeechesWorkspace';
import SpanishLawsWorkspace from './SpanishLawsWorkspace';
import SpanishExecutiveBillsWorkspace from './SpanishExecutiveBillsWorkspace';
import AutomaticCodingValidationWorkspace from './AutomaticCodingValidationWorkspace';
import QdemWebsiteWorkspace from './QdemWebsiteWorkspace';

// --- CONFIGURACIÓN DE ESTILO (Q-DEM INSPIRED) ---
const BRAND = {
  primary: 'bg-[#74c476]', // Q-DEM Green
  primaryHover: 'hover:bg-[#5aa85c]',
  secondary: 'bg-[#222222]', // Dark Grey Footer
  accent: 'text-[#74c476]',
  light: 'bg-stone-50',
  white: 'bg-white',
  border: 'border-stone-200'
};

const WORLD_COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
  "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
  "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Cote d'Ivoire", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
  "Fiji", "Finland", "France",
  "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary",
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
  "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan",
  "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
  "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
  "Oman",
  "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar",
  "Romania", "Russia", "Rwanda",
  "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
  "Yemen",
  "Zambia", "Zimbabwe"
];

const ENTITY_TYPE_OPTIONS = [
  "AI-specific advisory body",
  "Digital, data, ethics or societal advisory/deliberative body",
  "Governmental or interministerial coordination body",
  "Data protection supervisory authority",
  "AI/digital regulatory or supervisory authority excluding data protection",
  "Technical standardization, conformity or regulatory support body",
  "Digital government and public infrastructure operational agency",
  "National cybersecurity authority, agency or centre"
];

const PLATFORM_DATABASES = [
    { id: 'aipas', title: 'AI Policy Advisory Systems', section: 'international', sortOrder: 10, available: true, publicRead: false, colorHex: '#2A9D8F' },
    { id: 'oral_questions', title: 'Spanish Parliament Oral Questions', section: 'national', sortOrder: 20, available: true, publicRead: true, colorHex: '#3D405B' },
    { id: 'spanish_parliament_activity', title: 'Spanish Parliament Activity', section: 'national', sortOrder: 30, available: true, publicRead: false, colorHex: '#E76F51' },
    { id: 'spanish_commissions', title: 'Spanish Parliament Commissions', section: 'national', sortOrder: 40, available: true, publicRead: false, colorHex: '#264653' },
    { id: 'written_questions', title: 'Spanish Parliament Written Questions', section: 'national', sortOrder: 50, available: false, publicRead: false, colorHex: '#E9C46A' },
    { id: 'spanish_media_front_pages', title: 'Spanish Media Front Pages', section: 'national', sortOrder: 60, available: false, publicRead: false, colorHex: '#8D6A9F' },
    { id: 'catalan_oral_questions', title: 'Catalan Parliament Oral Questions', section: 'regional', sortOrder: 70, available: false, publicRead: false, colorHex: '#6C9A8B' },
    { id: 'spanish_parliamentary_speeches', title: 'Spanish Parliamentary Speeches', section: 'national', sortOrder: 80, available: true, publicRead: true, colorHex: '#3F6C8A' },
    { id: 'spanish_laws', title: 'Laws and Legislative Decrees', section: 'national', sortOrder: 90, available: true, publicRead: true, colorHex: '#A66E2D' },
    { id: 'spanish_executive_bills', title: 'Executive Bills', section: 'national', sortOrder: 100, available: true, publicRead: true, colorHex: '#6D7A4A' },
    { id: 'automatic_coding_validation', title: 'Automatic Coding Validation', section: 'national', sortOrder: 110, available: true, publicRead: false, colorHex: '#0F766E' },
    { id: 'qdem_website', title: 'Q-Dem Website', section: 'web', sortOrder: 120, available: true, publicRead: false, colorHex: '#79C88D' }
];

const SUPPORTED_DATABASE_WORKSPACES = ['aipas', 'oral_questions', 'spanish_commissions', 'spanish_parliament_activity', 'spanish_parliamentary_speeches', 'spanish_laws', 'spanish_executive_bills', 'automatic_coding_validation', 'qdem_website'];
const PUBLIC_READ_DATABASE_WORKSPACES = ['oral_questions', 'spanish_commissions', 'spanish_parliament_activity', 'spanish_parliamentary_speeches', 'spanish_laws', 'spanish_executive_bills'];

const DOCUMENT_TYPE_OPTIONS = [
    'Creation Norm',
    'Modification Norm',
    'Internal Regulation',
    'Meeting Minutes',
    'Other'
];

const OQ_CHART_COLORS = ['#74c476', '#5aa85c', '#4E647D', '#E4B063', '#D76559', '#6b7280', '#9ca3af', '#16a34a', '#15803d', '#65a30d'];

const toTitleCase = (value) => value
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const normalizeOralQuestionResult = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return 'Not specified';

    const normalized = raw
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    if (/(contestad|respondid|respuesta|sustanciad)/.test(normalized)) return 'Answered';
    if (/(tramitado por completo|tramitad[oa]\s+por\s+completo|tramitado\s+completamente)/.test(normalized)) return 'Processed';
    if (/(subsumid|subsumida|subsumido)/.test(normalized)) return 'Subsumed';
    if (/(tramitad[oa]|tramitacion completa|procesad[oa])/.test(normalized)) return 'Processed';
    if (/(enmiend|modificad|reformulad)/.test(normalized)) return 'Amended';
    if (/(rechazad|desestimad|denegad)/.test(normalized)) return 'Rejected';
    if (/(aplazad|pospuest|demorad)/.test(normalized)) return 'Postponed';
    if (/(admitid|admitida a tramite|admitida a tramitacion)/.test(normalized)) return 'Admitted';
    if (/(suspendid|suspendida|paralizad)/.test(normalized)) return 'Suspended';
    if (/(acordad|aprobad|estimad)/.test(normalized)) return 'Approved';
    if (/(retirad|retirada)/.test(normalized)) return 'Withdrawn';
    if (/(inadmit|no admit)/.test(normalized)) return 'Not admitted';
    if (/(caduc|expir)/.test(normalized)) return 'Expired';
    if (/(decaid|decaida)/.test(normalized)) return 'Lapsed';
    if (/(pendient|en tramit|tramitacion)/.test(normalized)) return 'Pending';
    if (/(acumulad|agrupad)/.test(normalized)) return 'Grouped';

    return toTitleCase(normalized);
};

const aggregateByNormalizedResult = (rows) => {
    const grouped = new Map();
    (rows || []).forEach((row) => {
        const label = normalizeOralQuestionResult(row?.result);
        const count = Number(row?.count || 0);
        grouped.set(label, (grouped.get(label) || 0) + count);
    });
    return Array.from(grouped.entries())
        .map(([result, count]) => ({ result, count }))
        .sort((a, b) => b.count - a.count);
};

const parseAuthorsFromField = (value) => {
    const raw = String(value || '').trim();
    if (!raw || ['none', 'null', 'nan', '[]'].includes(raw.toLowerCase())) return [];

    const unique = new Set();
    if (raw.startsWith('[') && raw.endsWith(']')) {
        const quoted = [...raw.matchAll(/'([^']+)'|"([^"]+)"/g)];
        if (quoted.length > 0) {
            quoted.forEach((m) => {
                const name = (m[1] || m[2] || '').trim();
                if (name) unique.add(name);
            });
            return Array.from(unique);
        }

        const inside = raw.slice(1, -1).trim();
        if (!inside) return [];
        inside.split(',').forEach((part) => {
            const cleaned = part.trim().replace(/^['"]|['"]$/g, '').trim();
            if (cleaned) unique.add(cleaned);
        });
        return Array.from(unique);
    }

    raw.split(';').forEach((part) => {
        const cleaned = part.trim();
        if (cleaned) unique.add(cleaned);
    });

    if (unique.size === 0) unique.add(raw);
    return Array.from(unique);
};

const parseStandardizedGroupsFromRow = (row) => {
    const primary = parseAuthorsFromField(row?.grupos_estandarizados);
    if (primary.length > 0) return primary;
    return parseAuthorsFromField(row?.grupos_estandarizados_str);
};

const isHiddenValue = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === '999' || normalized === 'none' || normalized === 'null' || normalized === 'nan';
};

const dayOfWeekLabel = (value) => {
    const raw = String(value || '').trim();
    const map = {
        '1': 'Monday',
        '2': 'Tuesday',
        '3': 'Wednesday',
        '4': 'Thursday',
        '5': 'Friday',
        '6': 'Saturday',
        '7': 'Sunday'
    };
    if (map[raw]) return map[raw];
    return raw || '—';
};

const isHiddenTopicValue = (topic, topicCode) => {
    const normalizedTopic = String(topic || '').trim();
    const normalizedCode = String(topicCode || '').trim();
    return normalizedTopic === '999' || normalizedCode === '999';
};

const romanToInt = (roman) => {
    const map = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
    const input = String(roman || '').toUpperCase().trim();
    if (!input) return null;
    let total = 0;
    let prev = 0;
    for (let i = input.length - 1; i >= 0; i -= 1) {
        const current = map[input[i]] || 0;
        if (current < prev) total -= current;
        else total += current;
        prev = current;
    }
    return total || null;
};

const legislatureSortKey = (label) => {
    const raw = String(label || '').trim();
    if (!raw) return Number.MAX_SAFE_INTEGER;

    const arabic = raw.match(/\d+/);
    if (arabic) return parseInt(arabic[0], 10);

    const roman = raw.match(/\b[IVXLCDM]+\b/i);
    if (roman) {
        const value = romanToInt(roman[0]);
        if (value) return value;
    }

    return Number.MAX_SAFE_INTEGER - 1;
};

const QDemLogo = ({ className = '', alt = 'Q-Dem' }) => (
    <img
        src="/Logo-Q-Dem-2022-1024x305.png"
        alt={alt}
        className={className}
        loading="eager"
    />
);

// In production (served by Express), use relative paths. In dev, point to backend port.
const IS_PROD = process.env.NODE_ENV === 'production';
const API_URL = IS_PROD ? '/api' : 'http://127.0.0.1:3001/api';
const UPLOADS_URL = IS_PROD ? '/uploads' : 'http://127.0.0.1:3001/uploads';

const GEO_URL = "https://raw.githubusercontent.com/leakyMirror/map-of-europe/master/TopoJSON/europe.topojson";

const SMALL_COUNTRIES = [
    { name: "Malta", coordinates: [14.4056, 35.9375] },
    { name: "Luxembourg", coordinates: [6.1296, 49.8153] },
    { name: "Cyprus", coordinates: [33.4299, 35.1264] }
];

const MapChart = ({ data, title, colorRange = ["#f5f5f4", "#74c476"], suffix="", children }) => {
    const maxValue = Math.max(0, ...Object.values(data));
    const colorScale = scaleLinear().domain([0, maxValue || 1]).range(colorRange);
    
    return (
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col h-[500px]">
             <div className="flex justify-between items-center mb-4 border-b border-stone-100 pb-2">
                 <h4 className="text-xs font-bold text-stone-500 uppercase tracking-wider">{title}</h4>
                 <div className="flex gap-2 items-center text-xs">{children}</div>
             </div>
             <div className="flex-grow w-full h-full relative border border-stone-50 bg-stone-50/50 rounded overflow-hidden">
                 <ComposableMap 
                    projection="geoMercator" 
                    projectionConfig={{ scale: 400, center: [15, 55] }} 
                    style={{width: "100%", height: "100%"}}
                 >
                    <Geographies geography={GEO_URL}>
                        {({ geographies }) => geographies.map((geo) => {
                            // Normalize names if needed (this TopoJSON usually has English names)
                            const countryName = geo.properties.NAME || geo.properties.name;
                            const cur = data[countryName]; 
                            return (
                                <Geography 
                                    key={geo.rsmKey} 
                                    geography={geo} 
                                    fill={cur !== undefined ? colorScale(cur) : "#e7e5e4"}
                                    stroke="#d6d3d1"
                                    strokeWidth={0.5}
                                    style={{
                                        default: { outline: "none" },
                                        hover: { outline: "none", fill: "#5aa85c", cursor: "pointer" },
                                        pressed: { outline: "none" }
                                    }}
                                    data-tooltip-id="map-tooltip"
                                    data-tooltip-content={`${countryName}: ${cur !== undefined ? cur : '0'}${suffix}`}
                                />
                            );
                        })}
                    </Geographies>
                    {SMALL_COUNTRIES.map(({ name, coordinates }) => {
                        const cur = data[name];
                        if (cur === undefined) return null;
                        return (
                            <Marker key={name} coordinates={coordinates}>
                                <circle 
                                    r={4} 
                                    fill={colorScale(cur)} 
                                    stroke="#fff" 
                                    strokeWidth={1} 
                                    className="cursor-pointer hover:stroke-stone-500"
                                    data-tooltip-id="map-tooltip"
                                    data-tooltip-content={`${name}: ${cur}${suffix}`}
                                />
                            </Marker>
                        );
                    })}
                 </ComposableMap>
                 <MapTooltip id="map-tooltip" style={{ fontSize: '12px', zIndex: 1000 }} />
             </div>
             <div className="mt-2 flex items-center gap-2 text-xs text-stone-400">
                <span className="w-3 h-3 rounded-full" style={{background: colorRange[0]}}></span>
                <span>Min ({0})</span>
                <span className="w-full h-1 bg-gradient-to-r from-stone-100 to-green-400 rounded-full mx-2"></span>
                <span className="w-3 h-3 rounded-full" style={{background: colorRange[1]}}></span>
                <span>Max ({maxValue})</span>
             </div>
        </div>

    );
};

const EntityTimeline = ({ entities }) => {
    const { data, countries } = useMemo(() => {
        const countrySet = new Set(); 
        const countryData = {}; 

        entities.forEach(e => {
            if (!e.country || !e.year) return;
            // Clean year
            const yStr = String(e.year).trim();
            const y = parseInt(yStr);
            const currentYear = new Date().getFullYear();
            if (isNaN(y) || y < 1900 || y > currentYear) return;
            
            countrySet.add(e.country);
            
            // Encode unique key
            const key = `${e.country}___${y}`;
            countryData[key] = (countryData[key] || 0) + 1;
        });

        const sortedCountries = Array.from(countrySet).sort();

        // Prepare data for ScatterChart: { x: year, y: countryIndex, z: count, ... }
        const chartData = Object.entries(countryData).map(([key, count]) => {
            const [country, yearStr] = key.split('___');
            const year = parseInt(yearStr);
            return {
                country,
                year,
                yIndex: sortedCountries.indexOf(country),
                z: count
            };
        });

        return { data: chartData, countries: sortedCountries };
    }, [entities]);

    return (
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col h-[500px]">
             <div className="flex justify-between items-center mb-4 border-b border-stone-100 pb-2">
                 <h4 className="text-xs font-bold text-stone-500 uppercase tracking-wider">Creation Timeline</h4>
             </div>
             <div className="flex-grow w-full">
                {data.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e5e5" />
                        <XAxis 
                            type="number" 
                            dataKey="year" 
                            name="year" 
                            domain={['dataMin', 2026]} 
                            tickCount={8}
                            tick={{fontSize: 10}}
                            axisLine={{ stroke: '#d6d3d1' }}
                            tickLine={{ stroke: '#d6d3d1' }}
                        />
                        <YAxis 
                            type="number" 
                            dataKey="yIndex" 
                            name="country" 
                            width={110}
                            tickFormatter={(index) => countries[index] || ''}
                            ticks={countries.map((_, i) => i)}
                            tick={{fontSize: 10}}
                            interval={0}
                            axisLine={{ stroke: '#d6d3d1' }}
                            tickLine={false}
                        />
                        <ZAxis type="number" dataKey="z" range={[60, 400]} name="entities" />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                <div className="bg-white p-2 border border-stone-200 shadow-sm text-xs rounded z-50">
                                    <p className="font-bold mb-1">{data.country}</p>
                                    <div className="flex justify-between gap-4">
                                        <span>Year:</span>
                                        <span className="font-mono">{data.year}</span>
                                    </div>
                                    <div className="flex justify-between gap-4 text-green-600 font-bold">
                                        <span>Created:</span>
                                        <span>{data.z}</span>
                                    </div>
                                </div>
                                );
                            }
                            return null;
                        }} />
                        <Scatter name="Entities" data={data} fill="#74c476" shape="circle" />
                    </ScatterChart>
                </ResponsiveContainer>
                ) : (
                    <div className="h-full flex items-center justify-center text-stone-300 text-sm italic">
                        No timeline data available
                    </div>
                )}
             </div>
        </div>
    );
};

// --- COMPONENTS ---

const AuditLogView = ({ embedded = false }) => {
    const [logs, setLogs] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [availableActions, setAvailableActions] = useState([]);
    const [selectedUser, setSelectedUser] = useState('all');
    const [selectedAction, setSelectedAction] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selectedLimit, setSelectedLimit] = useState('100');
    
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await authFetch(`${API_URL}/logs/users`);
                if (res.ok) {
                    const data = await res.json();
                    setAvailableUsers(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                console.error("Failed to fetch users", e);
            }
        };
        const fetchActions = async () => {
            try {
                const res = await authFetch(`${API_URL}/logs/actions`);
                if (res.ok) {
                    const data = await res.json();
                    setAvailableActions(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                console.error("Failed to fetch actions", e);
            }
        };
        fetchUsers();
        fetchActions();
    }, []);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const params = new URLSearchParams();
                params.set('limit', selectedLimit);
                if (selectedUser !== 'all') params.set('user', selectedUser);
                if (selectedAction !== 'all') params.set('action', selectedAction);
                if (dateFrom) params.set('dateFrom', `${dateFrom}T00:00:00.000Z`);
                if (dateTo) params.set('dateTo', `${dateTo}T23:59:59.999Z`);

                const res = await authFetch(`${API_URL}/logs?${params.toString()}`);
                if (res.ok) {
                    const data = await res.json();
                    setLogs(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                console.error("Failed to fetch logs", e);
            }
        };
        fetchLogs();
    }, [selectedUser, selectedAction, selectedLimit, dateFrom, dateTo]);

    const downloadAsJSON = () => {
        const filterSummary = {
            user: selectedUser !== 'all' ? selectedUser : null,
            action: selectedAction !== 'all' ? selectedAction : null,
            dateFrom: dateFrom || null,
            dateTo: dateTo || null,
            totalRecords: logs.length,
            exportDate: new Date().toISOString()
        };

        const exportData = {
            filters: filterSummary,
            logs: logs
        };

        const dataStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const filename = `audit-logs_${new Date().toISOString().split('T')[0]}.json`;
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className={embedded ? '' : `p-8 ${BRAND.light} min-h-screen`}>
            <div className={`mb-6 border-b border-stone-300 pb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 ${embedded ? 'pt-2' : ''}`}>
                <h2 className={`${embedded ? 'text-xl' : 'text-3xl'} font-bold text-stone-900 font-sans`}>Audit Logs</h2>
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs font-bold uppercase text-stone-500">Show</label>
                    <select
                        className="border border-stone-300 rounded-sm p-2 text-sm"
                        value={selectedLimit}
                        onChange={(e) => setSelectedLimit(e.target.value)}
                    >
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="250">250</option>
                        <option value="500">500</option>
                        <option value="1000">1000</option>
                        <option value="all">All</option>
                    </select>
                    <label className="text-xs font-bold uppercase text-stone-500">User</label>
                    <select
                        className="border border-stone-300 rounded-sm p-2 text-sm"
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                    >
                        <option value="all">All</option>
                        {availableUsers.map(username => (
                            <option key={username} value={username}>{username}</option>
                        ))}
                    </select>
                    <label className="text-xs font-bold uppercase text-stone-500">Action</label>
                    <select
                        className="border border-stone-300 rounded-sm p-2 text-sm"
                        value={selectedAction}
                        onChange={(e) => setSelectedAction(e.target.value)}
                    >
                        <option value="all">All</option>
                        {availableActions.map(action => (
                            <option key={action} value={action}>{action}</option>
                        ))}
                    </select>
                    <label className="text-xs font-bold uppercase text-stone-500">From</label>
                    <input type="date" className="border border-stone-300 rounded-sm p-2 text-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    <label className="text-xs font-bold uppercase text-stone-500">To</label>
                    <input type="date" className="border border-stone-300 rounded-sm p-2 text-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    <button
                        onClick={downloadAsJSON}
                        disabled={logs.length === 0}
                        className="ml-2 px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-sm hover:bg-blue-700 disabled:bg-stone-300 disabled:cursor-not-allowed transition"
                    >
                        Download JSON
                    </button>
                </div>
            </div>
            
            <div className="bg-white rounded-sm shadow-sm border border-stone-200 overflow-hidden">
                <table className="w-full text-left text-sm">
                    <thead className="bg-stone-50 text-stone-500 font-bold">
                        <tr>
                            <th className="p-3 border-b">Timestamp</th>
                            <th className="p-3 border-b">User</th>
                            <th className="p-3 border-b">Action</th>
                            <th className="p-3 border-b">Details</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                        {logs.map(log => (
                            <tr key={log.id} className="hover:bg-stone-50">
                                <td className="p-3 text-stone-500 font-mono text-xs whitespace-nowrap">
                                    {new Date(log.timestamp).toLocaleString()}
                                </td>
                                <td className="p-3 font-medium">
                                    {log.username}
                                </td>
                                <td className="p-3">
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                                        log.action === 'LOGIN' ? 'bg-green-100 text-green-700' :
                                        log.action === 'LOGOUT' ? 'bg-stone-200 text-stone-600' :
                                        log.action.includes('DELETE') ? 'bg-red-100 text-red-700' :
                                        'bg-blue-100 text-blue-700'
                                    }`}>
                                        {log.action}
                                    </span>
                                </td>
                                <td className="p-3 text-stone-600 truncate max-w-md" title={log.details}>
                                    {log.details}
                                </td>
                            </tr>
                        ))}
                        {logs.length === 0 && (
                            <tr>
                                <td colSpan="4" className="p-8 text-center text-stone-400">No logs found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const UserProfile = ({ onUserUpdate, embedded = false }) => {
    const splitFullName = (fullName) => {
        const normalized = (fullName || '').trim().replace(/\s+/g, ' ');
        if (!normalized) return { name: '', surname: '' };
        const [name, ...rest] = normalized.split(' ');
        return { name: name || '', surname: rest.join(' ') || '' };
    };

    const composeFullName = (name, surname) => [name, surname].map(v => (v || '').trim()).filter(Boolean).join(' ').trim();

    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        currentPassword: '',
        newPassword: ''
    });
    const [msg, setMsg] = useState(null);
    const user = JSON.parse(localStorage.getItem('user'));

    useEffect(() => {
        if (!user) return;
        const fallback = splitFullName(user.fullName);
        setFormData(prev => ({
            ...prev,
            name: user.name || fallback.name,
            surname: user.surname || fallback.surname
        }));
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMsg(null);

        if (!formData.currentPassword) {
            setMsg({ type: 'error', text: 'Current password is required to make changes.' });
            return;
        }

        try {
            const res = await authFetch(`${API_URL}/auth/profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            const data = await res.json();
            
            if (!res.ok) {
                throw new Error(data.error || "Update failed");
            }

            setMsg({ type: 'success', text: 'Profile updated successfully.' });
            
            const normalizedName = (formData.name || '').trim();
            const normalizedSurname = (formData.surname || '').trim();
            const updatedUser = {
                ...user,
                name: normalizedName,
                surname: normalizedSurname,
                fullName: composeFullName(normalizedName, normalizedSurname) || user?.fullName || user?.username || ''
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            if (onUserUpdate) onUserUpdate(updatedUser);
            
            // Clear passwords
            setFormData(prev => ({ ...prev, currentPassword: '', newPassword: '' }));

        } catch (err) {
            setMsg({ type: 'error', text: err.message });
        }
    };

    return (
        <div className={embedded ? '' : `p-8 ${BRAND.light} min-h-screen`}>
            {!embedded && <h2 className="text-3xl font-bold text-stone-900 font-sans mb-6 border-b border-stone-300 pb-4">My Profile</h2>}
            
            <div className={`${embedded ? '' : 'max-w-md '}bg-white p-6 rounded-sm shadow-sm border border-stone-200`}>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {msg && (
                        <div className={`p-3 text-sm rounded ${msg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                            {msg.text}
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-bold text-stone-500 mb-1">Name</label>
                        <input 
                            type="text" 
                            className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                            value={formData.name}
                            onChange={e => setFormData({...formData, name: e.target.value})}
                            placeholder="e.g. John"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-stone-500 mb-1">Surname</label>
                        <input 
                            type="text" 
                            className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                            value={formData.surname}
                            onChange={e => setFormData({...formData, surname: e.target.value})}
                            placeholder="e.g. Doe"
                        />
                    </div>

                    <div className="pt-4 border-t border-stone-100">
                        <h4 className="text-xs font-bold text-stone-400 uppercase mb-3">Change Password</h4>
                        
                        <div className="mb-3">
                            <label className="block text-xs font-bold text-stone-500 mb-1">Current Password <span className="text-red-500">*</span></label>
                            <input 
                                type="password" 
                                className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                value={formData.currentPassword}
                                onChange={e => setFormData({...formData, currentPassword: e.target.value})}
                                placeholder="Required"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-stone-500 mb-1">New Password</label>
                            <input 
                                type="password" 
                                className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                value={formData.newPassword}
                                onChange={e => setFormData({...formData, newPassword: e.target.value})}
                                placeholder="Leave blank to keep current"
                            />
                        </div>
                    </div>

                    <button 
                        type="submit" 
                        className="w-full bg-stone-800 text-white py-2 rounded-sm text-sm font-medium hover:bg-black transition-colors"
                    >
                        <Save size={16} className="inline mr-2" /> Save Changes
                    </button>
                </form>
            </div>
        </div>
    );
};

// 1. DATA MANAGEMENT VIEW (NEW)
// Legacy view kept for reference while DatabaseHub handles user management.
// eslint-disable-next-line no-unused-vars
const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({ username: '', password: '', name: '', surname: '', role: 'coder', allowedDatabases: ['aipas'] });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const currentUser = JSON.parse(localStorage.getItem('user'));

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await authFetch(`${API_URL}/users`);
            if (res.ok) {
                const data = await res.json();
                setUsers((data || []).map(u => ({ ...u, allowedDatabases: Array.isArray(u.allowedDatabases) ? u.allowedDatabases : [] })));
            }
        } catch (e) {
            console.error("Failed to fetch users", e);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!newUser.username || !newUser.password) {
            setError("Username and password are required");
            return;
        }

        try {
            const res = await authFetch(`${API_URL}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUser)
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to create user");

            setSuccess("User created successfully");
            setNewUser({ username: '', password: '', name: '', surname: '', role: 'coder', allowedDatabases: ['aipas'] });
            fetchUsers();
        } catch (e) {
            setError(e.message);
        }
    };

    const handleToggleNewUserDb = (dbId) => {
        setNewUser(prev => {
            const current = Array.isArray(prev.allowedDatabases) ? prev.allowedDatabases : [];
            const next = current.includes(dbId)
                ? current.filter(id => id !== dbId)
                : [...current, dbId];
            return { ...prev, allowedDatabases: next };
        });
    };

    const handleToggleUserDbPermission = async (targetUser, dbId) => {
        const current = Array.isArray(targetUser.allowedDatabases) ? targetUser.allowedDatabases : [];
        const next = current.includes(dbId)
            ? current.filter(id => id !== dbId)
            : [...current, dbId];

        setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, allowedDatabases: next } : u));

        try {
            const res = await authFetch(`${API_URL}/users/${targetUser.id}/permissions`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ allowedDatabases: next })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to update permissions');
        } catch (e) {
            setError(e.message);
            fetchUsers();
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;
        
        try {
            const res = await authFetch(`${API_URL}/users/${userId}`, { method: 'DELETE' });
            if (res.ok) {
                fetchUsers();
            } else {
                const data = await res.json();
                alert(data.error || "Failed to delete user");
            }
        } catch (e) {
            alert("Error deleting user");
        }
    };

    return (
        <div className="bg-white rounded-sm shadow-sm border border-stone-200 mt-8">
            <div className="p-4 border-b border-stone-200 bg-stone-50">
                <h3 className="text-sm font-bold text-stone-800 uppercase flex items-center gap-2">
                    <Users size={16} /> User Management
                </h3>
            </div>
            
            <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* User List */}
                    <div>
                        <h4 className="text-xs font-bold text-stone-500 uppercase mb-3">Existing Users</h4>
                        <div className="border border-stone-200 rounded-sm overflow-hidden">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-stone-50 text-stone-500 font-bold">
                                    <tr>
                                        <th className="p-2">Name / User</th>
                                        <th className="p-2">Role</th>
                                        <th className="p-2">Databases</th>
                                        <th className="p-2 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-100">
                                    {users.map(u => (
                                        <tr key={u.id} className="hover:bg-stone-50">
                                            <td className="p-2">
                                                <div className="font-bold">{[u.name, u.surname].filter(Boolean).join(' ') || u.fullName || '-'}</div>
                                                <div className="text-stone-400 text-xs">{u.username} {u.id === currentUser.id && '(you)'}</div>
                                            </td>
                                            <td className="p-2">
                                                <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                                                    u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {u.role}
                                                </span>
                                            </td>
                                            <td className="p-2">
                                                <div className="flex flex-wrap gap-2">
                                                    {PLATFORM_DATABASES.map(dbItem => {
                                                        const checked = (u.allowedDatabases || []).includes(dbItem.id);
                                                        return (
                                                            <label key={`${u.id}-${dbItem.id}`} className="inline-flex items-center gap-1 text-xs text-stone-700 bg-stone-100 px-2 py-1 rounded">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={() => handleToggleUserDbPermission(u, dbItem.id)}
                                                                    className="accent-green-700"
                                                                />
                                                                {dbItem.title}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                            <td className="p-2 text-right">
                                                {u.id !== currentUser.id && (
                                                    <button 
                                                        onClick={() => handleDeleteUser(u.id)}
                                                        className="text-red-400 hover:text-red-600 p-1"
                                                        title="Delete User"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Add User Form */}
                    <div>
                        <h4 className="text-xs font-bold text-stone-500 uppercase mb-3">Add New User</h4>
                        <form onSubmit={handleAddUser} className="space-y-4 bg-stone-50 p-4 rounded-sm border border-stone-100">
                            {error && <div className="text-red-600 text-xs">{error}</div>}
                            {success && <div className="text-green-600 text-xs">{success}</div>}
                            
                            <div>
                                <label className="block text-xs font-bold text-stone-500 mb-1">Name</label>
                                <input 
                                    type="text" 
                                    className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                    value={newUser.name}
                                    onChange={e => setNewUser({...newUser, name: e.target.value})}
                                    placeholder="Name"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-stone-500 mb-1">Surname</label>
                                <input 
                                    type="text" 
                                    className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                    value={newUser.surname}
                                    onChange={e => setNewUser({...newUser, surname: e.target.value})}
                                    placeholder="Surname"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-stone-500 mb-1">Username</label>
                                <input 
                                    type="text" 
                                    className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                    value={newUser.username}
                                    onChange={e => setNewUser({...newUser, username: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-stone-500 mb-1">Password</label>
                                <input 
                                    type="password" 
                                    className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                    value={newUser.password}
                                    onChange={e => setNewUser({...newUser, password: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-stone-500 mb-1">Role</label>
                                <select 
                                    className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                    value={newUser.role}
                                    onChange={e => setNewUser({...newUser, role: e.target.value})}
                                >
                                    <option value="coder">Coder (Edit Data)</option>
                                    <option value="admin">Admin (Full Access)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-stone-500 mb-2">Database Access</label>
                                <div className="flex flex-wrap gap-2">
                                    {PLATFORM_DATABASES.map(dbItem => (
                                        <label key={`new-${dbItem.id}`} className="inline-flex items-center gap-1 text-xs text-stone-700 bg-white px-2 py-1 rounded border border-stone-200">
                                            <input
                                                type="checkbox"
                                                checked={(newUser.allowedDatabases || []).includes(dbItem.id)}
                                                onChange={() => handleToggleNewUserDb(dbItem.id)}
                                                className="accent-green-700"
                                            />
                                            {dbItem.title}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <button 
                                type="submit" 
                                className="w-full bg-stone-800 text-white py-2 rounded-sm text-sm font-medium hover:bg-black transition-colors"
                            >
                                <UserPlus size={16} className="inline mr-2" /> Create User
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DatabaseHub = ({ user, onSelectDatabase, onLogout, onUserUpdate, platformDatabases = PLATFORM_DATABASES, onDatabasesUpdated }) => {
    const allowedDatabases = Array.isArray(user?.allowedDatabases) ? user.allowedDatabases : [];
    const isAdmin = user?.role === 'admin';
    const isPublicViewer = user?.role === 'viewer';
    const displayName = user?.name || user?.fullName?.trim()?.split(/\s+/)?.[0] || user?.username || '';
    const accessibleDatabaseItems = isAdmin
        ? (platformDatabases || []).filter(dbItem => dbItem.available)
        : isPublicViewer
        ? (platformDatabases || []).filter(dbItem => dbItem.available && dbItem.publicRead)
        : (platformDatabases || []).filter(dbItem => allowedDatabases.includes(dbItem.id));
    const [hubTab, setHubTab] = useState('databases');
    const [hubSectionFilter, setHubSectionFilter] = useState('all');
    const [users, setUsers] = useState([]);
    const [permissionsMsg, setPermissionsMsg] = useState(null);
    const [newUser, setNewUser] = useState({ username: '', password: '', name: '', surname: '', role: 'coder', allowedDatabases: ['aipas'], automaticValidationSampleSize: 200 });
    const [createUserMsg, setCreateUserMsg] = useState(null);
    const [userSampleSizeDrafts, setUserSampleSizeDrafts] = useState({});
    const [userPasswordDrafts, setUserPasswordDrafts] = useState({});
    const [pendingUserAction, setPendingUserAction] = useState(null);
    const [dbSettingsMsg, setDbSettingsMsg] = useState(null);
    const [databaseDrafts, setDatabaseDrafts] = useState({});
    const [catalogCategories, setCatalogCategories] = useState([]);
    const [categoryDrafts, setCategoryDrafts] = useState({});
    const [newCategoryName, setNewCategoryName] = useState('');
    const [pendingDeleteCategory, setPendingDeleteCategory] = useState(null);
    const [pendingDeleteReplacement, setPendingDeleteReplacement] = useState('national');
    const [categoryMsg, setCategoryMsg] = useState(null);

    const availableSectionOptions = useMemo(() => {
        const categoryKeys = (catalogCategories || []).map((item) => String(item?.key || '').trim()).filter(Boolean);
        if (categoryKeys.length > 0) {
            return Array.from(new Set(categoryKeys)).sort((a, b) => a.localeCompare(b, 'en'));
        }
        const sourceItems = isAdmin ? (platformDatabases || []) : accessibleDatabaseItems;
        const collected = new Set();
        sourceItems.forEach((dbItem) => {
            const section = String(dbItem?.section || '').trim();
            if (section) collected.add(section);
        });
        return Array.from(collected).sort((a, b) => a.localeCompare(b, 'en'));
    }, [catalogCategories, isAdmin, platformDatabases, accessibleDatabaseItems]);

    const hubVisibleSectionOptions = useMemo(() => {
        const collected = new Set();
        (accessibleDatabaseItems || []).forEach((dbItem) => {
            const section = String(dbItem?.section || '').trim();
            if (section) collected.add(section);
        });
        return Array.from(collected).sort((a, b) => a.localeCompare(b, 'en'));
    }, [accessibleDatabaseItems]);

    const hubSectionOptions = useMemo(() => (['all', ...hubVisibleSectionOptions]), [hubVisibleSectionOptions]);

    const filteredDatabaseItems = useMemo(() => (
        hubSectionFilter === 'all'
            ? accessibleDatabaseItems
            : accessibleDatabaseItems.filter((dbItem) => dbItem.section === hubSectionFilter)
    ), [accessibleDatabaseItems, hubSectionFilter]);

    useEffect(() => {
        if (hubSectionFilter === 'all') return;
        if (!hubSectionOptions.includes(hubSectionFilter)) {
            setHubSectionFilter('all');
        }
    }, [hubSectionFilter, hubSectionOptions]);

    const orderedDatabaseItems = useMemo(() => {
        const sectionOrder = {
            international: 0,
            national: 1,
            regional: 2,
            web: 3
        };
        return [...filteredDatabaseItems].sort((a, b) => {
            const orderDiff = Number(a.sortOrder ?? 999) - Number(b.sortOrder ?? 999);
            if (orderDiff !== 0) return orderDiff;
            const sectionDiff = (sectionOrder[a.section] ?? 99) - (sectionOrder[b.section] ?? 99);
            if (sectionDiff !== 0) return sectionDiff;
            return String(a.title || '').localeCompare(String(b.title || ''), 'en');
        });
    }, [filteredDatabaseItems]);

    const fetchUsers = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const res = await authFetch(`${API_URL}/users`);
            if (!res.ok) return;
            const data = await res.json();
            const normalizedUsers = (data || []).map(u => ({
                ...u,
                allowedDatabases: Array.isArray(u.allowedDatabases) ? u.allowedDatabases : [],
                automaticValidationSampleSize: Number.isFinite(Number(u?.databaseSettings?.automatic_coding_validation?.sampleSize))
                    ? Number(u.databaseSettings.automatic_coding_validation.sampleSize)
                    : Number.isFinite(Number(u?.automaticValidationSampleSize))
                    ? Number(u.automaticValidationSampleSize)
                    : 200
            }));
            setUsers(normalizedUsers);
            setUserSampleSizeDrafts(
                normalizedUsers.reduce((acc, item) => {
                    acc[item.id] = item.automaticValidationSampleSize;
                    return acc;
                }, {})
            );
        } catch {
            // silent
        }
    }, [isAdmin]);

    const fetchCategories = useCallback(async () => {
        if (!isAdmin) {
            setCatalogCategories([]);
            return;
        }
        try {
            const res = await authFetch(`${API_URL}/databases/categories`);
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                setCategoryMsg({ type: 'error', text: payload?.error || 'Failed to load categories.' });
                return;
            }
            const data = await res.json();
            const rows = Array.isArray(data?.rows) ? data.rows : [];
            setCatalogCategories(rows.map((item) => ({
                key: String(item?.key || '').trim(),
                sortOrder: Number.isFinite(Number(item?.sortOrder)) ? Number(item.sortOrder) : 999,
                databasesCount: Number(item?.databasesCount || 0)
            })).filter((item) => item.key));
        } catch {
            setCategoryMsg({ type: 'error', text: 'Failed to load categories.' });
        }
    }, [isAdmin]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        const nextDrafts = (catalogCategories || []).reduce((acc, item) => {
            acc[item.key] = item.key;
            return acc;
        }, {});
        setCategoryDrafts(nextDrafts);
    }, [catalogCategories]);

    const persistUserPermissions = async ({ targetUser, nextAllowedDatabases }) => {
        try {
            const res = await authFetch(`${API_URL}/users/${targetUser.id}/permissions`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ allowedDatabases: nextAllowedDatabases })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to update permissions');
            setPermissionsMsg({ type: 'success', text: 'Permissions updated.' });
            if (targetUser.id === user.id) {
                const updatedCurrentUser = {
                    ...user,
                    allowedDatabases: nextAllowedDatabases
                };
                localStorage.setItem('user', JSON.stringify(updatedCurrentUser));
                if (onUserUpdate) onUserUpdate(updatedCurrentUser);
            }
        } catch (e) {
            setPermissionsMsg({ type: 'error', text: e.message || 'Failed to update permissions.' });
            fetchUsers();
        }
    };

    const handleToggleUserDbPermission = async (targetUser, dbId) => {
        const current = Array.isArray(targetUser.allowedDatabases) ? targetUser.allowedDatabases : [];
        const nextAllowed = current.includes(dbId)
            ? current.filter(id => id !== dbId)
            : [...current, dbId];

        setUsers(prev => prev.map(u => u.id === targetUser.id
            ? { ...u, allowedDatabases: nextAllowed }
            : u));

        await persistUserPermissions({
            targetUser,
            nextAllowedDatabases: nextAllowed
        });
    };

    const handleSaveUserSampleSize = async (targetUser) => {
        const draftValue = Number.parseInt(String(userSampleSizeDrafts[targetUser.id] ?? ''), 10);
        const nextSampleSize = Number.isFinite(draftValue) && draftValue > 0 ? draftValue : 200;
        setPendingUserAction(`sample-${targetUser.id}`);
        try {
            const res = await authFetch(`${API_URL}/users/${targetUser.id}/database-settings/automatic_coding_validation`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sampleSize: nextSampleSize })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to update database settings');
            setUsers(prev => prev.map(u => u.id === targetUser.id
                ? {
                    ...u,
                    automaticValidationSampleSize: nextSampleSize,
                    databaseSettings: {
                        ...(u.databaseSettings || {}),
                        automatic_coding_validation: { sampleSize: nextSampleSize }
                    }
                }
                : u));
            setPermissionsMsg({ type: 'success', text: `Automatic Coding Validation settings updated for ${targetUser.username}.` });
        } catch (e) {
            setPermissionsMsg({ type: 'error', text: e.message || 'Failed to update database settings.' });
            fetchUsers();
        } finally {
            setPendingUserAction(null);
        }
    };

    const handleChangeUserRole = async (targetUser, nextRole) => {
        if (nextRole === targetUser.role) return;
        if (targetUser.role === 'admin' && nextRole !== 'admin') {
            const confirmed = window.confirm(`Remove administrator access from ${targetUser.username}? Their current sessions will be closed.`);
            if (!confirmed) return;
        }

        setPendingUserAction(`role-${targetUser.id}`);
        try {
            const res = await authFetch(`${API_URL}/users/${targetUser.id}/role`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role: nextRole })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to update role');
            setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, role: nextRole } : u));
            setPermissionsMsg({ type: 'success', text: `Role updated for ${targetUser.username}.` });
        } catch (e) {
            setPermissionsMsg({ type: 'error', text: e.message || 'Failed to update role.' });
            fetchUsers();
        } finally {
            setPendingUserAction(null);
        }
    };

    const handleResetUserPassword = async (targetUser) => {
        const password = String(userPasswordDrafts[targetUser.id] || '');
        if (!password) {
            setPermissionsMsg({ type: 'error', text: `Enter a new password for ${targetUser.username}.` });
            return;
        }
        if (!window.confirm(`Reset the password for ${targetUser.username}? All their current sessions will be closed.`)) return;

        setPendingUserAction(`password-${targetUser.id}`);
        try {
            const res = await authFetch(`${API_URL}/users/${targetUser.id}/password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to reset password');
            setUserPasswordDrafts(prev => ({ ...prev, [targetUser.id]: '' }));
            setPermissionsMsg({ type: 'success', text: `Password reset for ${targetUser.username}.` });
        } catch (e) {
            setPermissionsMsg({ type: 'error', text: e.message || 'Failed to reset password.' });
        } finally {
            setPendingUserAction(null);
        }
    };

    const handleToggleNewUserDb = (dbId) => {
        setNewUser(prev => {
            const current = Array.isArray(prev.allowedDatabases) ? prev.allowedDatabases : [];
            const next = current.includes(dbId)
                ? current.filter(id => id !== dbId)
                : [...current, dbId];
            return { ...prev, allowedDatabases: next };
        });
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setCreateUserMsg(null);

        if (!newUser.username || !newUser.password) {
            setCreateUserMsg({ type: 'error', text: 'Username and password are required.' });
            return;
        }

        try {
            const res = await authFetch(`${API_URL}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUser)
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to create user');

            setCreateUserMsg({ type: 'success', text: 'User created successfully.' });
            setNewUser({ username: '', password: '', name: '', surname: '', role: 'coder', allowedDatabases: ['aipas'], automaticValidationSampleSize: 200 });
            fetchUsers();
        } catch (e) {
            setCreateUserMsg({ type: 'error', text: e.message || 'Failed to create user.' });
        }
    };

    useEffect(() => {
        const nextDrafts = (platformDatabases || []).reduce((acc, item) => {
            acc[item.id] = {
                title: item.title || '',
                section: item.section || 'national',
                colorHex: item.colorHex || '#5F5D57',
                sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 999,
                available: !!item.available,
                publicRead: !!item.publicRead
            };
            return acc;
        }, {});
        setDatabaseDrafts(nextDrafts);
    }, [platformDatabases]);

    const managedDatabaseItems = useMemo(() => {
        return [...(platformDatabases || [])]
            .map((item) => {
                const draft = databaseDrafts[item.id] || {};
                return {
                    ...item,
                    effectiveSortOrder: Number.isFinite(Number(draft.sortOrder)) ? Number(draft.sortOrder) : 999
                };
            })
            .sort((a, b) => {
                const orderDiff = Number(a.effectiveSortOrder || 999) - Number(b.effectiveSortOrder || 999);
                if (orderDiff !== 0) return orderDiff;
                return String(a.title || '').localeCompare(String(b.title || ''), 'en');
            });
    }, [platformDatabases, databaseDrafts]);

    const handleDatabaseDraftChange = (databaseId, field, value) => {
        setDatabaseDrafts(prev => ({
            ...prev,
            [databaseId]: {
                ...(prev[databaseId] || {}),
                [field]: value
            }
        }));
    };

    const persistDatabaseSettings = useCallback(async (databaseId, draft, { silent = false } = {}) => {
        const res = await authFetch(`${API_URL}/databases/catalog/${encodeURIComponent(databaseId)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(draft)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to update database settings');

        if (!silent) {
            setDbSettingsMsg({ type: 'success', text: `Settings updated for ${draft.title || databaseId}.` });
            await Promise.all([
                onDatabasesUpdated ? onDatabasesUpdated() : Promise.resolve(),
                fetchCategories()
            ]);
        }
    }, [onDatabasesUpdated, fetchCategories]);

    const handleSectionSelect = async (databaseId, sectionValue) => {
        const normalizedSection = String(sectionValue || '').trim() || 'national';
        const currentDraft = databaseDrafts[databaseId];
        if (!currentDraft) return;

        const nextDraft = {
            ...currentDraft,
            section: normalizedSection
        };

        setDatabaseDrafts(prev => ({
            ...prev,
            [databaseId]: nextDraft
        }));

        setDbSettingsMsg({ type: 'success', text: `Saving category for ${nextDraft.title || databaseId}...` });
        try {
            await persistDatabaseSettings(databaseId, nextDraft, { silent: true });
            setDbSettingsMsg({ type: 'success', text: `Category updated for ${nextDraft.title || databaseId}.` });
            await Promise.all([
                onDatabasesUpdated ? onDatabasesUpdated() : Promise.resolve(),
                fetchCategories()
            ]);
        } catch (e) {
            setDbSettingsMsg({ type: 'error', text: e.message || 'Failed to update category.' });
        }
    };

    const handleMoveDatabase = async (databaseId, direction) => {
        const orderedIds = managedDatabaseItems.map((item) => item.id);
        const currentIndex = orderedIds.indexOf(databaseId);
        if (currentIndex < 0) return;

        const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (nextIndex < 0 || nextIndex >= orderedIds.length) return;

        const swappedIds = [...orderedIds];
        [swappedIds[currentIndex], swappedIds[nextIndex]] = [swappedIds[nextIndex], swappedIds[currentIndex]];

        const prevDrafts = databaseDrafts;
        const updatedDrafts = { ...prevDrafts };
        swappedIds.forEach((id, index) => {
            updatedDrafts[id] = {
                ...(updatedDrafts[id] || {}),
                sortOrder: index + 1
            };
        });

        const changedIds = swappedIds.filter((id) => {
            const prevOrder = Number(prevDrafts[id]?.sortOrder);
            const nextOrder = Number(updatedDrafts[id]?.sortOrder);
            return prevOrder !== nextOrder;
        });

        setDatabaseDrafts(updatedDrafts);
        setDbSettingsMsg({ type: 'success', text: 'Saving order...' });

        try {
            await Promise.all(changedIds.map((id) => persistDatabaseSettings(id, updatedDrafts[id], { silent: true })));
            setDbSettingsMsg({ type: 'success', text: 'Order updated.' });
            await Promise.all([
                onDatabasesUpdated ? onDatabasesUpdated() : Promise.resolve(),
                fetchCategories()
            ]);
        } catch (e) {
            setDbSettingsMsg({ type: 'error', text: e.message || 'Failed to update order.' });
        }
    };

    const handleSaveDatabaseSettings = async (databaseId) => {
        const draft = databaseDrafts[databaseId];
        if (!draft) return;
        setDbSettingsMsg(null);
        try {
            await persistDatabaseSettings(databaseId, draft, { silent: false });
        } catch (e) {
            setDbSettingsMsg({ type: 'error', text: e.message || 'Failed to update database settings.' });
        }
    };

    const handleCreateCategory = async () => {
        setCategoryMsg(null);
        setPendingDeleteCategory(null);
        const candidate = String(newCategoryName || '').trim();
        if (!candidate) {
            setCategoryMsg({ type: 'error', text: 'Category name is required.' });
            return;
        }

        try {
            const res = await authFetch(`${API_URL}/databases/categories`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: candidate })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.error || 'Failed to create category');
            setCategoryMsg({ type: 'success', text: `Category created: ${candidate}` });
            setNewCategoryName('');
            await fetchCategories();
        } catch (e) {
            setCategoryMsg({ type: 'error', text: e.message || 'Failed to create category.' });
        }
    };

    const handleRenameCategory = async (currentKey) => {
        setCategoryMsg(null);
        setPendingDeleteCategory(null);
        const nextName = String(categoryDrafts[currentKey] || '').trim();
        if (!nextName) {
            setCategoryMsg({ type: 'error', text: 'Category name is required.' });
            return;
        }

        try {
            const res = await authFetch(`${API_URL}/databases/categories/${encodeURIComponent(currentKey)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: nextName })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.error || 'Failed to rename category');
            setCategoryMsg({ type: 'success', text: `Category updated: ${currentKey} -> ${nextName}` });
            await Promise.all([fetchCategories(), onDatabasesUpdated ? onDatabasesUpdated() : Promise.resolve()]);
        } catch (e) {
            setCategoryMsg({ type: 'error', text: e.message || 'Failed to rename category.' });
        }
    };

    const startDeleteCategory = (categoryKey) => {
        setCategoryMsg(null);
        if (categoryKey === 'national') {
            setCategoryMsg({ type: 'error', text: 'Category "national" cannot be deleted.' });
            return;
        }

        const options = availableSectionOptions.filter((option) => option !== categoryKey);
        if (options.length === 0) {
            setCategoryMsg({ type: 'error', text: 'No replacement category available.' });
            return;
        }

        const defaultReplacement = options.includes('national') ? 'national' : options[0];
        setPendingDeleteCategory(categoryKey);
        setPendingDeleteReplacement(defaultReplacement);
    };

    const handleDeleteCategory = async (categoryKey, replacement) => {
        setCategoryMsg(null);
        if (!replacement || replacement === categoryKey) {
            setCategoryMsg({ type: 'error', text: 'Choose a valid replacement category.' });
            return;
        }

        try {
            const res = await authFetch(`${API_URL}/databases/categories/${encodeURIComponent(categoryKey)}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ replacement })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.error || 'Failed to delete category');
            setCategoryMsg({ type: 'success', text: `Category deleted: ${categoryKey}` });
            setPendingDeleteCategory(null);
            await Promise.all([fetchCategories(), onDatabasesUpdated ? onDatabasesUpdated() : Promise.resolve()]);
        } catch (e) {
            setCategoryMsg({ type: 'error', text: e.message || 'Failed to delete category.' });
        }
    };

    return (
        <div className={`min-h-screen ${BRAND.light} p-8`}>
            <div className="w-full">
                <div className="flex flex-col items-center gap-4 lg:flex-row lg:justify-between lg:items-start mb-8 border-b border-stone-300 pb-4">
                    <div className="w-full flex justify-center lg:w-auto lg:justify-start">
                        <div className="flex flex-col items-center text-center">
                            <QDemLogo className="w-40 lg:w-52 h-auto max-w-none" />
                            <span className="mt-1 text-3xl font-bold text-[#74c476] font-sans leading-none">Data</span>
                        </div>
                    </div>
                    <div className="w-full text-center lg:text-right">
                        <div className="text-3xl md:text-4xl font-bold text-stone-700">Welcome, {displayName}</div>
                        <div className="mt-3 flex flex-wrap justify-center lg:justify-end items-center gap-2 text-sm font-medium">
                            <div className="inline-flex rounded-sm border border-stone-300 overflow-hidden">
                                {hubTab !== 'databases' && (
                                    <button
                                        type="button"
                                        onClick={() => setHubTab('databases')}
                                        className="px-5 py-2 bg-white text-stone-600 hover:bg-stone-100"
                                    >
                                        Databases
                                    </button>
                                )}
                                {!isPublicViewer && (
                                    <button
                                        type="button"
                                        onClick={() => setHubTab('account')}
                                        className={`px-5 py-2 ${hubTab !== 'databases' ? 'border-l border-stone-300' : ''} ${hubTab === 'account' ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-100'}`}
                                    >
                                        My Account
                                    </button>
                                )}
                                {isAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => setHubTab('db-management')}
                                        className={`px-5 py-2 border-l border-stone-300 ${hubTab === 'db-management' ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-100'}`}
                                    >
                                        Databases Management
                                    </button>
                                )}
                                {isAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => setHubTab('users')}
                                        className={`px-5 py-2 border-l border-stone-300 ${hubTab === 'users' ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-100'}`}
                                    >
                                        User Management
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={onLogout}
                                className="px-5 py-2 rounded-sm border border-stone-300 bg-white text-stone-600 hover:bg-stone-100"
                            >
                                {isPublicViewer ? 'Sign in' : 'Log out'}
                            </button>
                        </div>
                    </div>
                </div>

                {hubTab === 'databases' && (
                    <div className="space-y-6">
                        <div className="flex flex-wrap items-center gap-2">
                                    {hubSectionOptions.map((sectionKey) => (
                                <button
                                    key={`hub-section-${sectionKey}`}
                                    type="button"
                                    onClick={() => setHubSectionFilter(sectionKey)}
                                    className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wide border transition-colors ${hubSectionFilter === sectionKey ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-100'}`}
                                >
                                    {sectionKey === 'all' ? 'all' : sectionKey}
                                </button>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-0 border border-stone-200">
                            {orderedDatabaseItems.map((dbItem) => {
                                const isClickable = dbItem.available;
                                return (
                                    <button
                                        key={dbItem.id}
                                        type="button"
                                        onClick={() => isClickable && onSelectDatabase(dbItem.id)}
                                        style={{ backgroundColor: dbItem.colorHex || '#5F5D57' }}
                                        className={`relative min-h-[210px] w-full text-white p-6 transition-all border border-white/20 ${isClickable ? 'hover:brightness-95' : 'opacity-90 cursor-default'}`}
                                    >
                                        <div className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.16em] bg-black/20 px-2 py-1 border border-white/30">
                                            {String(dbItem.section || 'national')}
                                        </div>
                                        <div className="h-full flex flex-col items-center justify-center text-center pt-3">
                                            <div className="text-xl md:text-2xl font-bold tracking-wide">{dbItem.title}</div>
                                            {!dbItem.available && <div className="text-xs mt-3 text-white/80">Coming soon</div>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {hubTab === 'databases' && filteredDatabaseItems.length === 0 && (
                    <div className="mt-4 p-4 bg-white border border-stone-200 text-stone-500 text-sm">No database assigned. Contact an administrator.</div>
                )}

                {!isPublicViewer && hubTab === 'account' && (
                    <div className="mt-8">
                        <UserProfile onUserUpdate={onUserUpdate} embedded />
                    </div>
                )}

                {isAdmin && hubTab === 'db-management' && (
                    <div className="mt-8 bg-white rounded-sm shadow-sm border border-stone-200">
                        <div className="p-4 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
                            <h3 className="text-sm font-bold text-stone-800 uppercase">Databases Management</h3>
                            <span className="text-xs text-stone-500">Colors, sections and visibility</span>
                        </div>
                        <div className="p-4 space-y-4">
                            {dbSettingsMsg && (
                                <div className={`p-2 rounded-sm text-xs ${dbSettingsMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                                    {dbSettingsMsg.text}
                                </div>
                            )}

                            <div className="p-3 bg-stone-50 border border-stone-200 rounded-sm space-y-3">
                                <div className="flex items-center justify-between gap-2">
                                    <div>
                                        <h4 className="text-xs font-bold text-stone-700 uppercase">Categories</h4>
                                        <p className="text-xs text-stone-500">Create or rename categories. If you delete one, its databases are moved to another category.</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={newCategoryName}
                                            onChange={(e) => setNewCategoryName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleCreateCategory();
                                                }
                                            }}
                                            placeholder="New category"
                                            className="border border-stone-300 rounded-sm p-2 text-xs"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleCreateCategory}
                                            className="px-3 py-2 rounded-sm bg-stone-800 text-white text-xs font-medium hover:bg-black"
                                        >
                                            Add category
                                        </button>
                                    </div>
                                </div>

                                {categoryMsg && (
                                    <div className={`p-2 rounded-sm text-xs ${categoryMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                                        {categoryMsg.text}
                                    </div>
                                )}

                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead className="text-left uppercase text-stone-500 border-b border-stone-200">
                                            <tr>
                                                <th className="py-2 pr-2">Category</th>
                                                <th className="py-2 pr-2">Databases</th>
                                                <th className="py-2">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-stone-100">
                                            {(catalogCategories || []).map((category) => (
                                                <tr key={`hub-category-${category.key}`}>
                                                    <td className="py-2 pr-2">
                                                        <input
                                                            type="text"
                                                            value={categoryDrafts[category.key] || category.key}
                                                            onChange={(e) => setCategoryDrafts(prev => ({ ...prev, [category.key]: e.target.value }))}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    handleRenameCategory(category.key);
                                                                }
                                                            }}
                                                            className="w-full border border-stone-300 rounded-sm p-2 text-xs"
                                                        />
                                                    </td>
                                                    <td className="py-2 pr-2 text-stone-600">{category.databasesCount}</td>
                                                    <td className="py-2">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRenameCategory(category.key)}
                                                                className="px-2 py-1 rounded-sm bg-stone-700 text-white text-[11px] hover:bg-black"
                                                            >
                                                                Save name
                                                            </button>

                                                            {category.key === 'national' ? (
                                                                <span className="text-[11px] text-stone-500">Protected</span>
                                                            ) : pendingDeleteCategory === category.key ? (
                                                                <>
                                                                    <select
                                                                        value={pendingDeleteReplacement}
                                                                        onChange={(e) => setPendingDeleteReplacement(e.target.value)}
                                                                        className="border border-stone-300 rounded-sm p-1.5 text-[11px] bg-white"
                                                                    >
                                                                        {availableSectionOptions.filter((option) => option !== category.key).map((option) => (
                                                                            <option key={`hub-delete-replacement-${category.key}-${option}`} value={option}>
                                                                                Move to: {option}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteCategory(category.key, pendingDeleteReplacement)}
                                                                        className="px-2 py-1 rounded-sm bg-red-700 text-white text-[11px] hover:bg-red-800"
                                                                    >
                                                                        Confirm delete
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setPendingDeleteCategory(null)}
                                                                        className="px-2 py-1 rounded-sm border border-stone-300 text-stone-600 text-[11px] hover:bg-stone-100"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => startDeleteCategory(category.key)}
                                                                    className="px-2 py-1 rounded-sm bg-red-700 text-white text-[11px] hover:bg-red-800"
                                                                >
                                                                    Delete
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-left text-xs uppercase text-stone-500 border-b border-stone-200">
                                        <tr>
                                            <th className="py-2 pr-3">Database</th>
                                            <th className="py-2 pr-3">Section</th>
                                            <th className="py-2 pr-3">Order</th>
                                            <th className="py-2 pr-3">Color</th>
                                            <th className="py-2 pr-3">Visible in hub</th>
                                            <th className="py-2 pr-3">Public read-only</th>
                                            <th className="py-2">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100">
                                        {managedDatabaseItems.map((dbItem, index) => {
                                            const draft = databaseDrafts[dbItem.id] || {};
                                            return (
                                                <tr key={`hub-db-settings-${dbItem.id}`}>
                                                    <td className="py-2 pr-3">
                                                        <input
                                                            type="text"
                                                            value={draft.title || ''}
                                                            onChange={(e) => handleDatabaseDraftChange(dbItem.id, 'title', e.target.value)}
                                                            className="w-full border border-stone-300 rounded-sm p-2 text-sm"
                                                        />
                                                    </td>
                                                    <td className="py-2 pr-3">
                                                        <select
                                                            value={String(draft.section || 'national')}
                                                            onChange={(e) => handleSectionSelect(dbItem.id, e.target.value)}
                                                            className="w-full border border-stone-300 rounded-sm p-2 text-sm bg-white"
                                                        >
                                                            {availableSectionOptions.map((section) => (
                                                                <option key={`hub-db-section-${dbItem.id}-${section}`} value={section}>
                                                                    {section}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="py-2 pr-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-stone-600 w-6">{index + 1}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleMoveDatabase(dbItem.id, 'up')}
                                                                disabled={index === 0}
                                                                className={`px-2 py-1 rounded-sm text-xs ${index === 0 ? 'bg-stone-200 text-stone-400 cursor-not-allowed' : 'bg-stone-700 text-white hover:bg-black'}`}
                                                            >
                                                                ↑
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleMoveDatabase(dbItem.id, 'down')}
                                                                disabled={index === managedDatabaseItems.length - 1}
                                                                className={`px-2 py-1 rounded-sm text-xs ${index === managedDatabaseItems.length - 1 ? 'bg-stone-200 text-stone-400 cursor-not-allowed' : 'bg-stone-700 text-white hover:bg-black'}`}
                                                            >
                                                                ↓
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="py-2 pr-3">
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="color"
                                                                value={draft.colorHex || '#5F5D57'}
                                                                onChange={(e) => handleDatabaseDraftChange(dbItem.id, 'colorHex', e.target.value)}
                                                                className="h-9 w-11 border border-stone-300 rounded-sm bg-white"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={draft.colorHex || '#5F5D57'}
                                                                onChange={(e) => handleDatabaseDraftChange(dbItem.id, 'colorHex', e.target.value)}
                                                                className="w-28 border border-stone-300 rounded-sm p-2 text-xs font-mono uppercase"
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="py-2 pr-3">
                                                        <label className="inline-flex items-center gap-2 text-xs text-stone-700">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!draft.available}
                                                                onChange={(e) => handleDatabaseDraftChange(dbItem.id, 'available', e.target.checked)}
                                                                className="accent-green-700"
                                                            />
                                                            Enabled
                                                        </label>
                                                    </td>
                                                    <td className="py-2 pr-3">
                                                        <label className="inline-flex items-center gap-2 text-xs text-stone-700">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!draft.publicRead}
                                                                disabled={!draft.available || !PUBLIC_READ_DATABASE_WORKSPACES.includes(dbItem.id)}
                                                                onChange={(e) => handleDatabaseDraftChange(dbItem.id, 'publicRead', e.target.checked)}
                                                                className="accent-green-700"
                                                            />
                                                            {PUBLIC_READ_DATABASE_WORKSPACES.includes(dbItem.id) ? 'Anonymous viewing' : 'Not supported'}
                                                        </label>
                                                    </td>
                                                    <td className="py-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleSaveDatabaseSettings(dbItem.id)}
                                                            className="px-3 py-1.5 rounded-sm bg-stone-800 text-white text-xs font-medium hover:bg-black"
                                                        >
                                                            Save
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {isAdmin && hubTab === 'users' && (
                    <div className="mt-8 bg-white rounded-sm shadow-sm border border-stone-200">
                        <div className="p-4 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
                            <h3 className="text-sm font-bold text-stone-800 uppercase">User Management</h3>
                            <span className="text-xs text-stone-500">Accounts, roles and database access</span>
                        </div>
                        <div className="p-4">
                            <div className="mb-6 p-4 bg-stone-50 border border-stone-200 rounded-sm">
                                <h4 className="text-xs font-bold text-stone-600 uppercase mb-3">Create User</h4>
                                {createUserMsg && (
                                    <div className={`mb-3 p-2 rounded-sm text-xs ${createUserMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                                        {createUserMsg.text}
                                    </div>
                                )}
                                <form onSubmit={handleCreateUser} className="space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <input
                                            type="text"
                                            className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                            value={newUser.name}
                                            onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                                            placeholder="Name"
                                        />
                                        <input
                                            type="text"
                                            className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                            value={newUser.surname}
                                            onChange={e => setNewUser({ ...newUser, surname: e.target.value })}
                                            placeholder="Surname"
                                        />
                                        <input
                                            type="text"
                                            className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                            value={newUser.username}
                                            onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                                            placeholder="Username"
                                        />
                                        <input
                                            type="password"
                                            className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                            value={newUser.password}
                                            onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                                            placeholder="Password"
                                        />
                                    </div>
                                    <div>
                                        <div className="text-[11px] font-bold uppercase text-stone-500 mb-2">Database access</div>
                                        <div className="flex flex-wrap gap-2">
                                            {platformDatabases.map(dbItem => (
                                                <label key={`hub-new-${dbItem.id}`} className="inline-flex items-center gap-1 text-xs text-stone-700 bg-white px-2 py-1 rounded border border-stone-200">
                                                    <input
                                                        type="checkbox"
                                                        checked={(newUser.allowedDatabases || []).includes(dbItem.id)}
                                                        onChange={() => handleToggleNewUserDb(dbItem.id)}
                                                        className="accent-green-700"
                                                    />
                                                    {dbItem.title}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
                                        <div>
                                            <div className="text-[11px] font-bold uppercase text-stone-500 mb-2">Automatic Coding Validation settings</div>
                                            <label className="block text-xs text-stone-600">
                                                Blind validation sample size
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="mt-1 w-40 border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none disabled:bg-stone-100 disabled:text-stone-400"
                                                    value={newUser.automaticValidationSampleSize}
                                                    onChange={e => setNewUser({ ...newUser, automaticValidationSampleSize: e.target.value })}
                                                    disabled={!(newUser.allowedDatabases || []).includes('automatic_coding_validation')}
                                                />
                                            </label>
                                            {!(newUser.allowedDatabases || []).includes('automatic_coding_validation') && (
                                                <div className="mt-1 text-[11px] text-stone-400">Enable access to Automatic Coding Validation to configure its sample.</div>
                                            )}
                                        </div>
                                        <div className="flex gap-2 items-center justify-start md:justify-end flex-wrap">
                                            <select
                                                className="border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                                value={newUser.role}
                                                onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                                            >
                                                <option value="coder">Coder</option>
                                                <option value="admin">Admin</option>
                                            </select>
                                            <button
                                                type="submit"
                                                className="bg-stone-800 text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-black transition-colors"
                                            >
                                                <UserPlus size={15} className="inline mr-2" />Create User
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            {permissionsMsg && (
                                <div className={`mb-3 p-2 rounded-sm text-xs ${permissionsMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                                    {permissionsMsg.text}
                                </div>
                            )}
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-left text-xs uppercase text-stone-500 border-b border-stone-200">
                                        <tr>
                                            <th className="py-2 pr-3">User</th>
                                            <th className="py-2 pr-3">Role</th>
                                            <th className="py-2">Visible databases</th>
                                            <th className="py-2 pr-3">Password reset</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100">
                                        {users.map(u => (
                                            <tr key={`hub-user-${u.id}`}>
                                                <td className="py-2 pr-3">
                                                    <div className="font-medium text-stone-800">{[u.name, u.surname].filter(Boolean).join(' ') || u.fullName || '-'}</div>
                                                    <div className="text-xs text-stone-400">{u.username}{u.id === user.id ? ' (you)' : ''}</div>
                                                </td>
                                                <td className="py-2 pr-3">
                                                    <select
                                                        className={`border rounded-sm p-1.5 text-xs font-bold uppercase ${u.role === 'admin' ? 'border-purple-200 bg-purple-50 text-purple-700' : 'border-blue-200 bg-blue-50 text-blue-700'} disabled:opacity-60`}
                                                        value={u.role}
                                                        onChange={(e) => handleChangeUserRole(u, e.target.value)}
                                                        disabled={u.id === user.id || pendingUserAction === `role-${u.id}`}
                                                        title={u.id === user.id ? 'You cannot change your own administrator role here.' : 'Change user role'}
                                                    >
                                                        <option value="coder">Coder</option>
                                                        <option value="admin">Admin</option>
                                                    </select>
                                                </td>
                                                <td className="py-2">
                                                    <div className="flex flex-wrap gap-2">
                                                        {platformDatabases.map(dbItem => {
                                                            const checked = (u.allowedDatabases || []).includes(dbItem.id);
                                                            return (
                                                                <label key={`hub-perm-${u.id}-${dbItem.id}`} className="inline-flex items-center gap-1 text-xs text-stone-700 bg-stone-100 px-2 py-1 rounded">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={checked}
                                                                        onChange={() => handleToggleUserDbPermission(u, dbItem.id)}
                                                                        className="accent-green-700"
                                                                    />
                                                                    {dbItem.title}
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                                <td className="py-2 pr-3">
                                                    <div className="flex items-center gap-2 min-w-[270px]">
                                                        <input
                                                            type="password"
                                                            className="w-44 border border-stone-300 rounded-sm p-1.5 text-xs"
                                                            value={userPasswordDrafts[u.id] || ''}
                                                            onChange={(e) => setUserPasswordDrafts(prev => ({ ...prev, [u.id]: e.target.value }))}
                                                            placeholder="New password"
                                                            autoComplete="new-password"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => handleResetUserPassword(u)}
                                                            disabled={pendingUserAction === `password-${u.id}`}
                                                            className="px-2 py-1.5 rounded-sm bg-stone-800 text-white text-[11px] hover:bg-black disabled:opacity-50"
                                                        >
                                                            Reset
                                                        </button>
                                                    </div>
                                                    <div className="mt-1 text-[10px] text-stone-400">10+ chars, upper/lowercase, number and symbol.</div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-6 border border-stone-200 rounded-sm">
                                <div className="p-3 bg-stone-50 border-b border-stone-200">
                                    <h4 className="text-xs font-bold uppercase text-stone-700">Database-specific settings</h4>
                                    <p className="mt-1 text-xs text-stone-500">Only databases with per-user configuration appear here.</p>
                                </div>
                                <div className="p-4">
                                    <div className="text-sm font-semibold text-stone-800">Automatic Coding Validation</div>
                                    <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                                        {users.map(u => {
                                            const hasAccess = u.role === 'admin' || (u.allowedDatabases || []).includes('automatic_coding_validation');
                                            return (
                                                <div key={`sample-setting-${u.id}`} className="flex items-center justify-between gap-3 p-3 border border-stone-200 rounded-sm">
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-medium text-stone-800 truncate">{[u.name, u.surname].filter(Boolean).join(' ') || u.username}</div>
                                                        <div className="text-xs text-stone-400">{u.username}</div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            className="w-24 border border-stone-300 rounded-sm p-1.5 text-xs disabled:bg-stone-100 disabled:text-stone-400"
                                                            value={userSampleSizeDrafts[u.id] ?? u.automaticValidationSampleSize ?? 200}
                                                            onChange={(e) => setUserSampleSizeDrafts(prev => ({ ...prev, [u.id]: e.target.value }))}
                                                            disabled={!hasAccess}
                                                            title={hasAccess ? 'Blind validation sample size' : 'This user has no access to Automatic Coding Validation'}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => handleSaveUserSampleSize(u)}
                                                            disabled={!hasAccess || pendingUserAction === `sample-${u.id}`}
                                                            className="px-2 py-1 rounded-sm bg-stone-800 text-white text-[11px] hover:bg-black disabled:opacity-40"
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 pt-6 border-t border-stone-200">
                                <AuditLogView embedded />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const DataView = ({ entities, members, fetchData }) => {
    const [importMode, setImportMode] = useState('append'); // 'replace' or 'append'
    const [isProcessing, setIsProcessing] = useState(false);
    const [msg, setMsg] = useState(null);
    const [dbEngine, setDbEngine] = useState('sqlite');
    const [isArchiveDownloading, setIsArchiveDownloading] = useState(false);
    useEffect(() => {
        const fetchStorageStatus = async () => {
            try {
                const res = await authFetch(`${API_URL}/admin/storage-status`);
                if (!res.ok) return;
                const data = await res.json();
                if (data?.engine) setDbEngine(data.engine);
            } catch (e) {
                // keep default ui text
            }
        };
        fetchStorageStatus();
    }, []);

    // Función para mapear columnas del Excel original a las claves de la App
    const mapEntityRow = (row, index) => {
        return {
            id: `imported-ent-${Date.now()}-${index}`,
            country: row['Country'] || row['country'] || '',
            officialName: row['Official name'] || row['Official Name'] || row['officialName'] || 'Unknown Entity',
            englishName: row['English name'] || row['English Name'] || row['englishName'] || '',
            year: row['Creation year'] || row['year'] || '',
            creationNorm: row['Creation norm'] || row['Creation Norm'] || row['creationNorm'] || '',
            functions: row['Current functions'] || row['functions'] || '',
            compositionRule: row['Composition rule'] || row['compositionRule'] || '',
            type: row['Type of entity'] || row['type'] || '',
            website: row['Website'] || row['website'] || '',
            isExtinct: !!(row['Extinct'] || row['extinct'] || row['isExtinct']),
            extinctionYear: row['Extinction year'] || row['Extinction Year'] || row['extinctionYear'] || '',
            succeededByEntityId: row['Succeeded by entity ID'] || row['succeededByEntityId'] || '',
            otherInfo: row['Other information'] || row['otherInfo'] || '',
            status: 'draft', // Por defecto al importar
            hasDocs: false
        };
    };

    const mapMemberRow = (row, entitiesList, index) => {
        const parentEntity = entitiesList.find(e => 
            e.officialName === (row['Official name'] || row['officialName']) && 
            e.country === (row['Country'] || row['country'])
        );

        return {
            id: `imported-mem-${Date.now()}-${index}`,
            entityId: parentEntity ? parentEntity.id : 'orphan', 
            name: row['Member'] || row['Name'] || row['name'] || 'Unknown',
            role: row['Role'] || row['role'] || '',
            gender: row['Gender'] || row['gender'] || '',
            background: row['Background'] || row['background'] || '',
            type: row["Member's type of organization"] || row['Type'] || row['type'] || 'Expert'
        };
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if(!file) return;

        if (importMode === 'replace') {
            const confirmed = window.confirm('Replace mode will delete all current entities and members before importing. Continue?');
            if (!confirmed) {
                e.target.value = '';
                return;
            }
        }

        setIsProcessing(true);
        setMsg(null);

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });

                // 1. Parse Entities
                const wsEntities = wb.Sheets['Entities'] || wb.Sheets[wb.SheetNames[0]]; 
                const jsonEntities = XLSX.utils.sheet_to_json(wsEntities);
                const mappedEntities = jsonEntities.map((row, i) => mapEntityRow(row, i));

                // 2. Parse Members
                let mappedMembers = [];
                const wsMembers = wb.Sheets['Members'];
                if (wsMembers) {
                    const jsonMembers = XLSX.utils.sheet_to_json(wsMembers);
                    mappedMembers = jsonMembers.map((row, i) => mapMemberRow(row, mappedEntities, i)).filter(m => m.entityId !== 'orphan');
                }

                // Upload to Server
                console.log(`Uploading ${mappedEntities.length} entities...`);
                const resEnt = await authFetch(`${API_URL}/entities/bulk?mode=${importMode}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(mappedEntities)
                });

                if (!resEnt.ok) {
                    const errText = await resEnt.text();
                    let errMsg = 'Unknown server error';
                    try {
                        const errJson = JSON.parse(errText);
                        errMsg = errJson.details ? JSON.stringify(errJson.details) : (errJson.error || errText);
                    } catch (e) { errMsg = errText; }
                    throw new Error(`Entities upload failed: ${errMsg}`);
                }

                if (mappedMembers.length > 0) {
                    console.log(`Uploading ${mappedMembers.length} members...`);
                    const resMem = await authFetch(`${API_URL}/members/bulk`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(mappedMembers)
                    });
                    
                    if (!resMem.ok) {
                        const errText = await resMem.text();
                        let errMsg = 'Unknown server error';
                        try {
                            const errJson = JSON.parse(errText);
                            errMsg = errJson.details ? JSON.stringify(errJson.details) : (errJson.error || errText);
                        } catch (e) { errMsg = errText; }
                        throw new Error(`Members upload failed: ${errMsg}`);
                    }
                }
                
                await fetchData(); // Recargar datos desde backend

                setMsg({ type: 'success', text: `Database updated! Imported ${mappedEntities.length} entities and ${mappedMembers.length} members.`});

            } catch (error) {
                console.error(error);
                setMsg({ type: 'error', text: `Error: ${error.message}` });
            } finally {
                setIsProcessing(false);
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleExportXLSX = () => {
        const exportEntities = entities.map(({id, hasDocs, status, ...rest}) => ({
            ...rest,
            'System Status': status
        }));
        
        const exportMembers = members.map(m => {
            const ent = entities.find(e => e.id === m.entityId);
            return {
                Country: ent ? ent.country : '',
                'Official name': ent ? ent.officialName : '',
                Member: m.name,
                Role: m.role,
                Gender: m.gender,
                Background: m.background,
                "Member's type of organization": m.type
            };
        });

        const wb = XLSX.utils.book_new();
        const ws1 = XLSX.utils.json_to_sheet(exportEntities);
        const ws2 = XLSX.utils.json_to_sheet(exportMembers);

        XLSX.utils.book_append_sheet(wb, ws1, "Entities");
        XLSX.utils.book_append_sheet(wb, ws2, "Members");

        XLSX.writeFile(wb, "AI_Policy_Database.xlsx");
    };

    const handleExportCSV = (type) => { // 'entities' or 'members'
        let dataToExport = [];
        let filename = `AIPAS_${type}_export.csv`;

        if (type === 'entities') {
             dataToExport = entities.map(({id, hasDocs, status, ...rest}) => ({
                ...rest,
                'System Status': status
            }));
        } else if (type === 'members') {
             dataToExport = members.map(m => {
                const ent = entities.find(e => e.id === m.entityId);
                return {
                    Country: ent ? ent.country : '',
                    'Official name': ent ? ent.officialName : '',
                    Member: m.name,
                    Role: m.role,
                    Gender: m.gender,
                    Background: m.background,
                    "Member's type of organization": m.type
                };
            });
        }

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const csv = XLSX.utils.sheet_to_csv(ws);
        
        // Manual Blob download
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        if (link.download !== undefined) { 
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleDownloadSanitizedDB = async () => {
        if(!window.confirm("Download full database copy (Sanitized: NO users/passwords)?")) return;
        
        try {
            const res = await authFetch(`${API_URL}/admin/export-db`);
            if(!res.ok) throw new Error("Download failed");
            
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const disposition = res.headers.get('content-disposition') || '';
            const contentType = (res.headers.get('content-type') || '').toLowerCase();
            const isJson = contentType.includes('application/json') || disposition.toLowerCase().includes('.json');
            a.download = `AIPAS_DB_SANITIZED_${new Date().toISOString().split('T')[0]}${isJson ? '.json' : '.sqlite'}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (e) {
            console.error(e);
            alert("Error downloading database: " + e.message);
        }
    };

    const handleDownloadArchive = async () => {
        if (!window.confirm('Download the database and all uploaded documents?')) return;
        setIsArchiveDownloading(true);
        try {
            const res = await authFetch(`${API_URL}/admin/export-archive`);
            if (!res.ok) {
                const error = await res.json().catch(() => ({}));
                throw new Error(error.error || 'Download failed');
            }
            const url = window.URL.createObjectURL(await res.blob());
            const a = document.createElement('a');
            a.href = url;
            a.download = `AIPAS_FULL_${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (e) {
            console.error(e);
            alert(`Error downloading archive: ${e.message}`);
        } finally {
            setIsArchiveDownloading(false);
        }
    };


    return (
        <div className={`p-8 ${BRAND.light} min-h-screen`}>
            <h2 className="text-3xl font-bold text-stone-900 font-sans mb-6 border-b border-stone-300 pb-4">Data Management</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* IMPORT SECTION */}
                <div className="bg-white p-8 rounded-sm shadow-sm border border-stone-200">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-green-100 text-green-800 rounded-full">
                            <Upload size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-stone-800">Import Data</h3>
                            <p className="text-sm text-stone-500">Load from .xlsx or .csv</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex gap-4 mb-4 text-sm">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                    type="radio" 
                                    name="mode" 
                                    checked={importMode === 'replace'} 
                                    onChange={() => setImportMode('replace')}
                                    className="accent-green-800" 
                                />
                                <span>Replace Database</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                    type="radio" 
                                    name="mode" 
                                    checked={importMode === 'append'} 
                                    onChange={() => setImportMode('append')}
                                    className="accent-green-800" 
                                />
                                <span>Append to Existing</span>
                            </label>
                        </div>

                        <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:bg-stone-50 transition-colors relative">
                            <input 
                                type="file" 
                                accept=".xlsx, .xls, .csv" 
                                onChange={handleFileUpload}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                            <div className="text-stone-400 flex flex-col items-center">
                                {isProcessing ? <RefreshCw className="animate-spin mb-2" /> : <FileSpreadsheet className="mb-2" size={32} />}
                                <span className="font-medium text-stone-600">Click to upload Excel File</span>
                                <span className="text-xs mt-1">Supports sheets "Entities" and "Members"</span>
                            </div>
                        </div>

                        {msg && (
                            <div className={`p-3 rounded-sm text-sm flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {msg.type === 'success' ? <CheckCircle size={16}/> : <AlertTriangle size={16}/>}
                                {msg.text}
                            </div>
                        )}
                        
                        <div className="bg-blue-50 p-4 rounded-sm text-xs text-blue-800">
                            <strong>Note:</strong> To link Members to Entities automatically, ensures the Excel has "Official name" and "Country" columns in both sheets exactly matching.
                        </div>
                    </div>
                </div>

                {/* EXPORT SECTION */}
                <div className="bg-white p-8 rounded-sm shadow-sm border border-stone-200">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-stone-100 text-stone-800 rounded-full">
                            <HardDrive size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-stone-800">Export / Save</h3>
                            <p className="text-sm text-stone-500">Download current database ({dbEngine.toUpperCase()})</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <p className="text-stone-600 text-sm">
                            {dbEngine === 'postgres'
                                ? 'The database is running on PostgreSQL. You can export it to Excel/CSV or download a sanitized JSON backup.'
                                : 'The database is stored in an SQLite file. You can export it to Excel for sharing or reporting.'}
                        </p>

                        <div className="grid grid-cols-1 gap-3 mt-6">
                            <button 
                                onClick={handleExportXLSX}
                                className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group"
                            >
                                <div className="flex items-center gap-3">
                                    <FileSpreadsheet className="text-green-600" />
                                    <div className="text-left">
                                        <div className="font-bold text-stone-800 group-hover:text-green-800">Export to Excel (.xlsx)</div>
                                        <div className="text-xs text-stone-500">Full dataset in one file</div>
                                    </div>
                                </div>
                                <Download size={18} />
                            </button>

                             <div className="flex gap-3">
                                <button 
                                    onClick={() => handleExportCSV('entities')}
                                    className="flex-1 flex items-center justify-center gap-2 p-3 border border-stone-300 rounded-sm text-sm font-medium hover:bg-stone-50 transition-colors"
                                >
                                    <FileIcon size={16} /> Export Entities (CSV)
                                </button>
                                <button 
                                    onClick={() => handleExportCSV('members')}
                                    className="flex-1 flex items-center justify-center gap-2 p-3 border border-stone-300 rounded-sm text-sm font-medium hover:bg-stone-50 transition-colors"
                                >
                                    <Users size={16} /> Export Members (CSV)
                                </button>
                            </div>

                            <button 
                                onClick={handleDownloadSanitizedDB}
                                className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-blue-600 hover:bg-blue-50 hover:text-blue-800 transition-all group mt-2"
                            >
                                <div className="flex items-center gap-3">
                                    <Database className="text-blue-600" />
                                    <div className="text-left">
                                        <div className="font-bold text-stone-800 group-hover:text-blue-800">Full Database Backup ({dbEngine === 'postgres' ? '.json' : '.sqlite'})</div>
                                        <div className="text-xs text-stone-500">Excludes credentials & logs ({dbEngine.toUpperCase()})</div>
                                    </div>
                                </div>
                                <Download size={18} />
                            </button>

                            <button
                                onClick={handleDownloadArchive}
                                disabled={isArchiveDownloading}
                                className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-blue-600 hover:bg-blue-50 hover:text-blue-800 transition-all group disabled:opacity-50 disabled:cursor-wait"
                            >
                                <div className="flex items-center gap-3">
                                    <Database className="text-blue-600" />
                                    <div className="text-left">
                                        <div className="font-bold text-stone-800 group-hover:text-blue-800">Database + Documents (.zip)</div>
                                        <div className="text-xs text-stone-500">JSON and uploaded files grouped by country and entity</div>
                                    </div>
                                </div>
                                {isArchiveDownloading ? <RefreshCw size={18} className="animate-spin" /> : <Download size={18} />}
                            </button>
                        </div>

                        <div className="mt-8 pt-6 border-t border-stone-100">
                            <h4 className="text-sm font-bold text-stone-800 mb-2">Database Stats</h4>
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div className="bg-stone-50 p-2 rounded">
                                    <div className={`text-xl font-bold ${BRAND.accent}`}>{entities.length}</div>
                                    <div className="text-xs text-stone-500">Entities</div>
                                </div>
                                <div className="bg-stone-50 p-2 rounded">
                                    <div className={`text-xl font-bold ${BRAND.accent}`}>{members.length}</div>
                                    <div className="text-xs text-stone-500">Members</div>
                                </div>
                                <div className="bg-stone-50 p-2 rounded">
                                    <div className={`text-xl font-bold ${BRAND.accent}`}>{new Set(entities.map(e=>e.country)).size}</div>
                                    <div className="text-xs text-stone-500">Countries</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};


// 1. DASHBOARD
const Dashboard = ({ entities, members, documents, target, setTarget, currentUser }) => {
  const [isEditingTarget, setIsEditingTarget] = useState(false);
  const [tempTarget, setTempTarget] = useState(target);
    const [selectedCountry, setSelectedCountry] = useState('Spain');

  // Map Filter States
  const [mapEntityFilter, setMapEntityFilter] = useState('All');
  const [mapGenderFilter, setMapGenderFilter] = useState('Female');
    const [mapInterestFilter, setMapInterestFilter] = useState('Academia & Research');

  // Verified entities filter
  const [verifiedFilter, setVerifiedFilter] = useState(false);

  // Helper functions (defined outside useMemo for reuse)
  const normalizeEntityType = (rawType) => {
    if (!rawType) return 'Other';
    const t = rawType.trim();
    const lowT = t.toLowerCase();

    const canonicalType = ENTITY_TYPE_OPTIONS.find(type => type.toLowerCase() === lowT);
    if (canonicalType) return canonicalType;

    if (lowT.includes('ai-specific') || lowT.includes('strategic council')) return 'AI-specific advisory body';
    if (lowT.includes('advisory') || lowT.includes('deliberative') || lowT.includes('ethics') || lowT.includes('data governance')) return 'Digital, data, ethics or societal advisory/deliberative body';
    if (lowT.includes('coordination') || lowT.includes('interministerial') || lowT.includes('working group')) return 'Governmental or interministerial coordination body';
    if (lowT.includes('data protection')) return 'Data protection supervisory authority';
    if ((lowT.includes('regulatory') || lowT.includes('supervisory') || lowT.includes('supervision')) && !lowT.includes('data protection')) return 'AI/digital regulatory or supervisory authority excluding data protection';
    if (lowT.includes('standard') || lowT.includes('standardization') || lowT.includes('conformity') || lowT.includes('technical')) return 'Technical standardization, conformity or regulatory support body';
    if (lowT.includes('public infrastructure') || lowT.includes('digital government') || lowT.includes('operational agency') || lowT.includes('office')) return 'Digital government and public infrastructure operational agency';
    if (lowT.includes('cybersecurity') || lowT.includes('cyber security')) return 'National cybersecurity authority, agency or centre';

    return t.length > 30 ? t.substring(0, 30) + '...' : t;
  };

  const normalizeType = (rawType) => {
    if (!rawType) return 'Unknown';
    const t = rawType.toLowerCase().trim();

    if (t.includes('academic') || t.includes('university') || t.includes('research') || t.includes('institute')) return 'Academia & Research';
    if (t.includes('business') || t.includes('company') || t.includes('industry') || t.includes('trade') || t.includes('corporate')) return 'Business & Industry';
    if (t.includes('government') || t.includes('public authority') || t.includes('ministry') || t.includes('state')) return 'Public Sector';
    if ((t.includes('non') && t.includes('governmental')) || t.includes('ngo') || t.includes('civil') || t.includes('society') || t.includes('consumer')) return 'Civil Society';
    if (t.includes('expert') || t.includes('individual')) return 'Independent Experts';

    return 'Other';
  };

  const normalizeGender = (rawGender) => {
    if (!rawGender) return 'Unknown';
    const g = rawGender.toLowerCase().trim();
    if (g === 'm' || g === 'male') return 'Male';
    if (g === 'f' || g === 'female') return 'Female';
    if (g === 'na' || g === 'n/a' || g === '-') return 'N/A';
    return 'Unknown';
  };

  const verifiedEntityIds = useMemo(
    () => new Set(entities.filter(e => e.status === 'verified').map(e => String(e.id))),
    [entities]
  );
  const displayEntities = verifiedFilter ? entities.filter(e => e.status === 'verified') : entities;
  const displayMembers = verifiedFilter ? members.filter(m => verifiedEntityIds.has(String(m.entityId))) : members;

  const stats = useMemo(() => {
    const countriesWithData = new Set(displayEntities.map(e => e.country)).size;
    const verifiedEntities = displayEntities.filter(e => e.status === 'verified').length;
    const restingEntities = displayEntities.filter(e => e.status !== 'verified').length;
    const totalMembers = displayMembers.length;
    const verifiedDocs = documents.length;

    // Global Progress: Percentage of entities with "current functions" info
    const entitiesWithFunctions = displayEntities.filter(e => e.functions && e.functions.trim().length > 0).length;
    const totalEntities = displayEntities.length > 0 ? displayEntities.length : 1;
    const completionRate = Math.round((entitiesWithFunctions / totalEntities) * 100);

    // Verified Rate
    const verifiedRate = Math.round((verifiedEntities / totalEntities) * 100);

    // Docs Rate (Entities with at least one document)
    // Assuming 'hasDocs' property exists on entities based on handleAddDocument logic
    const entitiesWithDocs = displayEntities.filter(e => e.hasDocs).length;
    const docsRate = Math.round((entitiesWithDocs / totalEntities) * 100);

    const entitiesWithMembers = new Set(displayMembers.map(m => m.entityId)).size;

    return {
        countriesWithData,
        verifiedEntities,
        restingEntities,
        totalMembers,
        verifiedDocs,
        completionRate,
        totalEntities,
        verifiedRate,
        docsRate,
        entitiesWithDocs,
        entitiesWithMembers
    };
  }, [displayEntities, displayMembers, documents]);

  const memberStats = useMemo(() => {
     const genderCount = {};
     const typeCount = {};
     
     // 1. Countries that have at least one member (List & Count)
     const countriesWithMembersList = [...new Set(
        displayMembers
            .map(m => displayEntities.find(e => String(e.id) === String(m.entityId)))
            .filter(e => e && e.country)
            .map(e => e.country)
     )].sort();

     const countriesWithMembers = countriesWithMembersList.length;

     // 2. Global Stats (Members & Entities)
     displayMembers.forEach(m => {
        // Gender
        const g = normalizeGender(m.gender);
        genderCount[g] = (genderCount[g] || 0) + 1;

        // Type (Members)
        const t = normalizeType(m.type);
        typeCount[t] = (typeCount[t] || 0) + 1;
     });

     // Global Stats (Entities)
     const entityTypeCount = {};
     displayEntities.forEach(e => {
        const t = normalizeEntityType(e.type);
        entityTypeCount[t] = (entityTypeCount[t] || 0) + 1;
     });

     const toData = (obj) => Object.entries(obj)
        .map(([name, value]) => ({ name, value }))
        .sort((a,b) => b.value - a.value); // Sort descending

     // 3. Country-Specific Stats (Gender)
     let genderByCountry = [];
      if (selectedCountry) {
        const countryMembers = displayMembers.filter(m => {
             const ent = displayEntities.find(e => String(e.id) === String(m.entityId));
                 return ent && ent.country === selectedCountry;
        });
        const gCount = {};
        countryMembers.forEach(m => {
            const g = normalizeGender(m.gender);
            gCount[g] = (gCount[g] || 0) + 1;
        });
        genderByCountry = toData(gCount);
     }

     // 4. Country-Specific Stats (Interest Groups)
     let interestByCountry = [];
      if (selectedCountry) {
        const countryMembers = displayMembers.filter(m => {
             const ent = displayEntities.find(e => String(e.id) === String(m.entityId));
                 return ent && ent.country === selectedCountry;
        });
        const iCount = {};
        countryMembers.forEach(m => {
            const t = normalizeType(m.type);
            iCount[t] = (iCount[t] || 0) + 1;
        });
        interestByCountry = toData(iCount);
     }

     // 5. Country-Specific Stats (Entity Types)
     let entityTypeByCountry = [];
      if (selectedCountry) {
          const countryEntities = displayEntities.filter(e => e.country === selectedCountry);
        const etCount = {};
        countryEntities.forEach(e => {
            const t = normalizeEntityType(e.type);
            etCount[t] = (etCount[t] || 0) + 1;
        });
        entityTypeByCountry = toData(etCount);
     }

     // 6. MAP DATA PREPARATION
     // A. Entities Map Data
     const entityMapData = {};
     displayEntities.forEach(e => {
        const type = normalizeEntityType(e.type);
        if (mapEntityFilter === 'All' || type === mapEntityFilter) {
            entityMapData[e.country] = (entityMapData[e.country] || 0) + 1;
        }
     });

     // B. Gender Map Data (% of selected gender)
     const genderMapData = {};
     if (mapGenderFilter) {
         // Get member counts per country first
         const membersByCountry = {};
         const genderCountsByCountry = {}; // { Country: { Female: 10, Male: 5 ... } }

         displayMembers.forEach(m => {
            const ent = displayEntities.find(e => String(e.id) === String(m.entityId));
            if (ent && ent.country) {
                const c = ent.country;
                membersByCountry[c] = (membersByCountry[c] || 0) + 1;

                if (!genderCountsByCountry[c]) genderCountsByCountry[c] = {};
                const g = normalizeGender(m.gender);
                genderCountsByCountry[c][g] = (genderCountsByCountry[c][g] || 0) + 1;
            }
         });

         Object.keys(membersByCountry).forEach(c => {
             const total = membersByCountry[c];
             const count = (genderCountsByCountry[c] && genderCountsByCountry[c][mapGenderFilter]) || 0;
             genderMapData[c] = parseFloat(((count / total) * 100).toFixed(1));
         });
     }

     // C. Interest Map Data (% of selected interest group)
     const interestMapData = {};
     if (mapInterestFilter) {
         // Re-use logic or similar
         const membersByCountry = {};
         const typeCountsByCountry = {};

         displayMembers.forEach(m => {
            const ent = displayEntities.find(e => String(e.id) === String(m.entityId));
            if (ent && ent.country) {
                const c = ent.country;
                membersByCountry[c] = (membersByCountry[c] || 0) + 1;

                if (!typeCountsByCountry[c]) typeCountsByCountry[c] = {};
                const t = normalizeType(m.type);
                typeCountsByCountry[c][t] = (typeCountsByCountry[c][t] || 0) + 1;
            }
         });

         Object.keys(membersByCountry).forEach(c => {
             const total = membersByCountry[c];
             const count = (typeCountsByCountry[c] && typeCountsByCountry[c][mapInterestFilter]) || 0;
             interestMapData[c] = parseFloat(((count / total) * 100).toFixed(1));
         });
     }

     return {
        gender: toData(genderCount),
        type: toData(typeCount),
        entityType: toData(entityTypeCount),
        countriesWithMembers,
        countriesWithMembersList,
        genderByCountry,
        interestByCountry,
        entityTypeByCountry,
        entityMapData,
        genderMapData,
        interestMapData,
        allEntityTypes: Object.keys(entityTypeCount)
     };
      }, [displayMembers, displayEntities, selectedCountry, mapEntityFilter, mapGenderFilter, mapInterestFilter]);

  const COLORS = ['#881337', '#b45309', '#047857', '#1d4ed8', '#4338ca', '#7e22ce', '#be123c'];
  
  const GENDER_COLORS_MAP = {
    'Female': '#7e22ce',  // Purple
    'Male': '#eab308',    // Yellow
    'N/A': '#57534e',     // Dark Grey
    'Unknown': '#d6d3d1'  // Light Grey
  };
  const getGenderColor = (name) => GENDER_COLORS_MAP[name] || '#d6d3d1';

    // Auth Context (Dashboard)
    const user = currentUser || null;
  const isAdmin = user && user.role === 'admin';
    useEffect(() => {
        const countryOptions = memberStats.countriesWithMembersList;
        if (!countryOptions || countryOptions.length === 0) return;

        if (!countryOptions.includes(selectedCountry)) {
            if (countryOptions.includes('Spain')) {
                setSelectedCountry('Spain');
            } else {
                setSelectedCountry(countryOptions[0]);
            }
        }
    }, [memberStats.countriesWithMembersList, selectedCountry]);

  const handleSaveTarget = async () => {
    const newTarget = Number(tempTarget);
    setTarget(newTarget);
    setIsEditingTarget(false);
    await fetch(`${API_URL}/config/target`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: newTarget })
    });
  };

  // ====== ADVANCED ANALYTICS HELPER FUNCTIONS ======

  const isAISpecificEntity = (entity) => {
    if (!entity) return false;
    const name = String(entity.englishName || entity.officialName || '').toLowerCase();
    const type = String(entity.type || '').toLowerCase();

    const hasAI = /\bai\b|\bartificial\s+intelligence\b/i.test(name) || /\bai\b|\bartificial\s+intelligence\b/i.test(type);
    return hasAI;
  };

  const normalizeMemberSector = (memberType) => {
    if (!memberType) return 'Other';
    const t = String(memberType).toLowerCase().trim();
    if (t.includes('public') || t.includes('government') || t.includes('ministry') || t.includes('authority')) return 'Public Authority';
    if (t.includes('industry') || t.includes('business') || t.includes('corporate') || t.includes('company') || t.includes('trade')) return 'Industry';
    if (t.includes('academic') || t.includes('university') || t.includes('research') || t.includes('institute')) return 'Academia';
    if (t.includes('expert') || t.includes('individual')) return 'Independent Expert';
    if (t.includes('civil') || t.includes('ngo') || t.includes('society') || t.includes('consumer')) return 'Civil Society';
    return 'Other';
  };

  const normalizeDateToEra = (year) => {
    const y = parseInt(year, 10);
    if (y < 2010) return 'Pre-2010';
    if (y < 2018) return '2010–2017';
    if (y < 2022) return '2018–2021';
    return '2022–2026';
  };

  // Analytics data calculations
  const analyticsData = useMemo(() => {
    const C = {
      blue: '#2E75B6', darkBlue: '#1F3864', light: '#BDD7EE', xlight: '#EBF3FB',
      orange: '#E8563A', amber: '#F5A623', green: '#3A8C5C', purple: '#7B5EA7',
      teal: '#1A8C7A', grey: '#8A8A8A', red: '#C0392B'
    };

    // 1. Entity Types
    const entityTypeMap = {};
    displayEntities.forEach(e => {
      const type = normalizeEntityType(e.type) || 'Other';
      entityTypeMap[type] = (entityTypeMap[type] || 0) + 1;
    });
    const entityTypeData = Object.entries(entityTypeMap)
      .map(([label, value]) => ({ name: label, value }))
      .sort((a, b) => b.value - a.value);

    // 2. Creation Waves (by year)
    const wavesByYear = {};
    const wavesByYearAI = {};
    displayEntities.forEach(e => {
      const year = e.year || '2026';
      if (!wavesByYear[year]) wavesByYear[year] = 0;
      if (!wavesByYearAI[year]) wavesByYearAI[year] = 0;
      wavesByYear[year]++;
      if (isAISpecificEntity(e)) wavesByYearAI[year]++;
    });
    const years = Object.keys(wavesByYear).sort();
    const waveData = years.map(y => ({
      year: y,
      'General digital': wavesByYear[y] - (wavesByYearAI[y] || 0),
      'AI-Specific': wavesByYearAI[y] || 0
    }));

    // 3. Sector composition by member type
    const sectorCounts = {};
    let memberWithSector = 0;
    displayMembers.forEach(m => {
      if (m.type) {
        const sector = normalizeMemberSector(m.type);
        sectorCounts[sector] = (sectorCounts[sector] || 0) + 1;
        memberWithSector++;
      }
    });
    const totalMembers = displayMembers.length || 1;
    const sectorData = Object.entries(sectorCounts)
      .map(([name, count]) => ({
        name,
        percentage: parseFloat(((count / totalMembers) * 100).toFixed(1))
      }))
      .sort((a, b) => b.percentage - a.percentage);

    // 4. Gender by Entity Type
    const genderByEntityType = {};
    displayEntities.forEach(e => {
      const eType = normalizeEntityType(e.type);
      if (!genderByEntityType[eType]) genderByEntityType[eType] = { male: 0, female: 0, total: 0 };

      const entityMembers = displayMembers.filter(m => String(m.entityId) === String(e.id));
      entityMembers.forEach(m => {
        const g = String(m.gender || '').toLowerCase().trim();
        genderByEntityType[eType].total++;
        if (g === 'f' || g === 'female') genderByEntityType[eType].female++;
        else if (g === 'm' || g === 'male') genderByEntityType[eType].male++;
      });
    });
    const genderTypeData = Object.entries(genderByEntityType)
      .filter(([_, data]) => data.total > 0)
      .map(([type, data]) => ({
        type,
        'Female %': parseFloat(((data.female / data.total) * 100).toFixed(1))
      }))
      .sort((a, b) => b['Female %'] - a['Female %']);

    // 5. Gender histogram by entity
    const entityGenderPercentages = [];
    displayEntities.forEach(e => {
      const entityMembers = displayMembers.filter(m => String(m.entityId) === String(e.id));
      if (entityMembers.length >= 3) {
        const femaleCount = entityMembers.filter(m => {
          const g = String(m.gender || '').toLowerCase().trim();
          return g === 'f' || g === 'female';
        }).length;
        const percentage = parseFloat(((femaleCount / entityMembers.length) * 100).toFixed(1));
        entityGenderPercentages.push(percentage);
      }
    });
    const genderHistBins = ['0–10%', '10–20%', '20–30%', '30–40%', '40–50%', '50–60%', '60–70%', '70–80%', '80–90%', '90–100%'];
    const genderHistCounts = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    entityGenderPercentages.forEach(pct => {
      const binIndex = Math.floor(pct / 10);
      if (binIndex <= 9) genderHistCounts[binIndex]++;
    });
    const genderHistData = genderHistBins.map((label, idx) => ({
      range: label,
      count: genderHistCounts[idx]
    }));

    // 6. Gender by country
    const genderByCountry = {};
    displayEntities.forEach(e => {
      if (!genderByCountry[e.country]) genderByCountry[e.country] = { female: 0, male: 0, total: 0 };
      const entityMembers = displayMembers.filter(m => String(m.entityId) === String(e.id));
      entityMembers.forEach(m => {
        const g = String(m.gender || '').toLowerCase().trim();
        genderByCountry[e.country].total++;
        if (g === 'f' || g === 'female') genderByCountry[e.country].female++;
        else if (g === 'm' || g === 'male') genderByCountry[e.country].male++;
      });
    });
    const genderCountryData = Object.entries(genderByCountry)
      .filter(([_, data]) => data.total > 0)
      .map(([country, data]) => ({
        country,
        percentage: parseFloat(((data.female / data.total) * 100).toFixed(1))
      }))
      .sort((a, b) => b.percentage - a.percentage);

    // 7. Entity types by era
    const typesByEra = {};
    displayEntities.forEach(e => {
      const era = normalizeDateToEra(e.year);
      const type = normalizeEntityType(e.type);
      const key = `${era}|${type}`;
      typesByEra[key] = (typesByEra[key] || 0) + 1;
    });
    const eras = ['Pre-2010', '2010–2017', '2018–2021', '2022–2026'];
    const eraTypeMap = {};
    eras.forEach(era => {
      eraTypeMap[era] = {};
      displayEntities.forEach(e => {
        if (normalizeDateToEra(e.year) === era) {
          const type = normalizeEntityType(e.type);
          eraTypeMap[era][type] = (eraTypeMap[era][type] || 0) + 1;
        }
      });
    });
    const eraTypeData = eras.map(era => {
      const eraData = { era };
      const types = Object.keys(eraTypeMap[era]);
      types.forEach(type => {
        eraData[type] = eraTypeMap[era][type] || 0;
      });
      return eraData;
    });

    // 8. Top entities by member count
    const entityMemberCounts = {};
    displayMembers.forEach(m => {
      const e = displayEntities.find(ent => String(ent.id) === String(m.entityId));
      if (e) {
        entityMemberCounts[e.id] = (entityMemberCounts[e.id] || 0) + 1;
      }
    });
    const topEntitiesData = Object.entries(entityMemberCounts)
      .map(([entityId, count]) => {
        const entity = displayEntities.find(e => e.id === entityId);
        const entityName = entity?.englishName || entity?.officialName || 'Unknown';
        const country = entity?.country || '';
        return { name: `${entityName} (${country})`, count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);

    // 9. Sector mix by country
    const sectorByCountry = {};
    displayEntities.forEach(e => {
      if (!sectorByCountry[e.country]) sectorByCountry[e.country] = {};
      const countryMembers = displayMembers.filter(m => String(m.entityId) === String(e.id));
      countryMembers.forEach(m => {
        const sector = normalizeMemberSector(m.type);
        sectorByCountry[e.country][sector] = (sectorByCountry[e.country][sector] || 0) + 1;
      });
    });
    const sectorCountryData = Object.entries(sectorByCountry)
      .map(([country, sectors]) => {
        const total = Object.values(sectors).reduce((a, b) => a + b, 0);
        const data = { country };
        Object.entries(sectors).forEach(([sector, count]) => {
          data[sector] = parseFloat(((count / total) * 100).toFixed(1));
        });
        return data;
      })
      .sort((a, b) => b.country.localeCompare(a.country));

    // 10. AI specialization by country
    const aiByCountry = {};
    displayEntities.forEach(e => {
      if (!aiByCountry[e.country]) aiByCountry[e.country] = { total: 0, ai: 0 };
      aiByCountry[e.country].total++;
      if (isAISpecificEntity(e)) aiByCountry[e.country].ai++;
    });
    const aiRatioData = Object.entries(aiByCountry)
      .map(([country, data]) => ({
        country,
        total: data.total,
        'AI-specific': data.ai
      }))
      .sort((a, b) => (b['AI-specific'] / b.total) - (a['AI-specific'] / a.total));

    // 11. Shannon diversity index by country
    const diversityByCountry = {};
    displayEntities.forEach(e => {
      if (!diversityByCountry[e.country]) {
        diversityByCountry[e.country] = { sectorCounts: {}, totalMembers: 0 };
      }
      const entityMembers = displayMembers.filter(m => String(m.entityId) === String(e.id));
      entityMembers.forEach(m => {
        const sector = normalizeMemberSector(m.type);
        diversityByCountry[e.country].sectorCounts[sector] = (diversityByCountry[e.country].sectorCounts[sector] || 0) + 1;
        diversityByCountry[e.country].totalMembers++;
      });
    });
    const diversityData = Object.entries(diversityByCountry)
      .filter(([_, data]) => data.totalMembers >= 5)
      .map(([country, data]) => {
        let shannon = 0;
        Object.values(data.sectorCounts).forEach(count => {
          const p = count / data.totalMembers;
          if (p > 0) shannon -= p * Math.log(p);
        });
        return { name: country, shannon: parseFloat(shannon.toFixed(3)) };
      })
      .sort((a, b) => b.shannon - a.shannon);

    // 12. Documents by type
    const docsByType = {};
    documents.forEach(d => {
      const type = d.type || 'Other';
      docsByType[type] = (docsByType[type] || 0) + 1;
    });
    const docsTypeData = Object.entries(docsByType)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);

    // 13. Entities by country
    const entitiesByCountry = {};
    displayEntities.forEach(e => {
      if (!entitiesByCountry[e.country]) entitiesByCountry[e.country] = { total: 0, active: 0, ai: 0 };
      entitiesByCountry[e.country].total++;
      if (!e.isExtinct) entitiesByCountry[e.country].active++;
      if (isAISpecificEntity(e)) entitiesByCountry[e.country].ai++;
    });
    const entitiesCountryData = Object.entries(entitiesByCountry)
      .map(([country, data]) => ({
        country,
        'Total': data.total,
        'Active': data.active,
        'AI-specific': data.ai
      }))
      .sort((a, b) => b['Total'] - a['Total']);

    return {
      entityTypeData,
      waveData,
      sectorData,
      genderTypeData,
      genderHistData,
      genderCountryData,
      eraTypeData,
      topEntitiesData,
      sectorCountryData,
      aiRatioData,
      diversityData,
      docsTypeData,
      entitiesCountryData,
      colors: C
    };
  }, [displayEntities, displayMembers, documents]);

  return (
    <div className={`p-8 space-y-8 ${BRAND.light} min-h-screen font-sans`}>
      <div className="flex justify-between items-end border-b border-stone-300 pb-4">
        <div>
          <h2 className="text-3xl font-bold text-stone-900 font-sans">Dashboard</h2>
        </div>
      </div>
      
      {/* UNIFIED GRID LAYOUT - KPIS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        
        {/* --- KPI 1: Countries Covered --- */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 relative group flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Policy Systems</p>
              {isEditingTarget ? (
                 <div className="flex items-center gap-2">
                    <input 
                        type="number" 
                        className="w-16 border border-stone-300 p-1 text-lg font-bold"
                        value={tempTarget}
                        onChange={(e) => setTempTarget(e.target.value)}
                    />
                    <button onClick={handleSaveTarget} className="text-green-600"><CheckCircle size={20}/></button>
                 </div>
              ) : (
                <div className="flex items-baseline gap-1">
                    <p className={`text-3xl font-bold ${BRAND.accent}`}>{stats.countriesWithData}</p>
                    <p className="text-stone-400 text-lg">/ {target}</p>
                </div>
              )}
               <p className="text-xs text-stone-400 mt-2">Jurisdictions with at least one identified Policy Advisory System.</p>
            </div>
            <div className={`h-10 w-10 ${BRAND.light} rounded-full flex items-center justify-center text-stone-600`}>
              <Globe size={20} />
            </div>
          </div>
          {isAdmin && (
            <button 
                onClick={() => setIsEditingTarget(true)}
                className="absolute top-4 right-14 text-stone-300 hover:text-stone-600 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Change Target (Admin)"
            >
                <Settings size={16} />
            </button>
          )}
          <div className="w-full bg-stone-100 h-1.5 mt-4 overflow-hidden absolute bottom-0 left-0">
            {/* The bar represents global progress on current functions now */}
            <div className={`${BRAND.primary} h-full`} style={{ width: `${stats.completionRate}%` }}></div>
          </div>
        </div>

        {/* --- KPI 2: Member Geo Coverage (Moved) --- */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col justify-between">
           <div className="flex justify-between items-start">
             <div>
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Member Coverage</p>
                <div className={`flex items-baseline gap-1 ${BRAND.accent}`}>
                    <span className="text-3xl font-bold">{memberStats.countriesWithMembers}</span>
                    <span className="text-lg text-stone-400">/ {target}</span>
                </div>
                <p className="text-stone-500 text-xs mt-2">Countries with registered members.</p>
             </div>
             <div className={`h-10 w-10 ${BRAND.light} rounded-full flex items-center justify-center ${BRAND.accent}`}>
                <Users size={20} />
             </div>
           </div>
        </div>

        {/* --- KPI 3: Total Members --- */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Members Mapped</p>
              <p className="text-3xl font-bold text-stone-800">{stats.totalMembers}</p>
              <p className="text-xs text-stone-400 mt-2">{stats.entitiesWithMembers} / {stats.totalEntities} entities with members</p>
            </div>
            <div className={`h-10 w-10 ${BRAND.light} rounded-full flex items-center justify-center text-stone-600`}>
              <Users size={20} />
            </div>
          </div>
        </div>
        
        {/* --- KPI 4: Verified Entities (filter toggle) --- */}
        <button
          onClick={() => setVerifiedFilter(v => !v)}
          title={verifiedFilter ? 'Click to show all entities' : 'Click to filter by verified entities'}
          className={`w-full text-left p-6 rounded-sm shadow-sm border flex flex-col justify-between transition-colors
            ${verifiedFilter
              ? 'bg-green-50 border-green-500 ring-1 ring-green-400'
              : 'bg-white border-stone-200 hover:border-stone-400'
            }`}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">
                Verified Entities
                {verifiedFilter && (
                  <span className="ml-2 bg-green-100 text-green-700 text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide">
                    Filtering
                  </span>
                )}
              </p>
              <div className="flex items-baseline gap-1">
                <p className="text-3xl font-bold text-stone-800">{stats.verifiedRate}%</p>
              </div>
            </div>
            <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${verifiedFilter ? 'bg-green-100 text-green-600' : `${BRAND.light} ${BRAND.accent}`}`}>
              <CheckCircle size={20} />
            </div>
          </div>
          <p className="text-xs text-stone-400 mt-3 border-t border-stone-100 pt-2">
            {stats.verifiedEntities} / {stats.totalEntities} entities
            {!verifiedFilter && <span className="ml-1 italic">· click to filter</span>}
          </p>
        </button>

        {/* --- KPI 5: Documents (Updated CONTENT) --- */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Documents</p>
              <div className="flex items-baseline gap-1">
                 <p className="text-3xl font-bold text-stone-800">{stats.docsRate}%</p>
              </div>
            </div>
            <div className={`h-10 w-10 ${BRAND.light} rounded-full flex items-center justify-center text-stone-600`}>
              <FileText size={20} />
            </div>
          </div>
          <p className="text-xs text-stone-400 mt-3 border-t border-stone-100 pt-2">{stats.entitiesWithDocs} / {stats.totalEntities} with docs</p>
        </div>
      </div>
      
      {/* --- ROW 1.5: ENTITY MAP & TIMELINE --- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-6">
          <MapChart 
             data={memberStats.entityMapData} 
             title="Geographic Distribution"
             suffix=" entities"
             colorRange={["#f0fdf4", "#15803d"]} // Light green to Dark green
          >
             <select 
                className="border border-stone-300 rounded p-1 max-w-[100px]"
                value={mapEntityFilter}
                onChange={(e) => setMapEntityFilter(e.target.value)}
             >
                 <option value="All">All Types</option>
                 {memberStats.allEntityTypes.map(t => <option key={t} value={t}>{t}</option>)}
             </select>
          </MapChart>
          
          <EntityTimeline entities={displayEntities} />
      </div>

          {/* --- ROW 2: CHARTS (UPDATED GRID) --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        {/* 1. ENTITY TYPE GLOBAL (NEW) */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col justify-between h-[340px]">
            <h4 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-4 border-b border-stone-100 pb-2">Entity Types (Global)</h4>
            <div className="flex-grow">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={memberStats.entityType} cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" paddingAngle={2}>
                            {memberStats.entityType.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip /> 
                        <Legend verticalAlign="bottom" height={80} iconSize={8} wrapperStyle={{fontSize: '10px'}}/>
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>

        {/* 2. INTEREST GROUP GLOBAL */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col justify-between h-[340px]">
            <h4 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-4 border-b border-stone-100 pb-2">Interest Groups (Global)</h4>
            <div className="flex-grow">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={memberStats.type} cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" paddingAngle={2}>
                            {memberStats.type.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip /> 
                        <Legend verticalAlign="bottom" height={80} iconSize={8} wrapperStyle={{fontSize: '10px'}}/>
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>

        {/* 3. GENDER PIE (GLOBAL) */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col justify-between h-[340px]">
            <h4 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-4 border-b border-stone-100 pb-2">Gender Distribution (Global)</h4>
            <div className="flex-grow">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={memberStats.gender} cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" paddingAngle={2}>
                            {memberStats.gender.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getGenderColor(entry.name)} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={36} iconSize={8} wrapperStyle={{fontSize: '10px'}}/>
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>

         {/* 4. ENTITY TYPE BY COUNTRY (NEW) */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col justify-between h-[340px]">
             <div className="flex justify-between items-center mb-4 border-b border-stone-100 pb-2">
                <h4 className="text-xs font-bold text-stone-500 uppercase tracking-wider">Entity Types by Country</h4>
                <select
                    className="text-xs border border-stone-300 rounded p-1 min-w-[120px]"
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                >
                    {memberStats.countriesWithMembersList.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>
             <div className="flex-grow">
                {memberStats.entityTypeByCountry.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={memberStats.entityTypeByCountry} cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" paddingAngle={2}>
                                    {memberStats.entityTypeByCountry.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip /> 
                                <Legend verticalAlign="bottom" height={80} iconSize={8} wrapperStyle={{fontSize: '10px'}}/>
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-stone-400 text-xs italic">
                            No data found.
                        </div>
                )}
            </div>
        </div>

        {/* 5. INTEREST GROUP BY COUNTRY */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col justify-between h-[340px]">
             <div className="flex justify-between items-center mb-4 border-b border-stone-100 pb-2">
                <h4 className="text-xs font-bold text-stone-500 uppercase tracking-wider">Interest by Country</h4>
                <span className="text-xs text-stone-500 font-medium">{selectedCountry}</span>
            </div>
             <div className="flex-grow">
                {memberStats.interestByCountry.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={memberStats.interestByCountry} cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" paddingAngle={2}>
                                    {memberStats.interestByCountry.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip /> 
                                <Legend verticalAlign="bottom" height={80} iconSize={8} wrapperStyle={{fontSize: '10px'}}/>
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-stone-400 text-xs italic">
                            No data found.
                        </div>
                )}
            </div>
        </div>
        
        {/* 6. GENDER BY COUNTRY */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 flex flex-col justify-between h-[340px]">
            <div className="flex justify-between items-center mb-4 border-b border-stone-100 pb-2">
                <h4 className="text-xs font-bold text-stone-500 uppercase tracking-wider">Gender by Country</h4>
                <span className="text-xs text-stone-500 font-medium">{selectedCountry}</span>
            </div>
            
            <div className="flex-grow">
                {memberStats.genderByCountry.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={memberStats.genderByCountry} cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" paddingAngle={2}>
                                    {memberStats.genderByCountry.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={getGenderColor(entry.name)} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend verticalAlign="bottom" height={36} iconSize={8} wrapperStyle={{fontSize: '10px'}}/>
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-stone-400 text-xs italic">
                            No members found.
                        </div>
                )}
            </div>
        </div>

      </div>

      {/* --- ROW 3: MAPS (Gender & Interest) --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-8">
            <MapChart 
                data={memberStats.genderMapData} 
                title="% Gender by Country"
                suffix="%"
                colorRange={["#fff", "#eab308"]} // White to Yellow (Male) or Purple? Let's dynamic? No, stick to one scale.
            >
                <select 
                    className="border border-stone-300 rounded p-1"
                    value={mapGenderFilter}
                    onChange={(e) => setMapGenderFilter(e.target.value)}
                >
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                </select>
            </MapChart>

            <MapChart 
                data={memberStats.interestMapData} 
                title="% Interest Group by Country"
                suffix="%"
                colorRange={["#eff6ff", "#1d4ed8"]} // Light Blue to Blue
            >
                <select 
                    className="border border-stone-300 rounded p-1 max-w-[150px]"
                    value={mapInterestFilter}
                    onChange={(e) => setMapInterestFilter(e.target.value)}
                >
                    <option value="">Select Group...</option>
                    <option value="Academia & Research">Academia & Research</option>
                    <option value="Business & Industry">Business & Industry</option>
                    <option value="Public Sector">Public Sector</option>
                    <option value="Civil Society">Civil Society</option>
                    <option value="Independent Experts">Independent Experts</option>
                </select>
            </MapChart>
      </div>

      {/* --- ADVANCED ANALYTICS SECTION --- */}
      <div className="mt-8">

        {/* 1. Creation Waves */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200">
            <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">Creation Waves by Year (1996–2026)</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.waveData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" angle={-45} textAnchor="end" height={80} tick={{fontSize: 11}} />
                <YAxis tick={{fontSize: 11}} />
                <Tooltip />
                <Legend />
                <Bar dataKey="General digital" stackId="a" fill={analyticsData.colors.blue} />
                <Bar dataKey="AI-Specific" stackId="a" fill={analyticsData.colors.orange} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200">
            <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">Women Representation by Entity Type</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.genderTypeData} layout="vertical" margin={{left: 150}}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{fontSize: 11}} label={{value: '% Women', angle: 0, position: 'bottom'}} />
                <YAxis dataKey="type" type="category" tick={{fontSize: 10}} width={140} />
                <Tooltip formatter={(value) => `${value}%`} />
                <Bar dataKey="Female %" fill={analyticsData.colors.purple} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 2. Gender Histogram */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200">
            <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">Women % Distribution by Entity</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.genderHistData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" angle={-45} textAnchor="end" height={80} tick={{fontSize: 10}} />
                <YAxis tick={{fontSize: 11}} label={{value: 'N entities', angle: -90, position: 'insideLeft'}} />
                <Tooltip />
                <Bar dataKey="count" fill={analyticsData.colors.red} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 3. Gender by Country */}
          <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200">
            <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">Women Representation by Country</h4>
            <div style={{maxHeight: '400px', overflowY: 'auto'}}>
              <ResponsiveContainer width="100%" height={550}>
                <BarChart data={analyticsData.genderCountryData} layout="vertical" margin={{left: 120}}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{fontSize: 10}} />
                  <YAxis dataKey="country" type="category" tick={{fontSize: 10}} width={110} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Bar dataKey="percentage">
                    {analyticsData.genderCountryData.map((entry, index) => {
                      let barColor = analyticsData.colors.orange;
                      if (entry.percentage >= 50) barColor = analyticsData.colors.green;
                      else if (entry.percentage >= 34.8) barColor = analyticsData.colors.blue;
                      else if (entry.percentage < 25) barColor = analyticsData.colors.red;
                      return <Cell key={`cell-${index}`} fill={barColor} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* 4. Entity Types by Era */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 mb-8">
          <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">Entity Types by Historical Era</h4>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={analyticsData.eraTypeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="era" tick={{fontSize: 11}} />
              <YAxis tick={{fontSize: 11}} label={{value: 'N entities', angle: -90, position: 'insideLeft'}} />
              <Tooltip />
              <Legend wrapperStyle={{paddingTop: '20px'}} />
              {Array.from(new Set(
                analyticsData.eraTypeData.flatMap(d => Object.keys(d).filter(k => k !== 'era'))
              )).map((type, idx) => (
                <Bar key={type} dataKey={type} stackId="a" fill={COLORS[idx % COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 5. Top Entities */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 mb-8">
          <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">Top 15 Entities by Member Count</h4>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={analyticsData.topEntitiesData} layout="vertical" margin={{left: 250}}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{fontSize: 11}} />
              <YAxis dataKey="name" type="category" tick={{fontSize: 9}} width={240} />
              <Tooltip />
              <Bar dataKey="count" fill={analyticsData.colors.blue} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 6. Sector Mix by Country */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 mb-8">
          <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">Member Sector Mix by Country</h4>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={analyticsData.sectorCountryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="country" angle={-45} textAnchor="end" height={80} tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 11}} label={{value: '% composition', angle: -90, position: 'insideLeft'}} />
              <Tooltip />
              <Legend wrapperStyle={{paddingTop: '20px'}} />
              <Bar dataKey="Public Authority" stackId="a" fill={analyticsData.colors.blue} />
              <Bar dataKey="Industry" stackId="a" fill={analyticsData.colors.orange} />
              <Bar dataKey="Academia" stackId="a" fill={analyticsData.colors.green} />
              <Bar dataKey="Independent Expert" stackId="a" fill={analyticsData.colors.amber} />
              <Bar dataKey="Civil Society" stackId="a" fill={analyticsData.colors.purple} />
              <Bar dataKey="Other" stackId="a" fill={analyticsData.colors.grey} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 7. AI Specialization */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 mb-8">
          <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">AI Specialization by Country</h4>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={analyticsData.aiRatioData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="country" angle={-45} textAnchor="end" height={80} tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 11}} label={{value: 'N entities', angle: -90, position: 'insideLeft'}} />
              <Tooltip />
              <Legend />
              <Bar dataKey="total" fill={analyticsData.colors.light} />
              <Bar dataKey="AI-specific" fill={analyticsData.colors.orange} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 8. Shannon Diversity */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 mb-8">
          <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">Sectoral Diversity Index by Country (Shannon H')</h4>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={analyticsData.diversityData} layout="vertical" margin={{left: 100}}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{fontSize: 11}} domain={[0, 2]} />
              <YAxis dataKey="name" type="category" tick={{fontSize: 11}} width={90} />
              <Tooltip formatter={(value) => value.toFixed(3)} />
              <Bar dataKey="shannon">
                {analyticsData.diversityData.map((entry, index) => {
                  let barColor = analyticsData.colors.red;
                  if (entry.shannon >= 1) barColor = analyticsData.colors.green;
                  else if (entry.shannon >= 0.5) barColor = analyticsData.colors.orange;
                  return <Cell key={`cell-${index}`} fill={barColor} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 9. Documents by Type */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200">
            <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">Documents by Type</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.docsTypeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" angle={-45} textAnchor="end" height={80} tick={{fontSize: 10}} />
                <YAxis tick={{fontSize: 11}} label={{value: 'N documents', angle: -90, position: 'insideLeft'}} />
                <Tooltip />
                <Bar dataKey="count" fill={analyticsData.colors.blue} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 10. Entities by Country */}
          <div className="bg-white p-6 rounded-sm shadow-sm border border-stone-200">
            <h4 className="text-sm font-bold text-stone-600 uppercase tracking-wider mb-4">Entities by Country</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.entitiesCountryData.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="country" angle={-45} textAnchor="end" height={80} tick={{fontSize: 10}} />
                <YAxis tick={{fontSize: 11}} label={{value: 'N entities', angle: -90, position: 'insideLeft'}} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Total" fill={analyticsData.colors.light} />
                <Bar dataKey="Active" fill={analyticsData.colors.blue} />
                <Bar dataKey="AI-specific" fill={analyticsData.colors.orange} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

    </div>
  );
};

const getEntityEnglishName = (entity) => String(entity?.englishName || entity?.officialName || '').trim();
const getEntityOriginalName = (entity) => String(entity?.officialName || entity?.englishName || '').trim();

const parseEntityTimestampMs = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const parsedNative = Date.parse(raw);
    if (Number.isFinite(parsedNative)) return parsedNative;

    // Support SQLite UTC format: YYYY-MM-DD HH:mm:ss
    const sqliteMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (sqliteMatch) {
        const year = Number(sqliteMatch[1]);
        const month = Number(sqliteMatch[2]);
        const day = Number(sqliteMatch[3]);
        const hour = Number(sqliteMatch[4]);
        const minute = Number(sqliteMatch[5]);
        const second = Number(sqliteMatch[6] || 0);
        const utc = Date.UTC(year, month - 1, day, hour, minute, second);
        return Number.isFinite(utc) ? utc : null;
    }

    return null;
};

const getEntityLastEditedMs = (entity) => {
    const parsed = parseEntityTimestampMs(entity?.lastEditedAt);
    return Number.isFinite(parsed) ? parsed : -1;
};

const formatEntityDateTime = (value) => {
    const parsed = parseEntityTimestampMs(value);
    if (!Number.isFinite(parsed)) return '—';
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).format(new Date(parsed));
};

const getInitialsFromUsername = (username) => {
    const cleaned = String(username || '').trim();
    if (!cleaned) return '?';

    const chunks = cleaned
        .replace(/[^a-zA-Z0-9._\-\s]+/g, ' ')
        .split(/[._\-\s]+/)
        .map((part) => part.trim())
        .filter(Boolean);

    if (chunks.length >= 2) {
        return `${chunks[0].charAt(0)}${chunks[1].charAt(0)}`.toUpperCase();
    }

    const first = chunks[0] || cleaned;
    return first.slice(0, 2).toUpperCase();
};

const sortEntitiesByMode = (items, mode = 'lastEdited') => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    return [...(items || [])].sort((a, b) => {
        if (mode === 'lastEdited') {
            const delta = getEntityLastEditedMs(b) - getEntityLastEditedMs(a);
            if (delta !== 0) return delta;
        }
        if (mode === 'nameOriginal') {
            return collator.compare(getEntityOriginalName(a), getEntityOriginalName(b));
        }
        return collator.compare(getEntityEnglishName(a), getEntityEnglishName(b));
    });
};

// 2. COUNTRY VIEW
const CountryView = ({ entities, onSelectEntity, onAddEntity, selectedCountryName, onSelectedCountryNameChange, sortMode, onSortModeChange, editorsByCountry }) => {
    // Auth context
    const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
    const canEdit = user && ['admin', 'coder'].includes(user.role);
    const countryList = useMemo(() => {
        const fromData = Array.from(new Set((entities || []).map((e) => String(e.country || '').trim()).filter(Boolean)));
        const merged = Array.from(new Set([...WORLD_COUNTRIES, ...fromData]));
        return merged.sort((a, b) => a.localeCompare(b));
    }, [entities]);

    const entitiesForSelectedCountry = useMemo(
        () => sortEntitiesByMode(
            (entities || []).filter((e) => String(e.country || '').trim() === selectedCountryName),
            sortMode
        ),
        [entities, selectedCountryName, sortMode]
    );

    return (
        <div className={`p-8 ${BRAND.light} min-h-screen`}>
            <div className="flex justify-between items-center mb-8 pb-4 border-b border-stone-300">
                <div>
                    <h2 className="text-3xl font-bold text-stone-900 font-sans">Countries</h2>
                    {selectedCountryName && <div className="text-sm text-stone-500 mt-1">{selectedCountryName}</div>}
                </div>
                {canEdit && (
                    <button 
                    onClick={onAddEntity}
                    className={`${BRAND.primary} ${BRAND.primaryHover} text-white px-5 py-2 rounded-sm text-sm font-medium flex items-center gap-2 transition-colors`}>
                    <Plus size={16} /> New Entity
                    </button>
                )}
            </div>

            {!selectedCountryName ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {countryList.map((country) => {
                        const count = (entities || []).filter((e) => String(e.country || '').trim() === country).length;
                        const hasData = count > 0;
                        const verifiedCount = (entities || []).filter((e) => String(e.country || '').trim() === country && e.status === 'verified').length;
                        const verifiedRate = hasData ? Math.round((verifiedCount / count) * 100) : 0;
                        const editors = Array.isArray(editorsByCountry?.[country]) ? editorsByCountry[country] : [];
                        return (
                            <button
                                key={country}
                                onClick={() => onSelectedCountryNameChange(country)}
                                className={`relative overflow-hidden text-left border ${hasData ? 'border-green-200 hover:border-green-300' : 'border-stone-100 hover:border-stone-300'} p-5 rounded-sm shadow-sm hover:shadow-md transition-all bg-white`}
                            >
                                <div
                                    className="absolute inset-y-0 left-0 bg-green-100 transition-all duration-300 ease-out"
                                    style={{ width: `${verifiedRate}%` }}
                                />
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className={`relative z-10 font-bold text-lg ${hasData ? 'text-green-950' : 'text-stone-400'}`}>{country}</h3>
                                    <span className={`relative z-10 text-xs px-2 py-1 rounded-full font-bold ${hasData ? 'bg-white/80 text-green-700' : 'bg-stone-50 text-stone-300'}`}>{count}</span>
                                </div>
                                <div className={`relative z-10 text-xs uppercase tracking-wider ${hasData ? 'text-green-700' : 'text-stone-500'}`}>Select country</div>
                                {editors.length > 0 && (
                                    <div className="relative z-10 mt-3 flex flex-wrap gap-1.5" title={`Edited by: ${editors.join(', ')}`}>
                                        {editors.map((username) => (
                                            <span
                                                key={`${country}-${username}`}
                                                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-stone-800 text-white text-[10px] font-bold uppercase"
                                            >
                                                {getInitialsFromUsername(username)}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="space-y-4">
                    <div>
                        <button
                            onClick={() => onSelectedCountryNameChange('')}
                            className="px-3 py-2 border border-stone-300 bg-white hover:bg-stone-50 text-sm"
                        >
                            Back to Countries
                        </button>
                    </div>
                    <div>
                        <select
                            value={sortMode}
                            onChange={(e) => onSortModeChange(e.target.value)}
                            className="px-3 py-2 border border-stone-300 rounded-sm bg-white text-sm focus:ring-1 focus:ring-green-600 focus:outline-none"
                        >
                            <option value="lastEdited">Sort: Last edited</option>
                            <option value="nameEn">Sort: Alphabetical (EN)</option>
                            <option value="nameOriginal">Sort: Alphabetical (Original)</option>
                        </select>
                    </div>
                    <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-4 space-y-2">
                        {entitiesForSelectedCountry.map((entity) => (
                            <button
                                key={entity.id}
                                onClick={() => onSelectEntity(entity)}
                                className={`w-full text-left text-sm p-3 border rounded-sm flex items-center justify-between group transition-colors ${
                                    entity.status === 'verified'
                                        ? 'bg-green-50 border-green-200 text-green-900 hover:bg-green-100'
                                        : `bg-stone-50 border-stone-100 ${BRAND.primaryHover} hover:text-white`
                                }`}
                            >
                                <span className={`truncate font-medium ${entity.status === 'verified' ? 'text-green-900' : 'text-stone-700 group-hover:text-white'}`}>{entity.englishName || entity.officialName}</span>
                                {entity.status === 'verified' && <CheckCircle size={12} className="text-green-700 flex-shrink-0" />}
                            </button>
                        ))}
                        {entitiesForSelectedCountry.length === 0 && (
                            <div className="p-4 text-sm text-stone-400 italic">No entities registered for this country.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// 3. ENTITY MANAGER (Table View)
const EntityManager = ({ entities, onSelectEntity, onAddEntity, filter, onFilterChange, sortMode, onSortModeChange }) => {
  
  // Auth context
  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
  const canEdit = user && ['admin', 'coder'].includes(user.role);

  const normalizedFilter = filter.toLowerCase();
  const filteredEntities = useMemo(() => {
      const filtered = (entities || []).filter(e =>
          (e.country || '').toLowerCase().includes(normalizedFilter) ||
          (e.englishName || '').toLowerCase().includes(normalizedFilter) ||
          (e.officialName || '').toLowerCase().includes(normalizedFilter)
      );
      return sortEntitiesByMode(filtered, sortMode);
  }, [entities, normalizedFilter, sortMode]);

  return (
    <div className={`p-8 ${BRAND.light} min-h-screen`}>
       <div className="flex justify-between items-center mb-6 border-b border-stone-300 pb-4">
        <h2 className="text-3xl font-bold text-stone-900 font-sans">Entity Database</h2>
        {canEdit && (
            <button 
            onClick={onAddEntity}
            className={`${BRAND.primary} ${BRAND.primaryHover} text-white px-5 py-2 rounded-sm text-sm font-medium flex items-center gap-2 transition-colors`}>
            <Plus size={16} /> New Entity
            </button>
        )}
      </div>

      <div className="bg-white rounded-sm shadow-sm border border-stone-200 overflow-hidden">
        <div className="p-4 border-b border-stone-200 flex gap-4 bg-stone-50">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <input 
              type="text" 
              placeholder="Search by country or name..." 
              className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-sm focus:ring-1 focus:ring-green-600 focus:outline-none text-sm"
              value={filter}
                            onChange={(e) => onFilterChange(e.target.value)}
            />
          </div>
                    <select
                        value={sortMode}
                                                onChange={(e) => onSortModeChange(e.target.value)}
                        className="px-3 py-2 border border-stone-300 rounded-sm bg-white text-sm focus:ring-1 focus:ring-green-600 focus:outline-none"
                    >
                        <option value="lastEdited">Sort: Last edited</option>
                        <option value="nameEn">Sort: Alphabetical (EN)</option>
                        <option value="nameOriginal">Sort: Alphabetical (Original)</option>
                    </select>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-stone-100 text-stone-600 text-xs font-bold uppercase tracking-wider">
              <tr>
                <th className="p-4 border-b border-stone-200">Country</th>
                <th className="p-4 border-b border-stone-200">Entity Name (EN)</th>
                                <th className="p-4 border-b border-stone-200 hidden lg:table-cell">Entity Name (Original)</th>
                <th className="p-4 border-b border-stone-200">Type</th>
                <th className="p-4 border-b border-stone-200">Year</th>
                                <th className="p-4 border-b border-stone-200">Last Edited</th>
                <th className="p-4 border-b border-stone-200">Status</th>
                <th className="p-4 border-b border-stone-200 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 text-sm">
              {filteredEntities.map(entity => (
                <tr key={entity.id} className={`transition-colors group ${
                    entity.status === 'verified' 
                    ? 'bg-white hover:bg-stone-50 border-l-4 border-l-green-600' 
                    : 'bg-stone-50 hover:bg-stone-100 border-l-4 border-l-transparent'
                  }`}>
                  <td className="p-4 font-bold text-stone-800 font-sans">
                      {entity.country}
                  </td>
                  <td className={`p-4 font-medium ${entity.status === 'verified' ? 'text-green-900' : 'text-stone-600'}`}>
                      {entity.englishName || entity.officialName}
                  </td>
                  <td className="p-4 text-stone-500 hidden lg:table-cell">
                      {entity.officialName || '—'}
                  </td>
                  <td className="p-4 text-stone-500">{entity.type}</td>
                  <td className="p-4 text-stone-500 font-mono">{entity.year}</td>
                                      <td className="p-4 text-stone-500 whitespace-nowrap">{formatEntityDateTime(entity.lastEditedAt)}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide ${
                      entity.status === 'verified' ? 'bg-green-200 text-green-900' :
                      entity.status === 'review' ? 'bg-amber-100 text-amber-800' :
                      'bg-stone-200 text-stone-600'
                    }`}>
                      {entity.status}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button 
                      onClick={() => onSelectEntity(entity)}
                      className={`font-medium flex items-center justify-end gap-1 w-full transition-colors font-sans text-xs uppercase tracking-wider ${
                          (entity.status === 'verified' || !canEdit) ? 'text-stone-400 hover:text-stone-600' : 'text-green-700 hover:text-green-900'
                      }`}
                    >
                                            <Eye size={16} /> 
                                            <span className="hidden md:inline">View</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// 4. ENTITY DETAIL & EDITOR
const EntityViewer = ({ entity, mode, onSave, onBack, onEdit, documents, onAddDocument, onDeleteDocument, onReplaceDocument, members, onAddMember, onUpdateMember, onDeleteMember, onDeleteEntity, entities }) => {
  const [formData, setFormData] = useState({ ...entity });
  const [activeTab, setActiveTab] = useState('general'); 
    const [uploadQueue, setUploadQueue] = useState([]);
    const [isBulkUploading, setIsBulkUploading] = useState(false);
    const [bulkUploadMsg, setBulkUploadMsg] = useState(null);
    const [docTypeOverrides, setDocTypeOverrides] = useState({});
    const [docActionMsg, setDocActionMsg] = useState(null);
    const [isReplacingDocId, setIsReplacingDocId] = useState(null);
  const [newMember, setNewMember] = useState({ name: '', role: '', gender: '', background: '', type: '' });
    const [bulkMembersCsv, setBulkMembersCsv] = useState('');
    const [bulkMembersMsg, setBulkMembersMsg] = useState(null);
  
  // Inline editing state for members
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editMemberData, setEditMemberData] = useState({});

  // Auth Context for EntityViewer
  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
  const canEdit = user && ['admin', 'coder'].includes(user.role);
  const canVerify = user && user.role === 'admin';
    const canEditEntity = canEdit && (entity.status !== 'verified' || canVerify);
  // If user is guest/readonly, mode is always 'view'
    const isEditMode = canEditEntity && mode === 'edit';

  const onDrop = useCallback(acceptedFiles => {
     if(acceptedFiles?.length) {
            const newItems = acceptedFiles.map(file => ({
                localId: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                file,
                type: DOCUMENT_TYPE_OPTIONS[0],
                displayName: file.name
            }));
            setUploadQueue(prev => [...prev, ...newItems]);
            setBulkUploadMsg(null);
     }
  }, []);
  const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop, multiple: true});

  useEffect(() => {
     setFormData({...entity});
  }, [entity]);

  const normalizeText = (field) => {
    const text = formData[field];
    if (!text) return;
    const normalized = text.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
    setFormData(prev => ({ ...prev, [field]: normalized }));
  };

  const handlePaste = async (field) => {
    try {
      const text = await navigator.clipboard.readText();
      setFormData(prev => ({ ...prev, [field]: text }));
    } catch (err) {
      alert('Use Ctrl+V');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleStatusChange = (status) => {
    setFormData(prev => ({ ...prev, status }));
  };

  const updateUploadItem = (localId, field, value) => {
      setUploadQueue(prev => prev.map(item => item.localId === localId ? { ...item, [field]: value } : item));
  };

  const removeUploadItem = (localId) => {
      setUploadQueue(prev => prev.filter(item => item.localId !== localId));
  };

  const handleUploadSubmit = async () => {
    if (uploadQueue.length === 0) {
        alert("Please select at least one file to upload.");
        return;
    }

    const missingDisplayName = uploadQueue.find(item => !item.displayName || !item.displayName.trim());
    if (missingDisplayName) {
        alert("Please enter a display name for every selected file.");
        return;
    }

    setIsBulkUploading(true);
    setBulkUploadMsg(null);

    let successCount = 0;
    let failCount = 0;

    for (const item of uploadQueue) {
        const payload = new FormData();
        payload.append('file', item.file);
        payload.append('id', `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
        payload.append('entityId', entity.id);
        payload.append('type', item.type);
        payload.append('display_name', item.displayName.trim());
        payload.append('date', new Date().toISOString().split('T')[0]);

        try {
            await onAddDocument(payload, { silent: true });
            successCount++;
        } catch (error) {
            failCount++;
        }
    }

    setIsBulkUploading(false);
    if (successCount > 0) {
        setUploadQueue([]);
    }
    setBulkUploadMsg({
        type: failCount === 0 ? 'success' : 'error',
        text: failCount === 0
            ? `Uploaded ${successCount} document${successCount === 1 ? '' : 's'} successfully.`
            : `Uploaded ${successCount} document${successCount === 1 ? '' : 's'} and failed ${failCount}.`
    });
  };

  const handleDeleteDocumentClick = async (doc) => {
    if (!window.confirm(`Delete document "${doc.display_name || doc.name}"? This cannot be undone.`)) return;
      setDocActionMsg(null);

      try {
          await onDeleteDocument(doc);
          setDocActionMsg({ type: 'success', text: 'Document deleted successfully.' });
      } catch (error) {
          setDocActionMsg({ type: 'error', text: error.message || 'Failed to delete document.' });
      }
  };

  const handleReplaceFileSelected = async (doc, file) => {
      if (!file) return;
      setDocActionMsg(null);
      setIsReplacingDocId(doc.id);

      try {
          const selectedType = docTypeOverrides[doc.id] || doc.type;
          await onReplaceDocument(doc.id, file, selectedType);
          setDocActionMsg({ type: 'success', text: `Document "${doc.display_name}" replaced successfully.` });
      } catch (error) {
          setDocActionMsg({ type: 'error', text: error.message || 'Failed to replace document.' });
      } finally {
          setIsReplacingDocId(null);
      }
  };

  const handleAddMemberSubmit = () => {
    if (!newMember.name) return;
    onAddMember({ ...newMember, entityId: entity.id, id: Date.now().toString() });
    setNewMember({ name: '', role: '', gender: '', background: '', type: '' });
  };

  const parseCsvLine = (line) => {
      const values = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
          const char = line[i];
          const next = line[i + 1];

          if (char === '"') {
              if (inQuotes && next === '"') {
                  current += '"';
                  i++;
              } else {
                  inQuotes = !inQuotes;
              }
          } else if (char === ',' && !inQuotes) {
              values.push(current.trim());
              current = '';
          } else {
              current += char;
          }
      }

      values.push(current.trim());
      return values;
  };

  const normalizeHeader = (header) => (header || '').toLowerCase().trim();

  const handleBulkMembersImport = async () => {
      setBulkMembersMsg(null);
      const raw = bulkMembersCsv.trim();
      if (!raw) {
          setBulkMembersMsg({ type: 'error', text: 'Paste CSV content first.' });
          return;
      }

      const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length === 0) {
          setBulkMembersMsg({ type: 'error', text: 'No CSV rows found.' });
          return;
      }

      const firstRow = parseCsvLine(lines[0]);
      const firstHeaders = firstRow.map(normalizeHeader);
      const hasHeader = firstHeaders.some(h => ['name', 'full name', 'member', 'role', 'type', 'interest group', 'gender', 'background', 'bio'].includes(h));

      let startIndex = 0;
      let colMap = { name: 0, role: 1, type: 2, gender: 3, background: 4 };

      if (hasHeader) {
          startIndex = 1;
          colMap = {
              name: firstHeaders.findIndex(h => ['name', 'full name', 'member'].includes(h)),
              role: firstHeaders.findIndex(h => h === 'role'),
              type: firstHeaders.findIndex(h => ['type', 'interest group', 'group'].includes(h)),
              gender: firstHeaders.findIndex(h => h === 'gender'),
              background: firstHeaders.findIndex(h => ['background', 'bio'].includes(h))
          };
      }

      let added = 0;
      let skipped = 0;

      for (let i = startIndex; i < lines.length; i++) {
          const row = parseCsvLine(lines[i]);
          const name = (colMap.name >= 0 ? row[colMap.name] : row[0]) || '';
          if (!name.trim()) {
              skipped++;
              continue;
          }

          const payload = {
              id: `${Date.now()}-${i}`,
              entityId: entity.id,
              name: name.trim(),
              role: ((colMap.role >= 0 ? row[colMap.role] : row[1]) || '').trim(),
              type: ((colMap.type >= 0 ? row[colMap.type] : row[2]) || '').trim(),
              gender: ((colMap.gender >= 0 ? row[colMap.gender] : row[3]) || '').trim(),
              background: ((colMap.background >= 0 ? row[colMap.background] : row[4]) || '').trim(),
          };

          try {
              await onAddMember(payload);
              added++;
          } catch {
              skipped++;
          }
      }

      setBulkMembersMsg({
          type: added > 0 ? 'success' : 'error',
          text: `Bulk import finished: ${added} added${skipped > 0 ? `, ${skipped} skipped` : ''}.`
      });

      if (added > 0) setBulkMembersCsv('');
  };

  const startEditMember = (member) => {
      setEditingMemberId(member.id);
      setEditMemberData({...member});
  };

  const cancelEditMember = () => {
      setEditingMemberId(null);
      setEditMemberData({});
  };

  const saveEditMember = () => {
      onUpdateMember(editMemberData);
      setEditingMemberId(null);
      setEditMemberData({});
  };

  const formatGenderDisplay = (rawGender) => {
      if (!rawGender) return 'Unknown';
      const g = String(rawGender).toLowerCase().trim();
      if (g === 'm' || g === 'male') return 'Male';
      if (g === 'f' || g === 'female') return 'Female';
      if (g === 'na' || g === 'n/a' || g === '-') return 'N/A';
      return 'Unknown';
  };

  const entityDocs = documents.filter(d => d.entityId === entity.id);
  const entityMembers = members.filter(m => m.entityId === entity.id);
    const countryOptions = useMemo(() => {
        const fromData = Array.from(new Set((entities || []).map((e) => String(e.country || '').trim()).filter(Boolean)));
        const currentCountry = String(formData.country || '').trim();
        const merged = Array.from(new Set([...WORLD_COUNTRIES, ...fromData, currentCountry].filter(Boolean)));
        return merged.sort((a, b) => a.localeCompare(b));
    }, [entities, formData.country]);
    const sameCountryEntities = (entities || [])
        .filter(e => e.id !== formData.id && e.country === formData.country)
        .sort((a, b) => (a.englishName || a.officialName || '').localeCompare(b.englishName || b.officialName || ''));

  return (
    <div className={`p-8 ${BRAND.light} min-h-screen`}>
      <div className="flex items-center justify-between gap-4 mb-8 border-b border-stone-300 pb-6">
        <div className="flex items-center gap-4">
             <button onClick={onBack} className="p-2 hover:bg-stone-200 rounded-full transition-colors">
              <ArrowLeft size={20} className="text-stone-600" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                 <h2 className="text-2xl font-bold text-stone-900 font-sans">
                    {formData.officialName || 'New Entity'}
                </h2>
                {formData.status === 'verified' && <div className="text-green-600 bg-green-50 px-2 py-0.5 rounded text-xs font-bold border border-green-200 flex items-center gap-1 uppercase tracking-wider"><CheckCircle size={14}/> Verified</div>}
                {formData.status === 'draft' && <div className="text-stone-500 bg-stone-100 px-2 py-0.5 rounded text-xs font-bold border border-stone-200 uppercase tracking-wider">Draft</div>}
              </div>
              <p className="text-stone-500 text-sm mt-1">{formData.country} • {formData.year}</p>
            </div>
        </div>
        
        {/* ACTION ROW */}
        {canEditEntity && (
            <div className="flex gap-3">
            {isEditMode ? (
                <>
                    {/* Only Admin can verify */}
                    {canVerify && (
                        <button 
                            onClick={() => handleStatusChange(formData.status === 'verified' ? 'review' : 'verified')}
                            className={`px-4 py-2 rounded-sm text-sm font-medium border transition-colors flex items-center gap-2 ${
                            formData.status === 'verified' 
                            ? 'bg-green-700 text-white border-green-700' 
                            : 'bg-white text-stone-600 border-stone-300 hover:border-green-600 hover:text-green-700'
                            }`}
                        >
                            <CheckCircle size={16} /> {formData.status === 'verified' ? 'Verified' : 'Verify'}
                        </button>
                    )}
                    <button 
                        onClick={() => onSave(formData)}
                        className={`${BRAND.primary} ${BRAND.primaryHover} text-white px-5 py-2 rounded-sm flex items-center gap-2 shadow-sm font-medium`}
                    >
                        <Save size={18} /> Save Changes
                    </button>
                </>
            ) : (
                <>
                {canVerify && onDeleteEntity && (
                    <button 
                        onClick={() => onDeleteEntity(entity.id)}
                        className="bg-white border border-red-200 text-red-600 hover:bg-red-50 px-5 py-2 rounded-sm flex items-center gap-2 shadow-sm font-medium transition-colors"
                        title="Delete Entity"
                    >
                        <Trash2 size={18} /> Delete
                    </button>
                )}
                <button 
                    onClick={onEdit}
                    className="bg-stone-800 hover:bg-stone-900 text-white px-5 py-2 rounded-sm flex items-center gap-2 shadow-sm font-medium"
                >
                    <Edit3 size={18} /> Edit Data
                </button>
                </>
            )}
            </div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-3">
          <nav className="flex flex-col space-y-1">
            {['general', 'details', 'members', 'docs'].map(tab => (
                <button 
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`text-left px-4 py-3 rounded-sm font-medium transition-colors flex items-center justify-between ${
                        activeTab === tab 
                        ? `bg-white ${BRAND.accent} border-l-4 border-green-600 shadow-sm` 
                        : 'text-stone-500 hover:bg-white hover:text-stone-800'
                    }`}
                >
                    <span className="capitalize">{tab === 'docs' ? 'Documents & Norms' : tab}</span>
                    {tab === 'docs' && entityDocs.length > 0 && (
                        <span className="bg-stone-200 text-stone-600 text-xs py-0.5 px-2 rounded-full">{entityDocs.length}</span>
                    )}
                     {tab === 'members' && entityMembers.length > 0 && (
                        <span className="bg-stone-200 text-stone-600 text-xs py-0.5 px-2 rounded-full">{entityMembers.length}</span>
                    )}
                </button>
            ))}
          </nav>
        </div>

        <div className="col-span-12 md:col-span-9">
          <div className="bg-white rounded-sm shadow-sm border border-stone-200 p-8 min-h-[500px]">
            {activeTab === 'general' && (
              <div className="space-y-6 max-w-3xl">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Country</label>
                    {isEditMode ? (
                        <select 
                        name="country" 
                        value={formData.country} 
                        onChange={handleChange}
                        className="w-full border border-stone-300 rounded-sm p-2.5 focus:ring-1 focus:ring-green-600 outline-none"
                        >
                        <option value="">Select Country</option>
                        {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    ) : (
                        <div className="text-lg text-stone-800">{formData.country}</div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Creation Year</label>
                    {isEditMode ? (
                        <input 
                        type="text" 
                        name="year" 
                        value={formData.year} 
                        onChange={handleChange}
                        className="w-full border border-stone-300 rounded-sm p-2.5 focus:ring-1 focus:ring-green-600 outline-none"
                        />
                    ) : (
                        <div className="text-lg text-stone-800 font-mono">{formData.year}</div>
                    )}
                  </div>
                </div>

                                <div>
                                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Last Edited</label>
                                    <div className="text-lg text-stone-800">{formatEntityDateTime(formData.lastEditedAt)}</div>
                                </div>

                <div className="pt-4">
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Official Name</label>
                  {isEditMode ? (
                    <input 
                        type="text" 
                        name="officialName" 
                        value={formData.officialName} 
                        onChange={handleChange}
                        className="w-full border border-stone-300 rounded-sm p-2.5 focus:ring-1 focus:ring-green-600 outline-none font-sans text-lg"
                    />
                   ) : (
                        <div className="text-xl text-stone-900 font-sans font-bold">{formData.officialName}</div>
                   )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">English Name</label>
                   {isEditMode ? (
                    <input 
                        type="text" 
                        name="englishName" 
                        value={formData.englishName} 
                        onChange={handleChange}
                        className="w-full border border-stone-300 rounded-sm p-2.5 focus:ring-1 focus:ring-green-600 outline-none"
                    />
                    ) : (
                        <div className="text-lg text-stone-700">{formData.englishName || '—'}</div>
                    )}
                </div>

                 <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Website</label>
                  {isEditMode ? (
                    <input 
                        type="url" 
                        name="website" 
                        value={formData.website} 
                        onChange={handleChange}
                        className="w-full border border-stone-300 rounded-sm p-2.5 focus:ring-1 focus:ring-green-600 outline-none text-blue-600"
                    />
                  ) : (
                    formData.website ? <a href={formData.website} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline flex items-center gap-1"><Globe size={14}/> {formData.website}</a> : <span className="text-stone-400">—</span>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Entity Type</label>
                  {isEditMode ? (
                    <select 
                        name="type" 
                        value={formData.type} 
                        onChange={handleChange}
                        className="w-full border border-stone-300 rounded-sm p-2.5 focus:ring-1 focus:ring-green-600 outline-none"
                    >
                        <option value="">Select Type</option>
                        {ENTITY_TYPE_OPTIONS.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                   ) : (
                        <div className="inline-block bg-stone-100 px-3 py-1 rounded-full text-sm font-medium text-stone-700">{formData.type}</div>
                   )}
                </div>

                                <div>
                                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Other information</label>
                                    {isEditMode ? (
                                        <textarea
                                            name="otherInfo"
                                            rows={4}
                                            value={formData.otherInfo || ''}
                                            onChange={handleChange}
                                            className="w-full border border-stone-300 rounded-sm p-3 focus:ring-1 focus:ring-green-600 outline-none text-sm leading-relaxed"
                                        />
                                    ) : (
                                        <div className="p-4 bg-stone-50 border border-stone-100 rounded-sm text-sm leading-relaxed text-stone-800 whitespace-pre-wrap">
                                            {formData.otherInfo || <span className="text-stone-400 italic">No information available.</span>}
                                        </div>
                                    )}
                                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="space-y-8">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider">Creation Norm / Legal Basis</label>
                    {isEditMode && (
                        <div className="flex gap-1">
                            <button type="button" onClick={() => handlePaste('creationNorm')} className="text-xs flex items-center gap-1 text-stone-500 hover:text-rose-700 bg-stone-100 px-2 py-1 rounded transition-colors">
                            <ClipboardPaste size={12} /> Paste
                            </button>
                            <button type="button" onClick={() => normalizeText('creationNorm')} className="text-xs flex items-center gap-1 text-stone-500 hover:text-rose-700 bg-stone-100 px-2 py-1 rounded transition-colors">
                            <Wand2 size={12} /> Clean
                            </button>
                        </div>
                    )}
                  </div>
                  {isEditMode ? (
                    <textarea 
                        name="creationNorm"
                        rows={3}
                        value={formData.creationNorm} 
                        onChange={handleChange}
                        className="w-full border border-stone-300 rounded-sm p-3 focus:ring-1 focus:ring-green-600 outline-none text-sm leading-relaxed"
                    />
                  ) : (
                    <div className="p-4 bg-stone-50 border border-stone-100 rounded-sm text-sm leading-relaxed text-stone-800 whitespace-pre-wrap">
                        {formData.creationNorm || <span className="text-stone-400 italic">No information available.</span>}
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider">Current Functions</label>
                    {isEditMode && (
                        <div className="flex gap-1">
                            <button type="button" onClick={() => handlePaste('functions')} className="text-xs flex items-center gap-1 text-stone-500 hover:text-rose-700 bg-stone-100 px-2 py-1 rounded transition-colors">
                            <ClipboardPaste size={12} /> Paste
                            </button>
                            <button type="button" onClick={() => normalizeText('functions')} className="text-xs flex items-center gap-1 text-stone-500 hover:text-rose-700 bg-stone-100 px-2 py-1 rounded transition-colors">
                            <Wand2 size={12} /> Clean
                            </button>
                        </div>
                    )}
                  </div>
                  {isEditMode ? (
                     <textarea 
                        name="functions"
                        rows={6}
                        value={formData.functions} 
                        onChange={handleChange}
                        className="w-full border border-stone-300 rounded-sm p-3 focus:ring-1 focus:ring-green-600 outline-none text-sm leading-relaxed"
                    />
                  ) : (
                    <div className="p-4 bg-stone-50 border border-stone-100 rounded-sm text-sm leading-relaxed text-stone-800 whitespace-pre-wrap">
                        {formData.functions || <span className="text-stone-400 italic">No information available.</span>}
                    </div>
                  )}
                </div>

                <div>
                   <div className="flex justify-between items-center mb-2">
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider">Composition Rule</label>
                    {isEditMode && (
                        <div className="flex gap-1">
                            <button type="button" onClick={() => handlePaste('compositionRule')} className="text-xs flex items-center gap-1 text-stone-500 hover:text-rose-700 bg-stone-100 px-2 py-1 rounded transition-colors">
                            <ClipboardPaste size={12} /> Paste
                            </button>
                            <button type="button" onClick={() => normalizeText('compositionRule')} className="text-xs flex items-center gap-1 text-stone-500 hover:text-rose-700 bg-stone-100 px-2 py-1 rounded transition-colors">
                            <Wand2 size={12} /> Clean
                            </button>
                        </div>
                    )}
                  </div>
                  {isEditMode ? (
                    <textarea 
                        name="compositionRule"
                        rows={6}
                        value={formData.compositionRule} 
                        onChange={handleChange}
                        className="w-full border border-stone-300 rounded-sm p-3 focus:ring-1 focus:ring-green-600 outline-none text-sm leading-relaxed"
                    />
                   ) : (
                    <div className="p-4 bg-stone-50 border border-stone-100 rounded-sm text-sm leading-relaxed text-stone-800 whitespace-pre-wrap">
                        {formData.compositionRule || <span className="text-stone-400 italic">No information available.</span>}
                    </div>
                   )}
                </div>

                                <div className="pt-2 border-t border-stone-100">
                                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Entity extinction status</label>
                                    {isEditMode ? (
                                        <label className="inline-flex items-center gap-2 text-sm text-stone-700">
                                            <input
                                                type="checkbox"
                                                name="isExtinct"
                                                checked={!!formData.isExtinct}
                                                onChange={handleChange}
                                                className="accent-green-700"
                                            />
                                            This entity is extinct
                                        </label>
                                    ) : (
                                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${formData.isExtinct ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                                            {formData.isExtinct ? 'Extinct' : 'Active'}
                                        </div>
                                    )}
                                </div>

                                {formData.isExtinct && (
                                    <>
                                        <div>
                                            <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Extinction year</label>
                                            {isEditMode ? (
                                                <input
                                                    type="text"
                                                    name="extinctionYear"
                                                    value={formData.extinctionYear || ''}
                                                    onChange={handleChange}
                                                    placeholder="e.g. 2019"
                                                    className="w-full border border-stone-300 rounded-sm p-2.5 focus:ring-1 focus:ring-green-600 outline-none"
                                                />
                                            ) : (
                                                <div className="text-sm text-stone-700">{formData.extinctionYear || <span className="text-stone-400">—</span>}</div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Succeeded by (same country)</label>
                                            {isEditMode ? (
                                                <select
                                                    name="succeededByEntityId"
                                                    value={formData.succeededByEntityId || ''}
                                                    onChange={handleChange}
                                                    className="w-full border border-stone-300 rounded-sm p-2.5 focus:ring-1 focus:ring-green-600 outline-none"
                                                >
                                                    <option value="">Select successor entity</option>
                                                    {sameCountryEntities.map(e => (
                                                        <option key={e.id} value={e.id}>{e.englishName || e.officialName}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <div className="text-sm text-stone-700">
                                                    {formData.succeededByEntityId
                                                        ? ((entities || []).find(e => e.id === formData.succeededByEntityId)?.englishName || (entities || []).find(e => e.id === formData.succeededByEntityId)?.officialName || formData.succeededByEntityId)
                                                        : <span className="text-stone-400">—</span>}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
              </div>
            )}

            {activeTab === 'members' && (
                <div>
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-sm font-bold text-stone-800 uppercase">Entity Composition</h3>
                        <span className="bg-stone-100 text-stone-500 text-xs px-2 py-1 rounded-full">Total: {entityMembers.length}</span>
                    </div>

                    <div className="space-y-3 mb-8">
                        {entityMembers.length === 0 ? (
                             <p className="text-stone-400 text-sm italic border p-4 rounded-sm border-dashed">No members registered for this entity.</p>
                        ) : (
                            <div className="border border-stone-200 rounded-sm overflow-hidden">
                                <table className="w-full text-left">
                                    <thead className="bg-stone-50 text-stone-500 text-xs font-bold uppercase">
                                        <tr>
                                            <th className="p-3">Name</th>
                                            <th className="p-3">Role</th>
                                            <th className="p-3">Interest Group</th>
                                            <th className="p-3">Gender</th>
                                            <th className="p-3">Background</th>
                                            {isEditMode && <th className="p-3 text-right">Action</th>}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100 text-sm">
                                        {entityMembers.map(m => {
                                            const isEditing = editingMemberId === m.id;
                                            return (
                                            <tr key={m.id} className="hover:bg-stone-50">
                                                {isEditing ? (
                                                    <>
                                                        <td className="p-2">
                                                            <input 
                                                                className="w-full border border-stone-300 p-1 rounded-sm text-xs"
                                                                value={editMemberData.name}
                                                                onChange={(e) => setEditMemberData({...editMemberData, name: e.target.value})}
                                                            />
                                                        </td>
                                                        <td className="p-2">
                                                            <input 
                                                                className="w-full border border-stone-300 p-1 rounded-sm text-xs"
                                                                value={editMemberData.role}
                                                                onChange={(e) => setEditMemberData({...editMemberData, role: e.target.value})}
                                                            />
                                                        </td>
                                                        <td className="p-2">
                                                            <select 
                                                                className="w-full border border-stone-300 p-1 rounded-sm text-xs"
                                                                value={editMemberData.type}
                                                                onChange={(e) => setEditMemberData({...editMemberData, type: e.target.value})}
                                                            >
                                                                <option value="">Select...</option>
                                                                <option value="Academic">Academic</option>
                                                                <option value="Expert">Expert</option>
                                                                <option value="Public Authority">Public Authority</option>
                                                                <option value="Civil Society">Civil Society</option>
                                                                <option value="Industry">Industry</option>
                                                            </select>
                                                        </td>
                                                        <td className="p-2">
                                                            <select 
                                                                className="w-full border border-stone-300 p-1 rounded-sm text-xs"
                                                                value={editMemberData.gender}
                                                                onChange={(e) => setEditMemberData({...editMemberData, gender: e.target.value})}
                                                            >
                                                                <option value="">Select...</option>
                                                                <option value="Male">Male</option>
                                                                <option value="Female">Female</option>
                                                                <option value="N/A">N/A</option>
                                                            </select>
                                                        </td>
                                                        <td className="p-2">
                                                            <input 
                                                                className="w-full border border-stone-300 p-1 rounded-sm text-xs"
                                                                value={editMemberData.background}
                                                                onChange={(e) => setEditMemberData({...editMemberData, background: e.target.value})}
                                                            />
                                                        </td>
                                                        <td className="p-2 text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <button onClick={saveEditMember} className="text-green-600 hover:text-green-800 p-1"><Save size={16} /></button>
                                                                <button onClick={cancelEditMember} className="text-stone-400 hover:text-stone-600 p-1"><Trash2 size={16} /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="p-3 font-medium text-stone-800">{m.name}</td>
                                                        <td className="p-3 text-stone-600">{m.role}</td>
                                                        <td className="p-3 text-stone-500">{m.type}</td>
                                                        <td className="p-3 text-stone-500">{formatGenderDisplay(m.gender)}</td>
                                                        <td className="p-3 text-stone-500 truncate max-w-[150px]" title={m.background}>{m.background}</td>
                                                        {isEditMode && (
                                                            <td className="p-3 text-right">
                                                                <div className="flex justify-end gap-1">
                                                                    <button 
                                                                        onClick={() => startEditMember(m)}
                                                                        className="text-stone-400 hover:text-stone-600 p-1"
                                                                    >
                                                                        <Edit3 size={16} />
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => onDeleteMember(m.id)}
                                                                        className="text-red-400 hover:text-red-600 p-1"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </>
                                                )}
                                            </tr>
                                        )})}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Only show add new member form if editing */}
                    {isEditMode && (
                        <div className="bg-stone-50 border border-stone-200 p-6 rounded-sm">
                             <h3 className="text-sm font-bold text-stone-800 uppercase mb-4 flex items-center gap-2">
                                <UserPlus size={16}/> Add New Member
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                                <input 
                                    type="text" placeholder="Full Name"
                                    className="border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                    value={newMember.name}
                                    onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                                />
                                <input 
                                    type="text" placeholder="Role (e.g. Chair)"
                                    className="border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                    value={newMember.role}
                                    onChange={(e) => setNewMember({...newMember, role: e.target.value})}
                                />
                                <select 
                                    className="border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                    value={newMember.type}
                                    onChange={(e) => setNewMember({...newMember, type: e.target.value})}
                                >
                                    <option value="">Interest Group...</option>
                                    <option value="Academic">Academic</option>
                                    <option value="Expert">Expert</option>
                                    <option value="Public Authority">Public Authority</option>
                                    <option value="Civil Society">Civil Society</option>
                                    <option value="Industry">Industry</option>
                                </select>
                                <select 
                                    className="border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                    value={newMember.gender}
                                    onChange={(e) => setNewMember({...newMember, gender: e.target.value})}
                                >
                                    <option value="">Gender...</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="N/A">N/A</option>
                                </select>
                                <input 
                                    type="text" placeholder="Background / Bio"
                                    className="border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-green-600 outline-none"
                                    value={newMember.background}
                                    onChange={(e) => setNewMember({...newMember, background: e.target.value})}
                                />
                            </div>
                            <button 
                                onClick={handleAddMemberSubmit}
                                className={`${BRAND.secondary} text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-black transition-colors w-full md:w-auto`}
                            >
                                Add Member
                            </button>

                            <div className="mt-6 pt-5 border-t border-stone-200">
                                <h4 className="text-xs font-bold text-stone-700 uppercase mb-2">Bulk add from CSV paste</h4>
                                <p className="text-xs text-stone-500 mb-3">Header supported: Name, Role, Type, Gender, Background (or no header in that same order).</p>
                                <textarea
                                    rows={7}
                                    value={bulkMembersCsv}
                                    onChange={(e) => setBulkMembersCsv(e.target.value)}
                                    placeholder={`Name,Role,Type,Gender,Background\nJane Doe,Chair,Academic,F,Professor of AI\nJohn Roe,Member,Industry,M,CTO`}
                                    className="w-full border border-stone-300 rounded-sm p-3 text-sm focus:ring-1 focus:ring-green-600 outline-none font-mono"
                                />
                                {bulkMembersMsg && (
                                    <div className={`mt-3 p-2 rounded-sm text-xs ${bulkMembersMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {bulkMembersMsg.text}
                                    </div>
                                )}
                                <button
                                    onClick={handleBulkMembersImport}
                                    className={`${BRAND.secondary} text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-black transition-colors mt-3 w-full md:w-auto`}
                                >
                                    Import Members from CSV
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'docs' && (
                <div className="space-y-8">
                    <div>
                        <h3 className="text-sm font-bold text-stone-800 uppercase mb-4">Existing Documents</h3>
                        {docActionMsg && (
                            <div className={`mb-3 p-3 rounded-sm text-sm ${docActionMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {docActionMsg.text}
                            </div>
                        )}
                        {entityDocs.length === 0 ? (
                            <p className="text-stone-400 text-sm italic border p-4 rounded-sm border-dashed">No documents archived for this entity.</p>
                        ) : (
                            <ul className="space-y-2">
                                {entityDocs.map(doc => (
                                    <li key={doc.id} className="p-3 border border-stone-200 rounded-sm hover:bg-stone-50 transition-colors group">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                            <div className="bg-green-50 text-green-800 p-2 rounded-sm">
                                                <FileIcon size={18} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-stone-800 font-medium text-sm">{doc.display_name}</p>
                                                <p className="text-xs text-stone-400">{doc.type} • {doc.date} • {doc.size}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <a 
                                                href={`${UPLOADS_URL}/${doc.name}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className={`text-stone-400 hover:${BRAND.accent} p-2`}
                                                title="Download / Open"
                                            >
                                                <Download size={18} />
                                            </a>
                                        </div>
                                        </div>

                                        {isEditMode && (
                                            <div className="mt-3 pt-3 border-t border-stone-100 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_auto] gap-3 items-end">
                                                <div>
                                                    <label className="block text-xs font-bold text-stone-500 mb-1">Document Type</label>
                                                    <select
                                                        className="w-full border border-stone-300 rounded-sm p-2 text-sm"
                                                        value={docTypeOverrides[doc.id] ?? doc.type}
                                                        onChange={(e) => setDocTypeOverrides(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                                    >
                                                        {DOCUMENT_TYPE_OPTIONS.map(typeOption => (
                                                            <option key={typeOption} value={typeOption}>{typeOption}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <label className={`${BRAND.secondary} text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-black transition-colors cursor-pointer text-center ${isReplacingDocId === doc.id ? 'opacity-60 cursor-wait' : ''}`}>
                                                    {isReplacingDocId === doc.id ? 'Replacing...' : 'Replace File'}
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        disabled={isReplacingDocId === doc.id}
                                                        onChange={(e) => {
                                                            const selectedFile = e.target.files?.[0];
                                                            handleReplaceFileSelected(doc, selectedFile);
                                                            e.target.value = '';
                                                        }}
                                                    />
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteDocumentClick(doc)}
                                                    className="px-4 py-2 rounded-sm text-sm font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Only show upload form if editing */}
                    {isEditMode && (
                        <div className="bg-stone-50 border border-stone-200 p-6 rounded-sm">
                        <h3 className="text-sm font-bold text-stone-800 uppercase mb-4 flex items-center gap-2">
                            <Upload size={16}/> Bulk Document Upload
                        </h3>

                        {/* DROPZONE AREA */}
                        <div className="mb-4">
                            <div 
                                {...getRootProps()} 
                                className={`border-2 border-dashed p-6 rounded-sm text-center cursor-pointer transition-colors ${
                                    isDragActive 
                                    ? 'border-rose-500 bg-rose-50' 
                                    : 'border-stone-300 bg-white hover:border-stone-400'
                                }`}
                            >
                                <input {...getInputProps()} />
                                {isDragActive ? (
                                    <p className="text-stone-500">Drop the files here...</p>
                                ) : (
                                    <div className="text-stone-500">
                                        <Upload className="mx-auto mb-2 text-stone-400" size={24} />
                                        <p className="text-sm">Drag & drop files here, or click to select multiple</p>
                                        <p className="text-xs text-stone-400 mt-1">After selecting, set the type for each document below.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {uploadQueue.length > 0 && (
                            <div className="mb-4 space-y-3">
                                {uploadQueue.map(item => (
                                    <div key={item.localId} className="border border-stone-200 rounded-sm bg-white p-3">
                                        <div className="flex items-center justify-between gap-3 mb-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <FileIcon size={16} className="text-stone-500 shrink-0" />
                                                <p className="text-xs text-stone-700 truncate" title={item.file.name}>{item.file.name}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeUploadItem(item.localId)}
                                                className="text-stone-400 hover:text-red-600 transition-colors"
                                                title="Remove file"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-bold text-stone-500 mb-1">Document Type</label>
                                                <select
                                                    className="w-full border border-stone-300 rounded-sm p-2 text-sm"
                                                    value={item.type}
                                                    onChange={(e) => updateUploadItem(item.localId, 'type', e.target.value)}
                                                >
                                                    {DOCUMENT_TYPE_OPTIONS.map(typeOption => (
                                                        <option key={typeOption} value={typeOption}>{typeOption}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-stone-500 mb-1">Display Name (Required)</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. Royal Decree 24/2023"
                                                    className="w-full border border-stone-300 rounded-sm p-2 text-sm focus:ring-1 focus:ring-rose-800 outline-none"
                                                    value={item.displayName}
                                                    onChange={(e) => updateUploadItem(item.localId, 'displayName', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {bulkUploadMsg && (
                            <div className={`mb-4 p-3 rounded-sm text-sm ${bulkUploadMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {bulkUploadMsg.text}
                            </div>
                        )}

                        <div className="flex justify-end">
                            <button 
                                onClick={handleUploadSubmit}
                                disabled={uploadQueue.length === 0 || isBulkUploading}
                                className={`${BRAND.secondary} text-white px-6 py-2 rounded-sm text-sm font-medium hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                {isBulkUploading ? 'Uploading...' : `Upload ${uploadQueue.length} Document${uploadQueue.length === 1 ? '' : 's'}`}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// 6. MEMBER LIST
const MembersView = ({ members, entities }) => {
    const [filter, setFilter] = useState('');

    const filteredMembers = useMemo(() => {
        return members
            .filter(m => 
                m.name.toLowerCase().includes(filter.toLowerCase()) ||
                m.type.toLowerCase().includes(filter.toLowerCase())
            )
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [members, filter]);

    return (
        <div className={`p-8 ${BRAND.light} min-h-screen`}>
            <div className="flex justify-between items-center mb-6 border-b border-stone-300 pb-4">
                 <h2 className="text-3xl font-bold text-stone-900 font-sans">Network Members</h2>
                 <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                    <input 
                        type="text" 
                        placeholder="Search members or groups..." 
                        className="pl-9 pr-4 py-2 border border-stone-300 rounded-sm text-sm focus:ring-1 focus:ring-green-600 outline-none"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                 </div>
            </div>
            
            <div className="bg-white rounded-sm shadow-sm border border-stone-200 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-stone-100 text-stone-600 text-xs font-bold uppercase tracking-wider">
                        <tr>
                            <th className="p-4 border-b border-stone-200">Name</th>
                            <th className="p-4 border-b border-stone-200">Role</th>
                            <th className="p-4 border-b border-stone-200">Interest Group</th>
                            <th className="p-4 border-b border-stone-200">Entity / Country</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100 text-sm">
                        {filteredMembers.map(member => {
                            const entity = entities.find(e => e.id === member.entityId);
                            return (
                                <tr key={member.id} className="hover:bg-stone-50">
                                    <td className="p-4 font-bold text-stone-800">{member.name}</td>
                                    <td className="p-4 text-stone-600">{member.role}</td>
                                    <td className="p-4">
                                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                            member.type === 'Academic' ? 'bg-blue-50 text-blue-700' :
                                            member.type === 'Public Authority' ? 'bg-stone-100 text-stone-700' :
                                            member.type === 'Industry' ? 'bg-amber-50 text-amber-700' :
                                            'bg-green-50 text-green-700'
                                        }`}>
                                            {member.type}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <div className="font-medium text-stone-900">{entity?.officialName}</div>
                                        <div className="text-xs text-stone-500">{entity?.country}</div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                 {filteredMembers.length === 0 && (
                     <div className="p-8 text-center text-stone-400">No members found.</div>
                 )}
            </div>
        </div>
    );
};

// --- AUTHENTICATION ---
const Login = ({ onLogin }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Login failed');
            onLogin(data.token, data.refreshToken, data.user);
        } catch (e) {
            setError(e.message);
        }
    };

    return (
        <div className="flex flex-col h-screen items-center justify-center bg-stone-100 p-4">
             <div className="text-center mb-8">
                <div className="flex flex-col items-center gap-2">
                    <QDemLogo className="h-20 w-auto" />
                    <span className="block text-4xl font-sans font-bold tracking-tight text-[#74c476] leading-tight">Data</span>
                </div>
            </div>
            <div className="bg-white p-8 rounded shadow-md w-full max-w-sm border border-stone-200">
                <h2 className="text-xl font-bold mb-6 text-center text-stone-700">Welcome</h2>
                {error && <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-xs">{error}</div>}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold uppercase text-stone-500 mb-1">Username</label>
                        <input 
                            type="text" 
                            className="w-full p-2 border border-stone-300 rounded focus:border-[#74c476] outline-none text-sm"
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase text-stone-500 mb-1">Password</label>
                        <input 
                            type="password" 
                            className="w-full p-2 border border-stone-300 rounded focus:border-[#74c476] outline-none text-sm"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                    </div>
                    <button 
                        type="submit" 
                        className="w-full bg-[#74c476] text-white py-2 rounded hover:bg-[#5aa85c] transition-colors font-medium text-sm"
                    >
                        Sign In
                    </button>
                    <button
                        type="button"
                        onClick={() => onLogin(null, null, { role: 'viewer', name: 'Visitor', allowedDatabases: [] })}
                        className="w-full text-stone-400 hover:text-stone-600 transition-colors font-medium text-xs mt-4"
                    >
                         Explore public databases (read only)
                    </button>
                </form>
            </div>
        </div>
    );
};

const OralQuestionsWorkspace = ({ user, onLogout, onBackToHub }) => {
    const [view, setView] = useState('dashboard');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState(null);
    const [filters, setFilters] = useState({
        arenas: [],
        results: [],
        parties: [],
        legislatures: [],
        presidents: [],
        questionTypes: [],
        authorGenders: [],
        parliamentaryGroups: [],
        topics: [],
        commissions: [],
        daysOfWeek: []
    });
    const [search, setSearch] = useState('');
    const [arena, setArena] = useState('');
    const [result, setResult] = useState('');
    const [party, setParty] = useState('');
    const [legislature, setLegislature] = useState('');
    const [president, setPresident] = useState('');
    const [questionType, setQuestionType] = useState('');
    const [authorGender, setAuthorGender] = useState('');
    const [parliamentaryGroup, setParliamentaryGroup] = useState('');
    const [topic, setTopic] = useState('');
    const [commission, setCommission] = useState('');
    const [dayOfWeek, setDayOfWeek] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [isEditingQuestion, setIsEditingQuestion] = useState(false);
    const [editingQuestionDraft, setEditingQuestionDraft] = useState(null);
    const [savingQuestion, setSavingQuestion] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
    const [authors, setAuthors] = useState([]);
    const [authorSearch, setAuthorSearch] = useState('');
    const [authorsPage, setAuthorsPage] = useState(1);
    const [authorsPageSize] = useState(50);
    const [authorsMeta, setAuthorsMeta] = useState({ total: 0, totalPages: 1 });
    const [selectedAuthor, setSelectedAuthor] = useState('');
    const [authorQuestions, setAuthorQuestions] = useState([]);
    const [authorQuestionsPage, setAuthorQuestionsPage] = useState(1);
    const [authorQuestionsMeta, setAuthorQuestionsMeta] = useState({ total: 0, totalPages: 1 });
    const [authorResultFilter, setAuthorResultFilter] = useState('');
    const [authorTopicFilter, setAuthorTopicFilter] = useState('');
    const [authorArenaFilter, setAuthorArenaFilter] = useState('');
    const [authorLegislatureFilter, setAuthorLegislatureFilter] = useState('');
    const [authorFacets, setAuthorFacets] = useState({ results: [], topics: [], arenas: [], legislatures: [] });
    const [authorTopicDistribution, setAuthorTopicDistribution] = useState([]);
    const [parliamentaryGroupsList, setParliamentaryGroupsList] = useState([]);
    const [groupSearch, setGroupSearch] = useState('');
    const [groupsPage, setGroupsPage] = useState(1);
    const [groupsPageSize] = useState(50);
    const [groupsMeta, setGroupsMeta] = useState({ total: 0, totalPages: 1 });
    const [selectedParliamentaryGroup, setSelectedParliamentaryGroup] = useState('');
    const [groupQuestions, setGroupQuestions] = useState([]);
    const [groupQuestionsPage, setGroupQuestionsPage] = useState(1);
    const [groupQuestionsMeta, setGroupQuestionsMeta] = useState({ total: 0, totalPages: 1 });
    const [groupResultFilter, setGroupResultFilter] = useState('');
    const [groupTopicFilter, setGroupTopicFilter] = useState('');
    const [groupArenaFilter, setGroupArenaFilter] = useState('');
    const [groupLegislatureFilter, setGroupLegislatureFilter] = useState('');
    const [groupFacets, setGroupFacets] = useState({ results: [], topics: [], arenas: [], legislatures: [] });
    const [groupTopicDistribution, setGroupTopicDistribution] = useState([]);
    const [trendDimension, setTrendDimension] = useState('topic');
    const [trendArena, setTrendArena] = useState('');
    const [trendSeriesCount, setTrendSeriesCount] = useState(5);
    const [trendValueMode, setTrendValueMode] = useState('percent');
    const [trendRows, setTrendRows] = useState([]);
    const [importMode, setImportMode] = useState('append');
    const [importMsg, setImportMsg] = useState(null);

    const isAdmin = user?.role === 'admin';
    const displayUserName = [user?.name, user?.surname].filter(Boolean).join(' ') || user?.fullName || user?.username || 'User';
    const questionColumns = [
        { key: 'date_presentation', label: 'Date' },
        { key: 'question_reference', label: 'Reference' },
        { key: 'question', label: 'Question' },
        { key: 'author', label: 'Author(s)' },
        { key: 'standardized_groups', label: 'Parliamentary Group(s)' },
        { key: 'cap_topic', label: 'Topic' },
        { key: 'arena', label: 'Arena' }
    ];

    const questionDetailSections = [
        {
            title: 'Context',
            fields: [
                { key: 'date_presentation', label: 'Date' },
                { key: 'day_of_week', label: 'Day of Week', normalize: dayOfWeekLabel },
                { key: 'legislature', label: 'Legislature' },
                { key: 'legislature_code', label: 'Legislature Code' },
                { key: 'legislature_number', label: 'Legislature Number' },
                { key: 'president_of_the_government', label: 'President' },
                { key: 'arena', label: 'Arena' },
                { key: 'competent_commission', label: 'Competent Commission' }
            ]
        },
        {
            title: 'Actors',
            fields: [
                { key: 'author', label: 'Author(s)' },
                { key: 'author_gender', label: 'Author Gender' },
                { key: 'grupos_estandarizados', label: 'Parliamentary Group' },
                { key: 'parliamentary_group', label: 'Parliamentary Group (Original)' },
                { key: 'party_within_grupo_mixto', label: 'Party within Grupo Mixto' }
            ]
        },
        {
            title: 'Topic',
            fields: [
                { key: 'cap_topic_code', label: 'Topic Code' },
                { key: 'cap_topic', label: 'Topic' },
                { key: 'cap_subtopic_code', label: 'Subtopic Code' },
                { key: 'cap_subtopic', label: 'Subtopic' }
            ]
        }
    ];

    const fetchDashboard = useCallback(async () => {
        const [statsRes, filtersRes] = await Promise.all([
            authFetch(`${API_URL}/oral-questions/dashboard`),
            authFetch(`${API_URL}/oral-questions/filters`)
        ]);

        if (statsRes.ok) {
            const data = await statsRes.json();
            setStats(data);
        }
        if (filtersRes.ok) {
            const data = await filtersRes.json();
            setFilters({
                arenas: Array.isArray(data?.arenas) ? data.arenas : [],
                results: Array.isArray(data?.results) ? data.results : [],
                parties: Array.isArray(data?.parties) ? data.parties : [],
                legislatures: Array.isArray(data?.legislatures) ? data.legislatures : [],
                presidents: Array.isArray(data?.presidents) ? data.presidents : [],
                questionTypes: Array.isArray(data?.questionTypes) ? data.questionTypes : [],
                authorGenders: Array.isArray(data?.authorGenders) ? data.authorGenders : [],
                parliamentaryGroups: Array.isArray(data?.parliamentaryGroups) ? data.parliamentaryGroups : [],
                topics: Array.isArray(data?.topics) ? data.topics.filter(v => !isHiddenTopicValue(v, '')) : [],
                commissions: Array.isArray(data?.commissions) ? data.commissions : [],
                daysOfWeek: Array.isArray(data?.daysOfWeek) ? data.daysOfWeek : []
            });
        }
    }, []);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize)
            });
            if (search.trim()) params.set('search', search.trim());
            if (arena) params.set('arena', arena);
            if (result) params.set('result', result);
            if (party) params.set('party', party);
            if (legislature) params.set('legislature', legislature);
            if (president) params.set('president', president);
            if (questionType) params.set('questionType', questionType);
            if (authorGender) params.set('authorGender', authorGender);
            if (parliamentaryGroup) params.set('parliamentaryGroup', parliamentaryGroup);
            if (topic) params.set('topic', topic);
            if (commission) params.set('commission', commission);
            if (dayOfWeek) params.set('dayOfWeek', dayOfWeek);
            if (dateFrom) params.set('dateFrom', dateFrom);
            if (dateTo) params.set('dateTo', dateTo);

            const res = await authFetch(`${API_URL}/oral-questions?${params.toString()}`);
            if (!res.ok) throw new Error('Failed to load oral questions');
            const data = await res.json();
            setRows(Array.isArray(data?.rows) ? data.rows : []);
            setMeta({ total: Number(data?.total || 0), totalPages: Number(data?.totalPages || 1) });
        } catch (e) {
            setRows([]);
            setMeta({ total: 0, totalPages: 1 });
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, search, arena, result, party, legislature, president, questionType, authorGender, parliamentaryGroup, topic, commission, dayOfWeek, dateFrom, dateTo]);

    const fetchAuthors = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(authorsPage),
                pageSize: String(authorsPageSize)
            });
            if (authorSearch.trim()) params.set('search', authorSearch.trim());

            const res = await authFetch(`${API_URL}/oral-questions/authors?${params.toString()}`);
            if (!res.ok) throw new Error('Failed to load authors');
            const data = await res.json();
            setAuthors(Array.isArray(data?.rows) ? data.rows : []);
            setAuthorsMeta({ total: Number(data?.total || 0), totalPages: Number(data?.totalPages || 1) });
        } catch {
            setAuthors([]);
            setAuthorsMeta({ total: 0, totalPages: 1 });
        } finally {
            setLoading(false);
        }
    }, [authorsPage, authorsPageSize, authorSearch]);

    const fetchTrendRows = useCallback(async () => {
        try {
            const params = new URLSearchParams({ dimension: trendDimension });
            if (trendArena) params.set('arena', trendArena);
            const res = await authFetch(`${API_URL}/oral-questions/trends?${params.toString()}`);
            if (!res.ok) throw new Error('Failed to load trends');
            const data = await res.json();
            setTrendRows(Array.isArray(data?.rows) ? data.rows : []);
        } catch {
            setTrendRows([]);
        }
    }, [trendDimension, trendArena]);

    const fetchParliamentaryGroups = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(groupsPage),
                pageSize: String(groupsPageSize)
            });
            if (groupSearch.trim()) params.set('search', groupSearch.trim());

            const res = await authFetch(`${API_URL}/oral-questions/parliamentary-groups?${params.toString()}`);
            if (!res.ok) throw new Error('Failed to load parliamentary groups');
            const data = await res.json();
            setParliamentaryGroupsList(Array.isArray(data?.rows) ? data.rows : []);
            setGroupsMeta({ total: Number(data?.total || 0), totalPages: Number(data?.totalPages || 1) });
        } catch {
            setParliamentaryGroupsList([]);
            setGroupsMeta({ total: 0, totalPages: 1 });
        } finally {
            setLoading(false);
        }
    }, [groupsPage, groupsPageSize, groupSearch]);

    const fetchGroupQuestions = useCallback(async () => {
        if (!selectedParliamentaryGroup) {
            setGroupQuestions([]);
            setGroupQuestionsMeta({ total: 0, totalPages: 1 });
            setGroupFacets({ results: [], topics: [], arenas: [], legislatures: [] });
            setGroupTopicDistribution([]);
            return;
        }

        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(groupQuestionsPage),
                pageSize: '25'
            });
            if (groupResultFilter) params.set('result', groupResultFilter);
            if (groupTopicFilter) params.set('topic', groupTopicFilter);
            if (groupArenaFilter) params.set('arena', groupArenaFilter);
            if (groupLegislatureFilter) params.set('legislature', groupLegislatureFilter);
            const encodedGroup = encodeURIComponent(selectedParliamentaryGroup);
            const res = await authFetch(`${API_URL}/oral-questions/parliamentary-groups/${encodedGroup}/questions?${params.toString()}`);
            if (!res.ok) throw new Error('Failed to load group questions');
            const data = await res.json();
            setGroupQuestions(Array.isArray(data?.rows) ? data.rows : []);
            setGroupQuestionsMeta({ total: Number(data?.total || 0), totalPages: Number(data?.totalPages || 1) });
            setGroupFacets({
                results: Array.isArray(data?.facets?.results) ? data.facets.results : [],
                topics: Array.isArray(data?.facets?.topics) ? data.facets.topics : [],
                arenas: Array.isArray(data?.facets?.arenas) ? data.facets.arenas : [],
                legislatures: Array.isArray(data?.facets?.legislatures) ? data.facets.legislatures : []
            });
            setGroupTopicDistribution(Array.isArray(data?.topicDistribution) ? data.topicDistribution : []);
        } catch {
            setGroupQuestions([]);
            setGroupQuestionsMeta({ total: 0, totalPages: 1 });
            setGroupFacets({ results: [], topics: [], arenas: [], legislatures: [] });
            setGroupTopicDistribution([]);
        } finally {
            setLoading(false);
        }
    }, [selectedParliamentaryGroup, groupQuestionsPage, groupResultFilter, groupTopicFilter, groupArenaFilter, groupLegislatureFilter]);

    const fetchAuthorQuestions = useCallback(async () => {
        if (!selectedAuthor) {
            setAuthorQuestions([]);
            setAuthorQuestionsMeta({ total: 0, totalPages: 1 });
            setAuthorFacets({ results: [], topics: [], arenas: [], legislatures: [] });
            return;
        }

        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(authorQuestionsPage),
                pageSize: '25'
            });
            if (authorResultFilter) params.set('result', authorResultFilter);
            if (authorTopicFilter) params.set('topic', authorTopicFilter);
            if (authorArenaFilter) params.set('arena', authorArenaFilter);
            if (authorLegislatureFilter) params.set('legislature', authorLegislatureFilter);
            const encodedAuthor = encodeURIComponent(selectedAuthor);
            const res = await authFetch(`${API_URL}/oral-questions/authors/${encodedAuthor}/questions?${params.toString()}`);
            if (!res.ok) throw new Error('Failed to load author questions');
            const data = await res.json();
            setAuthorQuestions(Array.isArray(data?.rows) ? data.rows : []);
            setAuthorQuestionsMeta({ total: Number(data?.total || 0), totalPages: Number(data?.totalPages || 1) });
            setAuthorFacets({
                results: Array.isArray(data?.facets?.results) ? data.facets.results : [],
                topics: Array.isArray(data?.facets?.topics) ? data.facets.topics : [],
                arenas: Array.isArray(data?.facets?.arenas) ? data.facets.arenas : [],
                legislatures: Array.isArray(data?.facets?.legislatures) ? data.facets.legislatures : []
            });
            setAuthorTopicDistribution(Array.isArray(data?.topicDistribution) ? data.topicDistribution : []);
        } catch {
            setAuthorQuestions([]);
            setAuthorQuestionsMeta({ total: 0, totalPages: 1 });
            setAuthorFacets({ results: [], topics: [], arenas: [], legislatures: [] });
            setAuthorTopicDistribution([]);
        } finally {
            setLoading(false);
        }
    }, [selectedAuthor, authorQuestionsPage, authorResultFilter, authorTopicFilter, authorArenaFilter, authorLegislatureFilter]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    useEffect(() => {
        if (view === 'questions') {
            fetchRows();
        }
    }, [view, fetchRows]);

    useEffect(() => {
        if (view === 'authors') {
            fetchAuthors();
        }
    }, [view, fetchAuthors]);

    useEffect(() => {
        if (view === 'groups') {
            fetchParliamentaryGroups();
        }
    }, [view, fetchParliamentaryGroups]);

    useEffect(() => {
        if (view === 'authors' && selectedAuthor) {
            fetchAuthorQuestions();
        }
    }, [view, selectedAuthor, fetchAuthorQuestions]);

    useEffect(() => {
        if (view === 'groups' && selectedParliamentaryGroup) {
            fetchGroupQuestions();
        }
    }, [view, selectedParliamentaryGroup, fetchGroupQuestions]);

    useEffect(() => {
        if (view === 'dashboard') {
            fetchTrendRows();
        }
    }, [view, fetchTrendRows]);

    const handleImportCsv = async (file) => {
        if (!file) return;
        setImportMsg(null);
        setLoading(true);
        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: 'array', raw: false });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const parsedRows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false })
                .map((row, index) => ({ ...row, __rowNumber: index + 2 }));

            if (!parsedRows.length) throw new Error('CSV without valid rows.');

            const CHUNK_SIZE = 1000;
            let importedTotal = 0;

            for (let i = 0; i < parsedRows.length; i += CHUNK_SIZE) {
                const chunk = parsedRows.slice(i, i + CHUNK_SIZE);
                const mode = i === 0 ? importMode : 'append';

                const res = await authFetch(`${API_URL}/oral-questions/bulk`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ rows: chunk, mode })
                });

                const payload = await res.json();
                if (!res.ok) throw new Error(payload?.error || 'Import failed');

                importedTotal += Number(payload?.imported || chunk.length);
                setImportMsg({ type: 'success', text: `Importing... ${Math.min(i + CHUNK_SIZE, parsedRows.length)}/${parsedRows.length}` });
            }

            setImportMsg({ type: 'success', text: `Imported ${importedTotal} of ${parsedRows.length} parsed rows.` });
            fetchDashboard();
            if (view === 'questions') fetchRows();
        } catch (e) {
            setImportMsg({ type: 'error', text: e.message || 'Import failed' });
        } finally {
            setLoading(false);
        }
    };

    const handleExportCsv = async () => {
        try {
            const res = await authFetch(`${API_URL}/oral-questions/export`);
            if (!res.ok) throw new Error('Export failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `oral_questions_export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(e.message || 'Export failed');
        }
    };

    const handleExportXlsx = async () => {
        try {
            const res = await authFetch(`${API_URL}/oral-questions/export-xlsx`);
            if (!res.ok) throw new Error('Export failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `oral_questions_export_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(e.message || 'Export failed');
        }
    };

    const handleExportOriginal = async () => {
        try {
            const res = await authFetch(`${API_URL}/oral-questions/export-original`);
            if (!res.ok) throw new Error('Export failed');
            const contentDisposition = res.headers.get('content-disposition') || '';
            const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
            let resolvedFilename = `oral_questions_original_${new Date().toISOString().split('T')[0]}.sql`;
            if (filenameMatch?.[1]) {
                try {
                    resolvedFilename = decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim());
                } catch {
                    resolvedFilename = filenameMatch[1].replace(/"/g, '').trim() || resolvedFilename;
                }
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = resolvedFilename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(e.message || 'Export failed');
        }
    };

    const openQuestionDetail = useCallback(async (questionId, fallbackRow = null) => {
        const normalizedId = String(questionId || '').trim();
        if (!normalizedId) return;
        setIsEditingQuestion(false);
        setEditingQuestionDraft(null);
        try {
            const res = await authFetch(`${API_URL}/oral-questions/item/${encodeURIComponent(normalizedId)}`);
            if (!res.ok) throw new Error('Failed to load question detail');
            const data = await res.json();
            setSelectedQuestion(data?.row || fallbackRow || null);
        } catch {
            setSelectedQuestion(fallbackRow || null);
        }
        setView('question_detail');
    }, []);

    const handleEditQuestion = () => {
        if (!selectedQuestion) return;
        setEditingQuestionDraft({ ...selectedQuestion });
        setIsEditingQuestion(true);
    };

    const handleSaveQuestion = async () => {
        if (!editingQuestionDraft?.id) return;
        setSavingQuestion(true);
        try {
            const res = await authFetch(`${API_URL}/oral-questions/item/${encodeURIComponent(editingQuestionDraft.id)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editingQuestionDraft)
            });
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                throw new Error(payload?.error || 'Failed to save question');
            }
            const data = await res.json();
            setSelectedQuestion(data?.row || editingQuestionDraft);
            setIsEditingQuestion(false);
            setEditingQuestionDraft(null);
        } catch (e) {
            alert(e.message || 'Failed to save question');
        } finally {
            setSavingQuestion(false);
        }
    };

    const normalizedByResult = useMemo(() => aggregateByNormalizedResult(stats?.byResult || []), [stats?.byResult]);
    const visibleTopics = useMemo(
        () => (stats?.topTopics || []).filter(item => !isHiddenTopicValue(item?.topic, item?.cap_topic_code)),
        [stats?.topTopics]
    );
    const topTopic = visibleTopics[0] || null;
    const arenaChartData = useMemo(
        () => (stats?.byArena || []).slice(0, 10).map(item => ({ name: item?.arena || 'N/A', count: Number(item?.count || 0) })),
        [stats?.byArena]
    );
    const resultChartData = useMemo(
        () => normalizedByResult.slice(0, 10).map(item => ({ name: item.result, count: Number(item.count || 0) })),
        [normalizedByResult]
    );
    const topicChartData = useMemo(
        () => visibleTopics.slice(0, 10).map(item => ({ name: item?.topic || 'N/A', count: Number(item?.count || 0) })),
        [visibleTopics]
    );
    const legislatureChartData = useMemo(
        () => (stats?.byLegislature || [])
            .map(item => ({ name: item?.legislature || 'N/A', count: Number(item?.count || 0) }))
            .sort((a, b) => legislatureSortKey(a.name) - legislatureSortKey(b.name))
            .slice(0, 20),
        [stats?.byLegislature]
    );
    const topTopicCurrentLegislature = useMemo(() => {
        const currentLegislature = String(stats?.currentLegislature || '').trim();
        const topic = String(stats?.topTopicCurrentLegislature?.topic || '').trim();
        const count = Number(stats?.topTopicCurrentLegislature?.count || 0);
        return {
            legislature: currentLegislature,
            topic,
            count
        };
    }, [stats?.currentLegislature, stats?.topTopicCurrentLegislature]);
    const dayOfWeekChartData = useMemo(() => {
        const grouped = new Map();
        (stats?.byDayOfWeek || []).forEach((item) => {
            const raw = String(item?.day_of_week || '').trim();
            if (!raw) return;
            grouped.set(raw, (grouped.get(raw) || 0) + Number(item?.count || 0));
        });
        return ['1', '2', '3', '4', '5', '6', '7'].map((day) => ({
            name: dayOfWeekLabel(day),
            count: Number(grouped.get(day) || 0),
            sortKey: Number(day)
        }));
    }, [stats?.byDayOfWeek]);
    const selectedAuthorGroup = useMemo(() => {
        const target = String(selectedAuthor || '').trim().toLowerCase();
        if (!target) return '';
        const match = (authors || []).find((row) => String(row?.author || '').trim().toLowerCase() === target);
        return String(match?.standardizedGroup || '').trim();
    }, [authors, selectedAuthor]);
    const trendChartData = useMemo(() => {
        const normalizedRows = (trendRows || []).filter((row) => String(row?.legislature || '').trim() && String(row?.value || '').trim());
        if (normalizedRows.length === 0) return { rows: [], keys: [] };

        const totalByValue = new Map();
        const totalByLegislature = new Map();
        normalizedRows.forEach((row) => {
            const key = String(row.value || '').trim();
            const leg = String(row.legislature || '').trim();
            const count = Number(row.count || 0);
            totalByValue.set(key, (totalByValue.get(key) || 0) + count);
            totalByLegislature.set(leg, (totalByLegislature.get(leg) || 0) + count);
        });

        const topKeys = Array.from(totalByValue.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, trendSeriesCount)
            .map(([key]) => key);

        const legislatureMap = new Map();
        normalizedRows.forEach((row) => {
            const leg = String(row.legislature || '').trim();
            const value = String(row.value || '').trim();
            if (!topKeys.includes(value)) return;
            if (!legislatureMap.has(leg)) legislatureMap.set(leg, { legislature: leg });
            const target = legislatureMap.get(leg);
            target[value] = Number(row.count || 0);
        });

        const rows = Array.from(legislatureMap.values())
            .map((row) => {
                const normalized = { legislature: row.legislature };
                const legislatureTotal = Number(totalByLegislature.get(row.legislature) || 0);
                topKeys.forEach((key) => {
                    const raw = Number(row[key] || 0);
                    normalized[`${key}__raw`] = raw;
                    normalized[`${key}__percent`] = legislatureTotal > 0 ? Number(((raw / legislatureTotal) * 100).toFixed(1)) : 0;
                });
                return normalized;
            })
            .sort((a, b) => legislatureSortKey(a.legislature) - legislatureSortKey(b.legislature));

        return { rows, keys: topKeys };
    }, [trendRows, trendSeriesCount]);

    const trendValueSuffix = trendValueMode === 'percent' ? '__percent' : '__raw';
    const trendDisplayRows = useMemo(
        () => (trendChartData.rows || []).map((row) => {
            const next = { legislature: row.legislature };
            (trendChartData.keys || []).forEach((key) => {
                next[key] = Number(row[`${key}${trendValueSuffix}`] || 0);
            });
            return next;
        }),
        [trendChartData, trendValueSuffix]
    );
    const trendYAxisMax = useMemo(() => {
        const allValues = [];
        trendDisplayRows.forEach((row) => {
            trendChartData.keys.forEach((key) => {
                allValues.push(Number(row[key] || 0));
            });
        });
        const maxValue = Math.max(0, ...allValues);
        if (trendValueMode === 'percent') {
            const padded = maxValue <= 0 ? 10 : Math.min(100, maxValue * 1.1);
            return Number(padded.toFixed(1));
        }
        const padded = maxValue <= 0 ? 10 : Math.ceil(maxValue * 1.1);
        return padded;
    }, [trendDisplayRows, trendChartData.keys, trendValueMode]);

    return (
        <div className="flex flex-col md:flex-row h-screen font-sans text-stone-800 bg-stone-50 overflow-hidden relative">
            {loading && !stats && (
                <div className="fixed inset-0 z-[70] bg-stone-50 flex items-center justify-center" role="status" aria-live="polite">
                    <div className="flex flex-col items-center text-center px-6">
                        <RefreshCw className="animate-spin text-green-600 mb-4" size={38} />
                        <p className="text-lg font-bold text-stone-800">Loading database…</p>
                        <p className="mt-1 text-sm text-stone-500">Preparing visualizations and filters</p>
                    </div>
                </div>
            )}
            <div className="md:hidden bg-[#222222] text-white p-4 flex justify-between items-center shadow-md z-50 shrink-0">
                <div className="flex flex-col">
                    <h1 className="text-lg font-bold font-sans tracking-tight">Spanish Parliament<span className={BRAND.accent}> OQ</span></h1>
                </div>
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-1">
                    {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {mobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-30 md:hidden"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            <aside className={`
                fixed inset-y-0 left-0 w-64 ${BRAND.secondary} text-stone-100 flex flex-col shadow-xl z-40 transform transition-transform duration-300 ease-in-out
                ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
                md:relative md:translate-x-0
            `}>
                <div className="p-6 border-b border-stone-700 hidden md:block">
                    <h1 className="text-xl font-sans font-bold tracking-tight">Spanish Parliament<span className={BRAND.accent}> OQ</span></h1>
                    <p className="text-xs text-stone-400 mt-1 uppercase tracking-widest">Congress of Deputies</p>
                </div>

                <nav className="flex-1 p-4 space-y-1">
                    <div className="px-4 py-2 mb-4">
                        <div className="text-xs text-stone-400 uppercase font-bold">Logged in as</div>
                        <div className="font-medium text-white truncate">{displayUserName}</div>
                        <div className="flex justify-between items-center text-xs mt-1">
                            <span className="text-green-400 capitalize bg-stone-800 px-1 rounded">{user?.role}</span>
                            <button onClick={onLogout} title="Log Out">
                                <ArrowLeft size={14} className="text-stone-400 hover:text-red-400" />
                            </button>
                        </div>
                    </div>

                    <button
                        onClick={() => { setView('dashboard'); setMobileMenuOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'dashboard' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
                    >
                        <LayoutDashboard size={18} />
                        <span>Dashboard</span>
                    </button>

                    <button
                        onClick={() => { setView('questions'); setPage(1); setMobileMenuOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'questions' || view === 'question_detail' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
                    >
                        <FileSpreadsheet size={18} />
                        <span>Questions</span>
                    </button>

                    <button
                        onClick={() => { setView('authors'); setAuthorsPage(1); setAuthorQuestionsPage(1); setMobileMenuOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'authors' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
                    >
                        <Users size={18} />
                        <span>Authors</span>
                    </button>

                    <button
                        onClick={() => { setView('groups'); setGroupsPage(1); setGroupQuestionsPage(1); setMobileMenuOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'groups' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
                    >
                        <Users size={18} />
                        <span>Parliamentary Groups</span>
                    </button>

                    <div className="pt-4 mt-4 border-t border-stone-700">
                        {user?.role !== 'viewer' && <button
                            onClick={() => { setView('data'); setMobileMenuOpen(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'data' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
                        >
                            <HardDrive size={18} />
                            <span>Data Management</span>
                        </button>}

                        {user?.role !== 'viewer' && <button
                            onClick={() => { onBackToHub(); setMobileMenuOpen(false); }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium text-stone-300 hover:bg-stone-700"
                        >
                            <Database size={18} />
                            <span>Databases Hub</span>
                        </button>}
                    </div>
                </nav>

                <div className="p-6 border-t border-stone-700">
                    <div className="text-xs text-stone-500 text-center">
                        Q-DEM Research Group<br/>
                        &copy; 2026
                    </div>
                </div>
            </aside>

            <main className={`flex-1 overflow-auto ${BRAND.light} relative p-6 md:p-8`}>
                {view === 'dashboard' && (
                    <div className="space-y-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between border-b border-stone-300 pb-4 gap-3">
                            <div>
                                <h2 className="text-3xl font-bold text-stone-900">Dashboard</h2>
                            </div>
                            <button
                                onClick={fetchDashboard}
                                className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 bg-white text-sm hover:bg-stone-50"
                            >
                                <RefreshCw size={14} /> Refresh stats
                            </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                            <div className="bg-white border border-stone-200 p-3 rounded-sm shadow-sm">
                                <div className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Total Questions</div>
                                <div className="text-2xl font-bold text-stone-900">{stats?.summary?.total || 0}</div>
                            </div>
                            <div className="bg-white border border-stone-200 p-3 rounded-sm shadow-sm">
                                <div className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Authors</div>
                                <div className="text-2xl font-bold text-stone-900">{stats?.summary?.authors || 0}</div>
                            </div>
                            <div className="bg-white border border-stone-200 p-3 rounded-sm shadow-sm">
                                <div className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Date Range</div>
                                <div className="text-sm font-medium text-stone-700">{stats?.summary?.minDate || '—'} → {stats?.summary?.maxDate || '—'}</div>
                            </div>
                            <div className="bg-white border border-stone-200 p-3 rounded-sm shadow-sm">
                                <div className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-1">Top Topic</div>
                                <div className="text-sm font-semibold text-stone-900 truncate">{topTopic?.topic || '—'}</div>
                                <div className="text-sm text-stone-500 mt-1">{topTopic?.count || 0} questions</div>
                            </div>
                            <div className="bg-white border border-stone-200 p-3 rounded-sm shadow-sm">
                                <div className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-1">Top Topic Current Legislature</div>
                                <div className="text-sm font-semibold text-stone-900 truncate">{topTopicCurrentLegislature?.topic || '—'}</div>
                                <div className="text-sm text-stone-500 mt-1">{topTopicCurrentLegislature?.legislature || '—'} · {topTopicCurrentLegislature?.count || 0} questions</div>
                            </div>
                        </div>

                        <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
                            <div className="flex items-center justify-between mb-2 gap-2">
                                <h3 className="text-sm font-bold">Legislature Trend</h3>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={trendDimension}
                                        onChange={(e) => setTrendDimension(e.target.value)}
                                        className="border border-stone-300 p-1 text-xs"
                                    >
                                        <option value="topic">By topic</option>
                                        <option value="gender">By gender</option>
                                        <option value="party">By parliamentary group</option>
                                    </select>
                                    <select
                                        value={String(trendSeriesCount)}
                                        onChange={(e) => setTrendSeriesCount(parseInt(e.target.value, 10))}
                                        className="border border-stone-300 p-1 text-xs"
                                    >
                                        <option value="3">Top 3 series</option>
                                        <option value="5">Top 5 series</option>
                                        <option value="8">Top 8 series</option>
                                        <option value="10">Top 10 series</option>
                                    </select>
                                    <select
                                        value={trendValueMode}
                                        onChange={(e) => setTrendValueMode(e.target.value)}
                                        className="border border-stone-300 p-1 text-xs"
                                    >
                                        <option value="percent">Percentages</option>
                                        <option value="total">Totals</option>
                                    </select>
                                    <select
                                        value={trendArena}
                                        onChange={(e) => setTrendArena(e.target.value)}
                                        className="border border-stone-300 p-1 text-xs"
                                    >
                                        <option value="">All arenas</option>
                                        {(filters.arenas || []).map((value) => (
                                            <option key={`trend-arena-${value}`} value={value}>{value}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height="92%">
                                <LineChart data={trendDisplayRows} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="legislature" tick={{ fontSize: 11 }} />
                                    <YAxis
                                        tick={{ fontSize: 11 }}
                                        domain={[0, trendYAxisMax]}
                                        tickFormatter={(value) => trendValueMode === 'percent' ? `${value}%` : `${value}`}
                                    />
                                    <Tooltip formatter={(value) => trendValueMode === 'percent' ? `${Number(value).toFixed(1)}%` : `${Number(value)}`} />
                                    {trendChartData.keys.map((key, index) => (
                                        <Line key={`trend-line-${key}`} type="monotone" dataKey={key} stroke={OQ_CHART_COLORS[index % OQ_CHART_COLORS.length]} strokeWidth={2} dot={false} />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
                                <h3 className="text-sm font-bold mb-3">Distribution Snapshot</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 h-[305px]">
                                    <div className="border border-stone-100 p-1 flex flex-col">
                                        <div className="text-[11px] text-stone-500 mb-1">Arena</div>
                                        <div className="flex-1 min-h-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={arenaChartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius="72%" innerRadius="42%">
                                                    {arenaChartData.map((entry, index) => <Cell key={`arena-pie-${entry.name}`} fill={OQ_CHART_COLORS[index % OQ_CHART_COLORS.length]} />)}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        </div>
                                    </div>
                                    <div className="border border-stone-100 p-1 flex flex-col">
                                        <div className="text-[11px] text-stone-500 mb-1">Result</div>
                                        <div className="flex-1 min-h-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={resultChartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius="72%" innerRadius="42%">
                                                    {resultChartData.map((entry, index) => <Cell key={`result-pie-${entry.name}`} fill={OQ_CHART_COLORS[index % OQ_CHART_COLORS.length]} />)}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        </div>
                                    </div>
                                    <div className="border border-stone-100 p-1 flex flex-col">
                                        <div className="text-[11px] text-stone-500 mb-1">Topic</div>
                                        <div className="flex-1 min-h-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={topicChartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius="72%" innerRadius="42%">
                                                    {topicChartData.map((entry, index) => <Cell key={`topic-pie-${entry.name}`} fill={OQ_CHART_COLORS[index % OQ_CHART_COLORS.length]} />)}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
                                <h3 className="text-sm font-bold mb-3">Questions by Result (Normalized)</h3>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={resultChartData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#5aa85c" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
                                <h3 className="text-sm font-bold mb-3">Top Topics</h3>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={topicChartData} margin={{ top: 10, right: 20, left: 0, bottom: 80 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={90} tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#4E647D" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
                                <h3 className="text-sm font-bold mb-3">Questions by Day of Week</h3>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={dayOfWeekChartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="name" interval={0} height={45} tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#D76559" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
                                <h3 className="text-sm font-bold mb-3">Questions by Legislature</h3>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={legislatureChartData} margin={{ top: 10, right: 20, left: 0, bottom: 80 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={90} tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#4E647D" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                        </div>
                    </div>
                )}

                {view === 'questions' && (
                    <div className="space-y-4">
                        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between border-b border-stone-300 pb-4">
                            <h2 className="text-3xl font-bold text-stone-900">Oral Questions</h2>
                            <div className="text-sm text-stone-500">{meta.total} records</div>
                        </div>

                        <div className="bg-white border border-stone-200 p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search question, author, reference, topic..." className="border border-stone-300 p-2 text-sm lg:col-span-2" />
                            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm" />
                            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm" />

                            <select value={legislature} onChange={(e) => { setLegislature(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All legislatures</option>{filters.legislatures.map(v => <option key={`leg-opt-${v}`} value={v}>{v}</option>)}</select>
                            <select value={arena} onChange={(e) => { setArena(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All arenas</option>{filters.arenas.map(v => <option key={`arena-opt-${v}`} value={v}>{v}</option>)}</select>
                            <select value={result} onChange={(e) => { setResult(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All results</option>{filters.results.map(v => <option key={`result-opt-${v}`} value={v}>{normalizeOralQuestionResult(v) === String(v || '').trim() ? v : `${normalizeOralQuestionResult(v)} · ${v}`}</option>)}</select>
                            <select value={topic} onChange={(e) => { setTopic(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All topics</option>{filters.topics.map(v => <option key={`topic-opt-${v}`} value={v}>{v}</option>)}</select>

                            <select value={president} onChange={(e) => { setPresident(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All presidents</option>{filters.presidents.map(v => <option key={`pres-opt-${v}`} value={v}>{v}</option>)}</select>
                            <select value={questionType} onChange={(e) => { setQuestionType(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All question types</option>{filters.questionTypes.map(v => <option key={`qt-opt-${v}`} value={v}>{v}</option>)}</select>
                            <select value={commission} onChange={(e) => { setCommission(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All commissions</option>{filters.commissions.map(v => <option key={`comm-opt-${v}`} value={v}>{v}</option>)}</select>
                            <select value={dayOfWeek} onChange={(e) => { setDayOfWeek(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All days</option>{filters.daysOfWeek.map(v => <option key={`day-opt-${v}`} value={v}>{`${dayOfWeekLabel(v)}${String(v || '').trim() ? ` (${v})` : ''}`}</option>)}</select>

                            <select value={authorGender} onChange={(e) => { setAuthorGender(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All author genders</option>{filters.authorGenders.map(v => <option key={`ag-opt-${v}`} value={v}>{v}</option>)}</select>
                            <select value={parliamentaryGroup} onChange={(e) => { setParliamentaryGroup(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All parliamentary groups</option>{filters.parliamentaryGroups.map(v => <option key={`pg-opt-${v}`} value={v}>{v}</option>)}</select>
                            <select value={party} onChange={(e) => { setParty(e.target.value); setPage(1); }} className="border border-stone-300 p-2 text-sm"><option value="">All parliamentary groups</option>{filters.parties.map(v => <option key={`party-opt-${v}`} value={v}>{v}</option>)}</select>
                            <button
                                onClick={() => {
                                    setSearch('');
                                    setArena('');
                                    setResult('');
                                    setParty('');
                                    setLegislature('');
                                    setPresident('');
                                    setQuestionType('');
                                    setAuthorGender('');
                                    setParliamentaryGroup('');
                                    setTopic('');
                                    setCommission('');
                                    setDayOfWeek('');
                                    setDateFrom('');
                                    setDateTo('');
                                    setPage(1);
                                }}
                                className="border border-stone-300 bg-stone-50 hover:bg-stone-100 text-sm px-3 py-2"
                            >
                                Clear filters
                            </button>
                        </div>

                        <div className="bg-white border border-stone-200 overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-stone-100 text-xs uppercase text-stone-600">
                                    <tr>
                                        {questionColumns.map((column) => (
                                            <th key={`oq-col-${column.key}`} className="p-3 whitespace-nowrap">{column.label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-100">
                                    {rows.map((r) => (
                                        <tr key={r.id} className="hover:bg-stone-50 align-top cursor-pointer" onClick={() => { openQuestionDetail(r.id, r); }}>
                                            {questionColumns.map((column) => {
                                                const value = r?.[column.key];
                                                if (column.key === 'question') {
                                                    return (
                                                        <td key={`oq-cell-${r.id}-${column.key}`} className="p-3 min-w-[420px]">
                                                            <div className="line-clamp-3">{value || '—'}</div>
                                                        </td>
                                                    );
                                                }

                                                if (column.key === 'author') {
                                                    const parsedAuthors = parseAuthorsFromField(value);
                                                    return (
                                                        <td key={`oq-cell-${r.id}-${column.key}`} className="p-3 min-w-[220px]">
                                                            {parsedAuthors.length > 0 ? (
                                                                <div className="flex flex-wrap gap-1">
                                                                    {parsedAuthors.map((authorName) => (
                                                                        <button
                                                                            key={`author-link-${r.id}-${authorName}`}
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                setSelectedAuthor(authorName);
                                                                                setAuthorResultFilter('');
                                                                                setAuthorTopicFilter('');
                                                                                setAuthorArenaFilter('');
                                                                                setAuthorLegislatureFilter('');
                                                                                setAuthorQuestionsPage(1);
                                                                                setView('authors');
                                                                            }}
                                                                            className="px-2 py-1 text-xs border border-stone-300 bg-stone-50 hover:bg-stone-100"
                                                                        >
                                                                            {authorName}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            ) : '—'}
                                                        </td>
                                                    );
                                                }

                                                if (column.key === 'standardized_groups') {
                                                    const parsedGroups = parseStandardizedGroupsFromRow(r);
                                                    return (
                                                        <td key={`oq-cell-${r.id}-${column.key}`} className="p-3 min-w-[220px]">
                                                            {parsedGroups.length > 0 ? (
                                                                <div className="flex flex-wrap gap-1">
                                                                    {parsedGroups.map((groupName) => (
                                                                        <button
                                                                            key={`group-link-${r.id}-${groupName}`}
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                setSelectedParliamentaryGroup(groupName);
                                                                                setGroupResultFilter('');
                                                                                setGroupTopicFilter('');
                                                                                setGroupArenaFilter('');
                                                                                setGroupLegislatureFilter('');
                                                                                setGroupQuestionsPage(1);
                                                                                setView('groups');
                                                                            }}
                                                                            className="px-2 py-1 text-xs border border-stone-300 bg-stone-50 hover:bg-stone-100"
                                                                        >
                                                                            {groupName}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            ) : '—'}
                                                        </td>
                                                    );
                                                }

                                                let displayValue = column.key === 'result' ? normalizeOralQuestionResult(value) : value;
                                                if (column.key === 'cap_topic' && isHiddenTopicValue(value, r?.cap_topic_code)) displayValue = '';
                                                if (isHiddenValue(displayValue)) displayValue = '';
                                                return (
                                                    <td key={`oq-cell-${r.id}-${column.key}`} className="p-3 min-w-[140px] whitespace-pre-wrap">
                                                        {displayValue || '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    {rows.length === 0 && !loading && (
                                        <tr><td colSpan={questionColumns.length} className="p-8 text-center text-stone-400">No records found</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                            <div>Page {page} / {meta.totalPages}</div>
                            <div className="flex items-center gap-2">
                                <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }} className="border border-stone-300 p-1">
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-1 border border-stone-300 disabled:opacity-40">Prev</button>
                                <button disabled={page >= meta.totalPages} onClick={() => setPage(p => Math.min(meta.totalPages, p + 1))} className="px-3 py-1 border border-stone-300 disabled:opacity-40">Next</button>
                            </div>
                        </div>
                    </div>
                )}

                {view === 'question_detail' && (
                    <div className="space-y-4">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between border-b border-stone-300 pb-4 gap-3">
                            <h2 className="text-3xl font-bold text-stone-900">Question View</h2>
                            <div className="flex items-center gap-2">
                                {isAdmin && !isEditingQuestion && (
                                    <button onClick={handleEditQuestion} className="px-3 py-2 border border-stone-300 bg-white hover:bg-stone-50 text-sm">Edit</button>
                                )}
                                {isAdmin && isEditingQuestion && (
                                    <>
                                        <button onClick={() => { setIsEditingQuestion(false); setEditingQuestionDraft(null); }} className="px-3 py-2 border border-stone-300 bg-white hover:bg-stone-50 text-sm" disabled={savingQuestion}>Cancel</button>
                                        <button onClick={handleSaveQuestion} className={`px-3 py-2 text-sm text-white ${BRAND.primary} ${BRAND.primaryHover}`} disabled={savingQuestion}>{savingQuestion ? 'Saving...' : 'Save changes'}</button>
                                    </>
                                )}
                                <button
                                    onClick={() => setView('questions')}
                                    className="px-3 py-2 border border-stone-300 bg-white hover:bg-stone-50 text-sm"
                                >
                                    Back to Questions
                                </button>
                            </div>
                        </div>

                        {!selectedQuestion ? (
                            <div className="bg-white border border-stone-200 p-8 text-stone-400 text-center">No question selected.</div>
                        ) : (
                            <div className="space-y-4">
                                <div className="bg-white border border-stone-200 p-5 rounded-sm shadow-sm">
                                    <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">{selectedQuestion?.question_reference || 'No reference'}</div>
                                    <div className="text-lg text-stone-900 leading-relaxed">{selectedQuestion?.question || '—'}</div>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        <span className="px-2 py-1 text-xs border border-stone-300 bg-stone-50">{selectedQuestion?.date_presentation || 'No date'}</span>
                                        <span className="px-2 py-1 text-xs border border-stone-300 bg-stone-50">{selectedQuestion?.legislature || 'No legislature'}</span>
                                        {!!String(selectedQuestion?.question_type || '').trim() && !isHiddenValue(selectedQuestion?.question_type) && (
                                            <span className="px-2 py-1 text-xs border border-stone-300 bg-stone-50">{selectedQuestion?.question_type}</span>
                                        )}
                                        <span className="px-2 py-1 text-xs border border-stone-300 bg-stone-50">{normalizeOralQuestionResult(selectedQuestion?.result)}</span>
                                        <span className="px-2 py-1 text-xs border border-stone-300 bg-stone-50">{selectedQuestion?.arena || 'No arena'}</span>
                                        {!!String(selectedQuestion?.url || '').trim() && (
                                            <a
                                                href={selectedQuestion.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`px-2 py-1 text-xs text-white ${BRAND.primary} ${BRAND.primaryHover}`}
                                            >
                                                View in the Congress website
                                            </a>
                                        )}
                                    </div>
                                </div>

                                {questionDetailSections.map((section) => {
                                    const renderedFields = section.fields.map((field) => {
                                                const sourceQuestion = isEditingQuestion ? (editingQuestionDraft || selectedQuestion || {}) : (selectedQuestion || {});
                                                let value = sourceQuestion?.[field.key];
                                                if (field.key === 'cap_topic' && isHiddenTopicValue(value, selectedQuestion?.cap_topic_code)) value = '';
                                                if (field.normalize) value = field.normalize(value);
                                                if (!isEditingQuestion && (
                                                    isHiddenValue(value)
                                                    || (field.key === 'cap_topic' && isHiddenTopicValue(value, sourceQuestion?.cap_topic_code))
                                                    || (field.key === 'party_within_grupo_mixto' && isHiddenValue(value))
                                                )) {
                                                    return null;
                                                }

                                                if (field.key === 'author') {
                                                    const parsedAuthors = parseAuthorsFromField(value);
                                                    return (
                                                        <div key={`q-detail-field-${field.key}`} className="border border-stone-100 p-3 rounded-sm bg-stone-50/40">
                                                            <div className="text-xs font-semibold uppercase text-stone-500 mb-2">{field.label}</div>
                                                            {parsedAuthors.length > 0 ? (
                                                                <div className="flex flex-wrap gap-1">
                                                                    {parsedAuthors.map((authorName) => (
                                                                        <button
                                                                            key={`q-detail-author-${authorName}`}
                                                                            onClick={() => {
                                                                                setSelectedAuthor(authorName);
                                                                                setAuthorResultFilter('');
                                                                                setAuthorTopicFilter('');
                                                                                setAuthorArenaFilter('');
                                                                                setAuthorLegislatureFilter('');
                                                                                setAuthorQuestionsPage(1);
                                                                                setView('authors');
                                                                            }}
                                                                            className="px-2 py-1 text-xs border border-stone-300 bg-white hover:bg-stone-100"
                                                                        >
                                                                            {authorName}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            ) : <div className="text-sm text-stone-800">—</div>}
                                                        </div>
                                                    );
                                                }

                                                if (field.key === 'grupos_estandarizados') {
                                                    const parsedGroups = parseStandardizedGroupsFromRow(selectedQuestion || {});
                                                    return (
                                                        <div key={`q-detail-field-${field.key}`} className="border border-stone-100 p-3 rounded-sm bg-stone-50/40">
                                                            <div className="text-xs font-semibold uppercase text-stone-500 mb-2">{field.label}</div>
                                                            {parsedGroups.length > 0 ? (
                                                                <div className="flex flex-wrap gap-1">
                                                                    {parsedGroups.map((groupName) => (
                                                                        <button
                                                                            key={`q-detail-group-${groupName}`}
                                                                            onClick={() => {
                                                                                setSelectedParliamentaryGroup(groupName);
                                                                                setGroupResultFilter('');
                                                                                setGroupTopicFilter('');
                                                                                setGroupArenaFilter('');
                                                                                setGroupLegislatureFilter('');
                                                                                setGroupQuestionsPage(1);
                                                                                setView('groups');
                                                                            }}
                                                                            className="px-2 py-1 text-xs border border-stone-300 bg-white hover:bg-stone-100"
                                                                        >
                                                                            {groupName}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            ) : <div className="text-sm text-stone-800">—</div>}
                                                        </div>
                                                    );
                                                }

                                                if (field.key === 'url') {
                                                    return (
                                                        <div key={`q-detail-field-${field.key}`} className="border border-stone-100 p-3 rounded-sm bg-stone-50/40">
                                                            <div className="text-xs font-semibold uppercase text-stone-500 mb-1">{field.label}</div>
                                                            {isEditingQuestion ? (
                                                                <input
                                                                    value={editingQuestionDraft?.[field.key] || ''}
                                                                    onChange={(e) => setEditingQuestionDraft(prev => ({ ...(prev || {}), [field.key]: e.target.value }))}
                                                                    className="w-full border border-stone-300 p-2 text-sm"
                                                                />
                                                            ) : (value ? <a href={value} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline break-all text-sm">{value}</a> : <div className="text-sm text-stone-800">—</div>)}
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <div key={`q-detail-field-${field.key}`} className="border border-stone-100 p-3 rounded-sm bg-stone-50/40">
                                                        <div className="text-xs font-semibold uppercase text-stone-500 mb-1">{field.label}</div>
                                                        {isEditingQuestion ? (
                                                            field.key === 'question' ? (
                                                                <textarea
                                                                    value={editingQuestionDraft?.[field.key] || ''}
                                                                    onChange={(e) => setEditingQuestionDraft(prev => ({ ...(prev || {}), [field.key]: e.target.value }))}
                                                                    rows={4}
                                                                    className="w-full border border-stone-300 p-2 text-sm"
                                                                />
                                                            ) : (
                                                                <input
                                                                    value={editingQuestionDraft?.[field.key] || ''}
                                                                    onChange={(e) => setEditingQuestionDraft(prev => ({ ...(prev || {}), [field.key]: e.target.value }))}
                                                                    className="w-full border border-stone-300 p-2 text-sm"
                                                                />
                                                            )
                                                        ) : (
                                                            <div className="text-sm text-stone-800 whitespace-pre-wrap">{value || '—'}</div>
                                                        )}
                                                    </div>
                                                );
                                            }).filter(Boolean);

                                            if (!isEditingQuestion && renderedFields.length === 0) return null;

                                            return (
                                                <div key={`q-detail-${section.title}`} className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm">
                                                    <h3 className="text-sm font-bold uppercase tracking-wider text-stone-500 mb-3">{section.title}</h3>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        {renderedFields}
                                                    </div>
                                                </div>
                                            );
                                        })}
                            </div>
                        )}
                    </div>
                )}

                {view === 'authors' && (
                    <div className="space-y-4">
                        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between border-b border-stone-300 pb-4">
                            <h2 className="text-3xl font-bold text-stone-900">Authors</h2>
                            <div className="text-sm text-stone-500">{authorsMeta.total} authors</div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            <div className="xl:col-span-1 bg-white border border-stone-200 p-4 space-y-3">
                                <input
                                    value={authorSearch}
                                    onChange={(e) => { setAuthorSearch(e.target.value); setAuthorsPage(1); }}
                                    placeholder="Search author..."
                                    className="w-full border border-stone-300 p-2 text-sm"
                                />

                                <div className="max-h-[520px] overflow-auto border border-stone-100">
                                    {(authors || []).map((row) => (
                                        <button
                                            key={`author-row-${row.author}`}
                                            onClick={() => {
                                                setSelectedAuthor(row.author);
                                                setAuthorResultFilter('');
                                                setAuthorTopicFilter('');
                                                setAuthorArenaFilter('');
                                                setAuthorLegislatureFilter('');
                                                setAuthorQuestionsPage(1);
                                            }}
                                            className={`w-full text-left px-3 py-2 border-b border-stone-100 hover:bg-stone-50 ${selectedAuthor === row.author ? 'bg-green-50' : ''}`}
                                        >
                                            <div className="text-sm font-medium text-stone-800 truncate">{row.author}{row.standardizedGroup && !isHiddenValue(row.standardizedGroup) ? ` (${row.standardizedGroup})` : ''}</div>
                                            <div className="text-xs text-stone-500">{row.count} questions</div>
                                        </button>
                                    ))}
                                    {(authors || []).length === 0 && !loading && (
                                        <div className="p-4 text-sm text-stone-400 italic">No authors found.</div>
                                    )}
                                </div>

                                <div className="flex items-center justify-between text-xs">
                                    <span>Page {authorsPage} / {authorsMeta.totalPages}</span>
                                    <div className="flex gap-1">
                                        <button className="px-2 py-1 border border-stone-300 disabled:opacity-40" disabled={authorsPage <= 1} onClick={() => setAuthorsPage(p => Math.max(1, p - 1))}>Prev</button>
                                        <button className="px-2 py-1 border border-stone-300 disabled:opacity-40" disabled={authorsPage >= authorsMeta.totalPages} onClick={() => setAuthorsPage(p => Math.min(authorsMeta.totalPages, p + 1))}>Next</button>
                                    </div>
                                </div>
                            </div>

                            <div className="xl:col-span-2 bg-white border border-stone-200 p-4">
                                <div className="flex justify-between items-center mb-3 border-b border-stone-100 pb-2">
                                    <h3 className="text-lg font-semibold text-stone-900 truncate">{selectedAuthor ? `${selectedAuthor}${selectedAuthorGroup && !isHiddenValue(selectedAuthorGroup) ? ` (${selectedAuthorGroup})` : ''}` : 'Select an author'}</h3>
                                    <span className="text-sm text-stone-500">{authorQuestionsMeta.total} questions</span>
                                </div>

                                {selectedAuthor ? (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
                                            <select value={authorResultFilter} onChange={(e) => { setAuthorResultFilter(e.target.value); setAuthorQuestionsPage(1); }} className="border border-stone-300 p-2 text-sm">
                                                <option value="">All results</option>
                                                {(authorFacets.results || []).map((facet) => (
                                                    <option key={`author-result-filter-${facet.value}`} value={String(facet.value || '').trim().toLowerCase()}>{normalizeOralQuestionResult(facet.value)} ({facet.count})</option>
                                                ))}
                                            </select>

                                            <select value={authorTopicFilter} onChange={(e) => { setAuthorTopicFilter(e.target.value); setAuthorQuestionsPage(1); }} className="border border-stone-300 p-2 text-sm">
                                                <option value="">All topics</option>
                                                {(authorFacets.topics || []).map((facet) => (
                                                    <option key={`author-topic-filter-${facet.value}`} value={String(facet.value || '').trim().toLowerCase()}>{facet.value} ({facet.count})</option>
                                                ))}
                                            </select>

                                            <select value={authorArenaFilter} onChange={(e) => { setAuthorArenaFilter(e.target.value); setAuthorQuestionsPage(1); }} className="border border-stone-300 p-2 text-sm">
                                                <option value="">All arenas</option>
                                                {(authorFacets.arenas || []).map((facet) => (
                                                    <option key={`author-arena-filter-${facet.value}`} value={String(facet.value || '').trim().toLowerCase()}>{facet.value} ({facet.count})</option>
                                                ))}
                                            </select>

                                            <select value={authorLegislatureFilter} onChange={(e) => { setAuthorLegislatureFilter(e.target.value); setAuthorQuestionsPage(1); }} className="border border-stone-300 p-2 text-sm">
                                                <option value="">All legislatures</option>
                                                {(authorFacets.legislatures || []).map((facet) => (
                                                    <option key={`author-leg-filter-${facet.value}`} value={String(facet.value || '').trim().toLowerCase()}>{facet.value} ({facet.count})</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="bg-white border border-stone-200 p-3 mb-3">
                                            <div className="text-xs font-semibold uppercase text-stone-500 mb-2">Topic distribution (filtered)</div>
                                            <div className="h-[250px]">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={(authorTopicDistribution || []).slice(0, 10).map(item => ({ name: item.topic, value: item.count }))}
                                                            dataKey="value"
                                                            nameKey="name"
                                                            innerRadius={62}
                                                            outerRadius={108}
                                                            paddingAngle={2}
                                                        >
                                                            {(authorTopicDistribution || []).slice(0, 10).map((entry, index) => (
                                                                <Cell key={`author-topic-donut-${entry.topic}`} fill={OQ_CHART_COLORS[index % OQ_CHART_COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>

                                        <div className="overflow-x-auto border border-stone-100">
                                            <table className="w-full text-sm text-left">
                                                <thead className="bg-stone-100 text-xs uppercase text-stone-600">
                                                    <tr>
                                                        <th className="p-2">Date</th>
                                                        <th className="p-2">Reference</th>
                                                        <th className="p-2">Question</th>
                                                        <th className="p-2">Result</th>
                                                        <th className="p-2">Topic</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-stone-100">
                                                    {(authorQuestions || []).map((row) => (
                                                        <tr key={`author-q-${row.id}`} className="hover:bg-stone-50 align-top cursor-pointer" onClick={() => openQuestionDetail(row.id, row)}>
                                                            <td className="p-2 whitespace-nowrap">{row.date_presentation || '—'}</td>
                                                            <td className="p-2 whitespace-nowrap">{row.question_reference || '—'}</td>
                                                            <td className="p-2 min-w-[320px]"><div className="line-clamp-3">{row.question || '—'}</div></td>
                                                            <td className="p-2">{normalizeOralQuestionResult(row.result)}</td>
                                                            <td className="p-2">{isHiddenTopicValue(row.cap_topic, row.cap_topic_code) ? '—' : (row.cap_topic || '—')}</td>
                                                        </tr>
                                                    ))}
                                                    {(authorQuestions || []).length === 0 && !loading && (
                                                        <tr><td colSpan={5} className="p-8 text-center text-stone-400">No questions found for this author.</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="flex items-center justify-between text-sm mt-3">
                                            <span>Page {authorQuestionsPage} / {authorQuestionsMeta.totalPages}</span>
                                            <div className="flex gap-1">
                                                <button className="px-2 py-1 border border-stone-300 disabled:opacity-40" disabled={authorQuestionsPage <= 1} onClick={() => setAuthorQuestionsPage(p => Math.max(1, p - 1))}>Prev</button>
                                                <button className="px-2 py-1 border border-stone-300 disabled:opacity-40" disabled={authorQuestionsPage >= authorQuestionsMeta.totalPages} onClick={() => setAuthorQuestionsPage(p => Math.min(authorQuestionsMeta.totalPages, p + 1))}>Next</button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="h-[420px] flex items-center justify-center text-stone-400 italic">Choose an author from the left list.</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {view === 'groups' && (
                    <div className="space-y-4">
                        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between border-b border-stone-300 pb-4">
                            <h2 className="text-3xl font-bold text-stone-900">Parliamentary Groups</h2>
                            <div className="text-sm text-stone-500">{groupsMeta.total} groups</div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            <div className="xl:col-span-1 bg-white border border-stone-200 p-4 space-y-3">
                                <input
                                    value={groupSearch}
                                    onChange={(e) => { setGroupSearch(e.target.value); setGroupsPage(1); }}
                                    placeholder="Search parliamentary group..."
                                    className="w-full border border-stone-300 p-2 text-sm"
                                />

                                <div className="max-h-[520px] overflow-auto border border-stone-100">
                                    {(parliamentaryGroupsList || []).map((row) => (
                                        <button
                                            key={`group-row-${row.group}`}
                                            onClick={() => {
                                                setSelectedParliamentaryGroup(row.group);
                                                setGroupResultFilter('');
                                                setGroupTopicFilter('');
                                                setGroupArenaFilter('');
                                                setGroupLegislatureFilter('');
                                                setGroupQuestionsPage(1);
                                            }}
                                            className={`w-full text-left px-3 py-2 border-b border-stone-100 hover:bg-stone-50 ${selectedParliamentaryGroup === row.group ? 'bg-green-50' : ''}`}
                                        >
                                            <div className="text-sm font-medium text-stone-800 truncate">{row.group}</div>
                                            <div className="text-xs text-stone-500">{row.count} questions</div>
                                        </button>
                                    ))}
                                    {(parliamentaryGroupsList || []).length === 0 && !loading && (
                                        <div className="p-4 text-sm text-stone-400 italic">No parliamentary groups found.</div>
                                    )}
                                </div>

                                <div className="flex items-center justify-between text-xs">
                                    <span>Page {groupsPage} / {groupsMeta.totalPages}</span>
                                    <div className="flex gap-1">
                                        <button className="px-2 py-1 border border-stone-300 disabled:opacity-40" disabled={groupsPage <= 1} onClick={() => setGroupsPage(p => Math.max(1, p - 1))}>Prev</button>
                                        <button className="px-2 py-1 border border-stone-300 disabled:opacity-40" disabled={groupsPage >= groupsMeta.totalPages} onClick={() => setGroupsPage(p => Math.min(groupsMeta.totalPages, p + 1))}>Next</button>
                                    </div>
                                </div>
                            </div>

                            <div className="xl:col-span-2 bg-white border border-stone-200 p-4">
                                <div className="flex justify-between items-center mb-3 border-b border-stone-100 pb-2">
                                    <h3 className="text-lg font-semibold text-stone-900 truncate">{selectedParliamentaryGroup || 'Select a parliamentary group'}</h3>
                                    <span className="text-sm text-stone-500">{groupQuestionsMeta.total} questions</span>
                                </div>

                                {selectedParliamentaryGroup ? (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
                                            <select value={groupResultFilter} onChange={(e) => { setGroupResultFilter(e.target.value); setGroupQuestionsPage(1); }} className="border border-stone-300 p-2 text-sm">
                                                <option value="">All results</option>
                                                {(groupFacets.results || []).map((facet) => (
                                                    <option key={`group-result-filter-${facet.value}`} value={String(facet.value || '').trim().toLowerCase()}>{normalizeOralQuestionResult(facet.value)} ({facet.count})</option>
                                                ))}
                                            </select>

                                            <select value={groupTopicFilter} onChange={(e) => { setGroupTopicFilter(e.target.value); setGroupQuestionsPage(1); }} className="border border-stone-300 p-2 text-sm">
                                                <option value="">All topics</option>
                                                {(groupFacets.topics || []).map((facet) => (
                                                    <option key={`group-topic-filter-${facet.value}`} value={String(facet.value || '').trim().toLowerCase()}>{facet.value} ({facet.count})</option>
                                                ))}
                                            </select>

                                            <select value={groupArenaFilter} onChange={(e) => { setGroupArenaFilter(e.target.value); setGroupQuestionsPage(1); }} className="border border-stone-300 p-2 text-sm">
                                                <option value="">All arenas</option>
                                                {(groupFacets.arenas || []).map((facet) => (
                                                    <option key={`group-arena-filter-${facet.value}`} value={String(facet.value || '').trim().toLowerCase()}>{facet.value} ({facet.count})</option>
                                                ))}
                                            </select>

                                            <select value={groupLegislatureFilter} onChange={(e) => { setGroupLegislatureFilter(e.target.value); setGroupQuestionsPage(1); }} className="border border-stone-300 p-2 text-sm">
                                                <option value="">All legislatures</option>
                                                {(groupFacets.legislatures || []).map((facet) => (
                                                    <option key={`group-leg-filter-${facet.value}`} value={String(facet.value || '').trim().toLowerCase()}>{facet.value} ({facet.count})</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="bg-white border border-stone-200 p-3 mb-3">
                                            <div className="text-xs font-semibold uppercase text-stone-500 mb-2">Topic distribution (filtered)</div>
                                            <div className="h-[250px]">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={(groupTopicDistribution || []).slice(0, 10).map(item => ({ name: item.topic, value: item.count }))}
                                                            dataKey="value"
                                                            nameKey="name"
                                                            innerRadius={62}
                                                            outerRadius={108}
                                                            paddingAngle={2}
                                                        >
                                                            {(groupTopicDistribution || []).slice(0, 10).map((entry, index) => (
                                                                <Cell key={`group-topic-donut-${entry.topic}`} fill={OQ_CHART_COLORS[index % OQ_CHART_COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>

                                        <div className="overflow-x-auto border border-stone-100">
                                            <table className="w-full text-sm text-left">
                                                <thead className="bg-stone-100 text-xs uppercase text-stone-600">
                                                    <tr>
                                                        <th className="p-2">Date</th>
                                                        <th className="p-2">Reference</th>
                                                        <th className="p-2">Question</th>
                                                        <th className="p-2">Result</th>
                                                        <th className="p-2">Topic</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-stone-100">
                                                    {(groupQuestions || []).map((row) => (
                                                        <tr key={`group-q-${row.id}`} className="hover:bg-stone-50 align-top cursor-pointer" onClick={() => openQuestionDetail(row.id, row)}>
                                                            <td className="p-2 whitespace-nowrap">{row.date_presentation || '—'}</td>
                                                            <td className="p-2 whitespace-nowrap">{row.question_reference || '—'}</td>
                                                            <td className="p-2 min-w-[320px]"><div className="line-clamp-3">{row.question || '—'}</div></td>
                                                            <td className="p-2">{normalizeOralQuestionResult(row.result)}</td>
                                                            <td className="p-2">{isHiddenTopicValue(row.cap_topic, row.cap_topic_code) ? '—' : (row.cap_topic || '—')}</td>
                                                        </tr>
                                                    ))}
                                                    {(groupQuestions || []).length === 0 && !loading && (
                                                        <tr><td colSpan={5} className="p-8 text-center text-stone-400">No questions found for this parliamentary group.</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="flex items-center justify-between text-sm mt-3">
                                            <span>Page {groupQuestionsPage} / {groupQuestionsMeta.totalPages}</span>
                                            <div className="flex gap-1">
                                                <button className="px-2 py-1 border border-stone-300 disabled:opacity-40" disabled={groupQuestionsPage <= 1} onClick={() => setGroupQuestionsPage(p => Math.max(1, p - 1))}>Prev</button>
                                                <button className="px-2 py-1 border border-stone-300 disabled:opacity-40" disabled={groupQuestionsPage >= groupQuestionsMeta.totalPages} onClick={() => setGroupQuestionsPage(p => Math.min(groupQuestionsMeta.totalPages, p + 1))}>Next</button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="h-[420px] flex items-center justify-center text-stone-400 italic">Choose a parliamentary group from the left list.</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {view === 'data' && user?.role !== 'viewer' && (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-bold text-stone-900 border-b border-stone-300 pb-4">Data Management</h2>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {isAdmin && (
                            <div className="bg-white p-8 rounded-sm shadow-sm border border-stone-200">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-green-100 text-green-800 rounded-full">
                                        <Upload size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-stone-800">Import Data</h3>
                                        <p className="text-sm text-stone-500">Load oral questions from CSV</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex gap-4 text-sm">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="oqmode" checked={importMode === 'replace'} onChange={() => setImportMode('replace')} className="accent-green-800" />
                                            <span>Replace database</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="oqmode" checked={importMode === 'append'} onChange={() => setImportMode('append')} className="accent-green-800" />
                                            <span>Append to existing</span>
                                        </label>
                                    </div>

                                    <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:bg-stone-50 transition-colors relative">
                                        <input type="file" accept=".csv" onChange={(e) => handleImportCsv(e.target.files?.[0])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                        <div className="text-stone-400 flex flex-col items-center">
                                            {loading ? <RefreshCw className="animate-spin mb-2" /> : <FileSpreadsheet className="mb-2" size={32} />}
                                            <span className="font-medium text-stone-600">Click to upload CSV file</span>
                                            <span className="text-xs mt-1">Compatible with OQ25.csv structure</span>
                                        </div>
                                    </div>

                                    {importMsg && (
                                        <div className={`p-3 rounded-sm text-sm flex items-center gap-2 ${importMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                                            {importMsg.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                                            {importMsg.text}
                                        </div>
                                    )}
                                </div>
                            </div>
                            )}

                            <div className={`bg-white p-8 rounded-sm shadow-sm border border-stone-200 ${isAdmin ? '' : 'lg:col-span-2'}`}>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-stone-100 text-stone-800 rounded-full">
                                        <HardDrive size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-stone-800">Export / Save</h3>
                                        <p className="text-sm text-stone-500">Download current oral questions dataset</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <p className="text-stone-600 text-sm">Export the Oral Questions dataset in CSV, Excel or full DB storage format.</p>
                                    <button onClick={handleExportCsv} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                                        <div className="flex items-center gap-3">
                                            <FileSpreadsheet className="text-green-600" />
                                            <div className="text-left">
                                                <div className="font-bold text-stone-800 group-hover:text-green-800">Export full CSV</div>
                                                <div className="text-xs text-stone-500">All current oral questions records</div>
                                            </div>
                                        </div>
                                        <Download size={18} />
                                    </button>

                                    <button onClick={handleExportXlsx} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                                        <div className="flex items-center gap-3">
                                            <FileSpreadsheet className="text-green-600" />
                                            <div className="text-left">
                                                <div className="font-bold text-stone-800 group-hover:text-green-800">Export full Excel (.xlsx)</div>
                                                <div className="text-xs text-stone-500">Workbook with all current oral questions rows</div>
                                            </div>
                                        </div>
                                        <Download size={18} />
                                    </button>

                                    <button onClick={handleExportOriginal} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                                        <div className="flex items-center gap-3">
                                            <HardDrive className="text-green-600" size={20} />
                                            <div className="text-left">
                                                <div className="font-bold text-stone-800 group-hover:text-green-800">Download full DB in original format</div>
                                                <div className="text-xs text-stone-500">Current storage engine snapshot (.db SQLite / .sql PostgreSQL)</div>
                                            </div>
                                        </div>
                                        <Download size={18} />
                                    </button>

                                    <div>
                                        <div className="text-xs font-bold uppercase text-stone-400 mb-1">Storage engine</div>
                                        <div className="capitalize">{stats?.storageEngine || 'sqlite'}</div>
                                    </div>

                                    <div className="mt-8 pt-6 border-t border-stone-100">
                                        <h4 className="text-sm font-bold text-stone-800 mb-2">Dataset Stats</h4>
                                        <div className="grid grid-cols-2 gap-4 text-center">
                                            <div className="bg-stone-50 p-2 rounded">
                                                <div className={`text-xl font-bold ${BRAND.accent}`}>{stats?.summary?.total || 0}</div>
                                                <div className="text-xs text-stone-500">Questions</div>
                                            </div>
                                            <div className="bg-stone-50 p-2 rounded">
                                                <div className={`text-xl font-bold ${BRAND.accent}`}>{stats?.summary?.authors || 0}</div>
                                                <div className="text-xs text-stone-500">Authors</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

// --- API HELPER ---
let refreshInFlight = null;

const refreshAccessToken = async () => {
    if (refreshInFlight) return refreshInFlight;

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    refreshInFlight = fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
    })
        .then(async (res) => {
            if (!res.ok) return null;
            const data = await res.json();
            if (data?.token) localStorage.setItem('token', data.token);
            if (data?.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
            return data?.token || null;
        })
        .catch(() => null)
        .finally(() => {
            refreshInFlight = null;
        });

    return refreshInFlight;
};

const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { ...(options.headers || {}) };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, { ...options, headers });

    const isAuthRoute = /\/auth\/(login|refresh)$/.test(url);
    const alreadyRetried = options.__retried === true;

    if ((response.status === 401 || response.status === 403) && !isAuthRoute && !alreadyRetried) {
        const newToken = await refreshAccessToken();
        if (newToken) {
            const retryHeaders = { ...(options.headers || {}), Authorization: `Bearer ${newToken}` };
            return fetch(url, { ...options, headers: retryHeaders, __retried: true });
        }
    }

    return response;
};

// --- MAIN APP SHELL ---
const publicDatabaseIdFromLocation = () => {
    const match = window.location.pathname.match(/^\/public\/([a-z0-9_-]+)\/?$/i);
    return match ? match[1].toLowerCase() : null;
};

export default function App() {
  const [view, setView] = useState('dashboard');
    const [selectedDatabase, setSelectedDatabase] = useState(null);
        const [platformDatabases, setPlatformDatabases] = useState(PLATFORM_DATABASES);
        const [platformCatalogReady, setPlatformCatalogReady] = useState(false);
  const [target, setTarget] = useState(27);
  const [entities, setEntities] = useState([]);
  const [members, setMembers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
    const [detailBackView, setDetailBackView] = useState('entities');
    const [countrySelectedCountryName, setCountrySelectedCountryName] = useState('');
    const [countrySortMode, setCountrySortMode] = useState('lastEdited');
    const [entityFilter, setEntityFilter] = useState('');
    const [entitySortMode, setEntitySortMode] = useState('lastEdited');
    const [countryEditorsByCountry, setCountryEditorsByCountry] = useState({});
  
  // Auth State
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) return JSON.parse(storedUser);
      return publicDatabaseIdFromLocation() ? { role: 'viewer', name: 'Visitor', allowedDatabases: [] } : null;
  });
  const [authChecked, setAuthChecked] = useState(false);
  const [guestMode, setGuestMode] = useState(() => !localStorage.getItem('token') && !!publicDatabaseIdFromLocation());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check auth on load
  useEffect(() => {
    const clearAuthState = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        setGuestMode(false);
        setSelectedDatabase(null);
        setPlatformDatabases(PLATFORM_DATABASES);
        setView('dashboard');
    };

    // If we have a token, we could verify it with /api/auth/me but for now local storage is enough for UI state
    // But let's verify if requested
    if (token) {
        authFetch(`${API_URL}/auth/me`)
            .then(res => {
                if (!res.ok) {
                    clearAuthState(); // Invalid token
                } else {
                    return res.json();
                }
            })
            .then(u => {
                if(u) setUser(u);
            })
            .catch(() => clearAuthState())
            .finally(() => setAuthChecked(true));
    } else {
        setAuthChecked(true);
    }
  }, [token]);

    const handleLogin = (newToken, newRefreshToken, newUser) => {
    if (newToken) {
        localStorage.setItem('token', newToken);
                if (newRefreshToken) {
                        localStorage.setItem('refreshToken', newRefreshToken);
                }
        localStorage.setItem('user', JSON.stringify(newUser));
        setToken(newToken);
        setUser(newUser);
        setSelectedDatabase(null);
        setGuestMode(false);
    } else {
        // Guest mode
        setUser(newUser || { role: 'viewer', name: 'Visitor', allowedDatabases: [] });
        setSelectedDatabase(null);
        setGuestMode(true);
        setAuthChecked(true); 
    }
  };

  const handleLogout = () => {
        const refreshToken = localStorage.getItem('refreshToken');

    // Notify server to log the logout event (fire and forget)
        authFetch(`${API_URL}/auth/logout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken })
        }).catch(() => {});
    
    localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setGuestMode(false);
    setSelectedDatabase(null);
    setPlatformDatabases(PLATFORM_DATABASES);
        setPlatformCatalogReady(false);
    setView('dashboard'); 
  };

  const fetchPlatformDatabases = useCallback(async () => {
      if (!token) {
          setPlatformCatalogReady(false);
          try {
              const res = await fetch(`${API_URL}/public/databases`);
              if (!res.ok) throw new Error('Failed to load public databases');
              const data = await res.json();
              const rows = Array.isArray(data?.rows) ? data.rows : [];
              const publicRows = rows.map((item) => ({
                  id: item.id,
                  title: item.title,
                  section: item.section,
                  sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 999,
                  available: !!item.available,
                  publicRead: !!item.publicRead,
                  colorHex: item.colorHex || '#5F5D57'
              }));
              setPlatformDatabases(publicRows);
              const requestedDatabaseId = publicDatabaseIdFromLocation();
              if (requestedDatabaseId && publicRows.some((item) => item.id === requestedDatabaseId)) {
                  setUser({ role: 'viewer', name: 'Visitor', allowedDatabases: [] });
                  setGuestMode(true);
                  setSelectedDatabase(requestedDatabaseId);
              }
          } catch {
              setPlatformDatabases([]);
          } finally {
              setPlatformCatalogReady(true);
          }
          return;
      }

      setPlatformCatalogReady(false);
      try {
          const res = await authFetch(`${API_URL}/databases/catalog`);
          if (!res.ok) throw new Error('Failed to load databases catalog');
          const data = await res.json();
          const rows = Array.isArray(data?.rows) ? data.rows : [];
          if (rows.length > 0) {
              setPlatformDatabases(rows.map((item) => ({
                  id: item.id,
                  title: item.title,
                  section: item.section,
                  sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 999,
                  available: !!item.available,
                  publicRead: !!item.publicRead,
                  colorHex: item.colorHex || '#5F5D57'
              })));
          } else {
              setPlatformDatabases(PLATFORM_DATABASES);
          }
      } catch {
          setPlatformDatabases(PLATFORM_DATABASES);
      } finally {
          setPlatformCatalogReady(true);
      }
  }, [token]);

  useEffect(() => {
      fetchPlatformDatabases();
  }, [fetchPlatformDatabases]);

  const handleSelectDatabase = (databaseId) => {
      const allowedDatabases = Array.isArray(user?.allowedDatabases) ? user.allowedDatabases : [];
      const publicViewerCanOpen = guestMode && platformDatabases.some((item) => item.id === databaseId && item.publicRead);
      if (user?.role !== 'admin' && !allowedDatabases.includes(databaseId) && !publicViewerCanOpen) {
          alert('You do not have access to this database.');
          return;
      }
      const dbMeta = platformDatabases.find((item) => item.id === databaseId);
      if (!dbMeta || !dbMeta.available || !SUPPORTED_DATABASE_WORKSPACES.includes(databaseId)) {
          alert('This database is still a placeholder.');
          return;
      }
      setSelectedDatabase(databaseId);
      if (guestMode) window.history.pushState({}, '', `/public/${databaseId}`);
      setView('dashboard');
  };

  const handleBackToDatabaseHub = () => {
      setSelectedDatabase(null);
      if (guestMode) window.history.pushState({}, '', '/');
  };

  // Cargar datos del backend
  const fetchData = async () => {
      try {
          const [ents, mems, docs, tgt] = await Promise.all([
             fetch(`${API_URL}/entities`).then(r => r.json()),
             fetch(`${API_URL}/members`).then(r => r.json()),
             fetch(`${API_URL}/documents`).then(r => r.json()),
             fetch(`${API_URL}/config/target`).then(r => r.json()),
          ]);
          setEntities(ents);
          setMembers(mems);
          setDocuments(docs);
          setTarget(tgt.target);

          try {
              const editorsRes = await authFetch(`${API_URL}/entities/editors-by-country`);
              if (editorsRes.ok) {
                  const editorsData = await editorsRes.json();
                  setCountryEditorsByCountry(editorsData && typeof editorsData === 'object' ? editorsData : {});
              } else {
                  setCountryEditorsByCountry({});
              }
          } catch {
              setCountryEditorsByCountry({});
          }
      } catch (e) {
          console.error("Error fetching data:", e);
      }
  };

    useEffect(() => {
        if (selectedDatabase === 'aipas') {
            fetchData();
        }
    }, [selectedDatabase]);

    useEffect(() => {
            if (selectedDatabase === 'aipas') {
                    const allowedDatabases = Array.isArray(user?.allowedDatabases) ? user.allowedDatabases : [];
                    if (!allowedDatabases.includes('aipas')) {
                            setSelectedDatabase(null);
                    }
            }
    }, [selectedDatabase, user]);

  // Handlers
  const handleUpdateEntity = async (updatedEntity) => {
    if (!user) return alert("You must be logged in to edit entities.");
    
    // Authorization check
    if (selectedEntity?.status === 'verified' && user.role !== 'admin') {
        alert('Only admins can modify verified entities');
        return;
    }
    
    const res = await authFetch(`${API_URL}/entities`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(updatedEntity)
    });
    
    if (!res.ok) {
        const err = await res.json();
        alert(err.error || "Failed to update entity");
        return;
    }

    const payload = await res.json();
    const savedEntity = payload?.entity || updatedEntity;

    setEntities(prev => {
        const exists = prev.some(e => e.id === savedEntity.id);
        if (exists) return prev.map(e => (e.id === savedEntity.id ? savedEntity : e));
        return [...prev, savedEntity];
    });

    setSelectedEntity(savedEntity);
    setIsEditMode(false); 
  };


  const handleCreateEntity = () => {
    const newId = `new-${Date.now()}`;
    const newEntity = {
        id: newId,
        country: '',
        officialName: '',
        englishName: '',
        year: '',
        creationNorm: '',
        functions: '',
        compositionRule: '',
        type: '',
        website: '',
        isExtinct: false,
        extinctionYear: '',
        succeededByEntityId: '',
        otherInfo: '',
        status: 'draft',
        hasDocs: false
    };
        setDetailBackView(view);
    setSelectedEntity(newEntity);
    setIsEditMode(true);
    setView('detail');
  };

  const handleSelectEntity = (entity) => {
        setDetailBackView(view);
    setSelectedEntity(entity);
    setIsEditMode(false);
    setView('detail');
  };

  const handleAddDocument = async (payload, options = {}) => {
      const { silent = false } = options;
      let newDoc;
      
      try {
        if (payload instanceof FormData) {
            const res = await authFetch(`${API_URL}/documents`, {
                method: 'POST',
                body: payload
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Upload failed");
            
            newDoc = {
                id: payload.get('id'),
                entityId: payload.get('entityId'),
                type: payload.get('type'),
                display_name: payload.get('display_name'),
                date: payload.get('date'),
                name: data.filename,
                size: (payload.get('file').size / 1024 / 1024).toFixed(2) + ' MB'
            };
        } else {
            const res = await authFetch(`${API_URL}/documents`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error("Document save failed");
            newDoc = payload;
        }
        
        setDocuments(prev => [...prev, newDoc]);
        
        const entity = entities.find(e => e.id === newDoc.entityId);
        if (entity && !entity.hasDocs) {
            const newEntity = { ...entity, hasDocs: true };
            await handleUpdateEntity(newEntity);
        }
      } catch (e) {
          console.error(e);
          if (!silent) {
              alert("Error: " + e.message);
          }
          throw e;
      }
  };

  const handleDeleteDocument = async (doc) => {
      const res = await authFetch(`${API_URL}/documents/${doc.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete document');

      setDocuments(prev => {
          const nextDocs = prev.filter(d => d.id !== doc.id);
          const hasRemainingForEntity = nextDocs.some(d => d.entityId === doc.entityId);
          setEntities(prevEntities => prevEntities.map(e => e.id === doc.entityId ? { ...e, hasDocs: hasRemainingForEntity } : e));
          setSelectedEntity(prevEntity => prevEntity && prevEntity.id === doc.entityId ? { ...prevEntity, hasDocs: hasRemainingForEntity } : prevEntity);
          return nextDocs;
      });
  };

  const handleReplaceDocument = async (docId, file, type) => {
      const payload = new FormData();
      payload.append('file', file);
      if (type) payload.append('type', type);

      const res = await authFetch(`${API_URL}/documents/${docId}/replace`, {
          method: 'PUT',
          body: payload
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to replace document');

      if (data?.document) {
          setDocuments(prev => prev.map(d => d.id === docId ? data.document : d));
      }
  };

  const handleAddMember = async (member) => {
      const res = await authFetch(`${API_URL}/members`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(member)
      });
      if (res.ok) {
        setMembers(prev => [...prev, member]);
      } else {
        alert("Failed to add member");
      }
  };

  const handleUpdateMember = async (member) => {
      const res = await authFetch(`${API_URL}/members`, {
          method: 'POST', // Server uses INSERT OR REPLACE
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(member)
      });
      if (res.ok) {
        setMembers(prev => prev.map(m => m.id === member.id ? member : m));
      } else {
        alert("Failed to update member");
      }
  };

  const handleDeleteMember = async (id) => {
      const res = await authFetch(`${API_URL}/members/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setMembers(prev => prev.filter(m => m.id !== id));
      } else {
        alert("Failed to delete member");
      }
  };

  const handleDeleteEntity = async (id) => {
      if(!window.confirm("Are you sure you want to delete this ENTITY? This will permanently remove the entity, its members, and documents.")) return;
      
      const res = await authFetch(`${API_URL}/entities/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setEntities(prev => prev.filter(e => e.id !== id));
        // Remove related members from local state to avoid stale data if we navigate back
        setMembers(prev => prev.filter(m => m.entityId !== id));
                setView(detailBackView || 'entities');
        setSelectedEntity(null);
      } else {
        const err = await res.json();
        alert("Failed to delete entity: " + (err.error || 'Unknown error'));
      }
  };


  // View Routing
  const renderView = () => {
    if (view === 'detail' && selectedEntity) {
      return (
        <EntityViewer 
            entity={selectedEntity} 
            mode={isEditMode ? 'edit' : 'view'}
            onSave={handleUpdateEntity} 
            onDeleteEntity={handleDeleteEntity}
            onBack={() => setView(detailBackView || 'entities')} 
            onEdit={() => setIsEditMode(true)}
            documents={documents}
            onAddDocument={handleAddDocument}
            onDeleteDocument={handleDeleteDocument}
            onReplaceDocument={handleReplaceDocument}
            members={members}
            onAddMember={handleAddMember}
            onUpdateMember={handleUpdateMember}
            onDeleteMember={handleDeleteMember}
                        entities={entities}
        />
      );
    }
    
    switch(view) {
    case 'dashboard': return <Dashboard entities={entities} members={members} documents={documents} target={target} setTarget={setTarget} currentUser={user} />;
            case 'countries': return <CountryView entities={entities} onSelectEntity={handleSelectEntity} onAddEntity={handleCreateEntity} selectedCountryName={countrySelectedCountryName} onSelectedCountryNameChange={setCountrySelectedCountryName} sortMode={countrySortMode} onSortModeChange={setCountrySortMode} editorsByCountry={countryEditorsByCountry} />;
            case 'entities': return <EntityManager entities={entities} onSelectEntity={handleSelectEntity} onAddEntity={handleCreateEntity} filter={entityFilter} onFilterChange={setEntityFilter} sortMode={entitySortMode} onSortModeChange={setEntitySortMode} />;
      case 'members': return <MembersView members={members} entities={entities} />;
      case 'data': return <DataView entities={entities} members={members} fetchData={fetchData} />;
    default: return <Dashboard entities={entities} members={members} documents={documents} target={target} setTarget={setTarget} currentUser={user} />;
    }
  };

    const activeSidebarView = view === 'detail' ? detailBackView : view;

  if (!authChecked) {
      return (
        <div className="flex h-screen items-center justify-center bg-stone-100">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        </div>
      );
  }

  if (!token && !guestMode) {
      return <Login onLogin={handleLogin} />;
  }

  if ((token || guestMode) && selectedDatabase === null) {
      if (!platformCatalogReady) {
          return (
            <div className="flex h-screen items-center justify-center bg-stone-100">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            </div>
          );
      }
      return <DatabaseHub user={user} onSelectDatabase={handleSelectDatabase} onLogout={handleLogout} onUserUpdate={setUser} platformDatabases={platformDatabases} onDatabasesUpdated={fetchPlatformDatabases} />;
  }

  if ((token || guestMode) && selectedDatabase === 'oral_questions') {
      return <OralQuestionsWorkspace user={user} onLogout={handleLogout} onBackToHub={handleBackToDatabaseHub} />;
  }

  if ((token || guestMode) && selectedDatabase === 'spanish_commissions') {
      return <SpanishCommissionsWorkspace user={user} onLogout={handleLogout} onBackToHub={handleBackToDatabaseHub} apiUrl={API_URL} />;
  }

  if ((token || guestMode) && selectedDatabase === 'spanish_parliament_activity') {
      return <SpanishParliamentActivityWorkspace user={user} onLogout={handleLogout} onBackToHub={handleBackToDatabaseHub} apiUrl={API_URL} />;
  }

  if ((token || guestMode) && selectedDatabase === 'spanish_parliamentary_speeches') {
      return <SpanishParliamentarySpeechesWorkspace user={user} onLogout={handleLogout} onBackToHub={handleBackToDatabaseHub} apiUrl={API_URL} />;
  }

  if ((token || guestMode) && selectedDatabase === 'spanish_laws') {
      return <SpanishLawsWorkspace user={user} onLogout={handleLogout} onBackToHub={handleBackToDatabaseHub} apiUrl={API_URL} />;
  }

  if ((token || guestMode) && selectedDatabase === 'spanish_executive_bills') {
      return <SpanishExecutiveBillsWorkspace user={user} onLogout={handleLogout} onBackToHub={handleBackToDatabaseHub} apiUrl={API_URL} />;
  }

  if (token && selectedDatabase === 'automatic_coding_validation') {
      return <AutomaticCodingValidationWorkspace user={user} onLogout={handleLogout} onBackToHub={() => setSelectedDatabase(null)} apiUrl={API_URL} />;
  }

  if (token && selectedDatabase === 'qdem_website') {
      return <QdemWebsiteWorkspace user={user} onLogout={handleLogout} onBackToHub={() => setSelectedDatabase(null)} apiUrl={API_URL} authFetch={authFetch} />;
  }

  return (
    <div className="flex flex-col md:flex-row h-screen font-sans text-stone-800 bg-stone-50 overflow-hidden relative">
      
      {/* Mobile Header */}
      <div className="md:hidden bg-[#222222] text-white p-4 flex justify-between items-center shadow-md z-50 shrink-0">
         <div className="flex flex-col">
            <h1 className="text-lg font-bold font-sans tracking-tight">AI Policy<span className={BRAND.accent}>DB</span></h1>
         </div>
         <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-1">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
         </button>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar (Q-DEM Style) */}
      <aside className={`
        fixed inset-y-0 left-0 w-64 ${BRAND.secondary} text-stone-100 flex flex-col shadow-xl z-40 transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        md:relative md:translate-x-0
      `}>
        <div className="p-6 border-b border-stone-700 hidden md:block">
          <h1 className="text-xl font-sans font-bold tracking-tight">AI Policy<span className={BRAND.accent}>DB</span></h1>
          <p className="text-xs text-stone-400 mt-1 uppercase tracking-widest">Advisory Systems</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          <div className="px-4 py-2 mb-4">
              {user ? (
                  <div className="flex flex-col gap-1">
                      <div className="text-xs text-stone-400 uppercase font-bold">Logged in as</div>
                      <div className="font-medium text-white truncate">{[user.name, user.surname].filter(Boolean).join(' ') || user.fullName || user.username}</div>
                      <div className="flex justify-between items-center text-xs mt-1">
                          <span className="text-green-400 capitalize bg-stone-800 px-1 rounded">{user.role}</span>
                          <div className="flex gap-2">
                             <button onClick={handleLogout} title="Log Out">
                                <ArrowLeft size={14} className="text-stone-400 hover:text-red-400" />
                             </button>
                          </div>
                      </div>
                  </div>
              ) : (
                  <div className="flex flex-col gap-1">
                      <div className="text-xs text-stone-400 uppercase font-bold">Guest Mode</div>
                      <button onClick={() => setGuestMode(false)} className="text-xs text-green-400 hover:underline text-left">Log in</button>
                  </div>
              )}
          </div>

                    <button 
            onClick={() => setView('dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'dashboard' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
          >
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </button>

          <button 
            onClick={() => setView('countries')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${activeSidebarView === 'countries' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
          >
            <Globe size={18} />
            <span>Countries</span>
          </button>

          <button 
            onClick={() => setView('entities')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${activeSidebarView === 'entities' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
          >
            <Database size={18} />
            <span>All Entities</span>
          </button>

          <button 
            onClick={() => setView('members')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'members' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
          >
            <Users size={18} />
            <span>Members</span>
          </button>

          {/* Only Admin can see Data Management */}
          {(user && user.role === 'admin') && (
            <div className="pt-4 mt-4 border-t border-stone-700">
                <button 
                    onClick={() => setView('data')}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'data' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
                >
                    <HardDrive size={18} />
                    <span>Data Management</span>
                </button>
                <button 
                    onClick={() => {
                        setSelectedDatabase(null);
                        setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${selectedDatabase === null ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
                >
                    <Database size={18} />
                    <span>Databases Hub</span>
                </button>
            </div>
          )}

          {(user && user.role !== 'admin') && (
            <div className="pt-4 mt-4 border-t border-stone-700">
                <button 
                    onClick={() => {
                        setSelectedDatabase(null);
                        setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${selectedDatabase === null ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
                >
                    <Database size={18} />
                    <span>Databases Hub</span>
                </button>
            </div>
          )}
        </nav>

        <div className="p-6 border-t border-stone-700">
            <div className="text-xs text-stone-500 text-center">
                Q-DEM Research Group<br/>
                &copy; 2026
            </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 overflow-auto ${BRAND.light} relative`}>
        {renderView()}
      </main>

    </div>
  );
}
